/* ════════════════════════════════════════════════════════════════════════
   KIDS RECYCLE CYCLE — App.js
   "Powered by Pedals not Petrol" · kidsrecyclecycle.com

   WHAT THIS IS
   A single-page React app for a kid-run community recycling service. Kids
   collect 10c-deposit cans/bottles from neighbours; the app tracks people,
   houses, bags, pickups, groups and sponsors. It is free for households —
   KRC never touches the recycling-refund money.

   HOW IT'S ORGANISED (top → bottom of this file)
     1. Imports (React + ./firebase)
     2. Small helpers ......... uid(), now(), getEmoji(), authErrMsg(), etc.
     3. Constants ............. EMOJI_OPTIONS, C (colours), SK (storage key),
                                EMPTY (blank data), style consts (inp, btnG…)
     4. Data layer ............ loadAll() / saveAll() / migrate() and the new
                                public mirror (derivePublic) + setup-status
                                meta helpers
     5. Shared UI bits ........ Card, SectionTitle, Tag, Empty, Fonts
     6. Screens ............... BootstrapRequired, LoginScreen, SignupScreen,
                                NeighbourScreen, BagErrorScreen, Loading
     7. Role tabs / views ..... dashboard tabs for Admin, Coordinator, Parent,
                                Kid, Sponsor (Team, Groups, Sponsors, Pickups,
                                Bags, Reports, Settings, …)
     8. App() ................. the top-level component: data state, load/save
                                effects, live sync, routing between screens

   DATA MODEL (one object held in React state as `data`)
     users[]    {id, role, username(=email), email, authUid, firstName,
                 lastName?, yearOfBirth?, houses[], parentId?, groupId?/
                 groupIds[], active, status, emoji, notes{}, …}
                 role ∈ admin | coordinator | parent | kid | sponsor
     houses[]   {id, nickname/name, address…, …}   (address = PRIVATE)
     bags[]     {id, bagNumber, bagCode, houseId, status}
     pickups[]  {id, houseId, status, …}
     groups[]   {id, name, suburb, isDefault}
     notifications[], activityLog[], declineRequests[],
     coordinatorParents[], familyStats[]
     setupDone, mainOwnerId

   WHERE THE DATA LIVES (Firestore, project "kids-recycle-cycle")
     SPLIT COLLECTIONS (live): users, houses, bags, pickups, groups,
       notifications, declineRequests, coordinatorParents, familyStats,
     activity (create-only), meta/app.
       Ordinary profile/configuration edits use revisioned three-way patches;
       workflow-critical changes use dedicated atomic commands. Authoritative
       backend delivery remains the pre-pilot target.
     userAccess/{authUid} .... allowlist: role + appUserId (the login gate)
     publicBags/{token} ...... one tiny NON-sensitive doc per AT-HOUSE bag,
                               keyed by the bag's RANDOM QR id (never the
                               sequential bag number). Holds only the bag
                               number + coarse status — nothing personal.
     pickupRequests / parentSignups ... public write-only inboxes; a
                               Coordinator relay folds them into private data.
     appMeta/status .......... public {setupDone} flag, read before login
     appData/krc-v5-fresh4 ... FROZEN legacy backup (pre-split), readable
                               only by Main and never client-writable; the live
                               app never reads it. The
                               migration code paths in this file are DEAD but
                               kept deliberately — reviewers: this is not
                               "old code being live".
     Passwords are NOT stored here — they live only in Firebase Authentication
     (hashed). See firebase.js.

   SECURITY MODEL (Stage 4 — enforced by the DATABASE, verified per role)
     • Logins: Firebase Authentication (email + password, hashed/salted).
       Login → userAccess/{uid} → role → loadForRole() loads ONLY that
       role's slice, via queries that exactly mirror firestore.rules.
     • Main/Admin: platform staff, groups and aggregate stats only.
       Coordinator: own groups, Parent approval projections and counts only.
       Parent: own family. Kid: own things (+ parent doc). Sponsor: linked
       group names + counts only. Default deny last.
     • Internal account provisioning uses App-Check-protected callable
       functions and the Admin SDK. The recipient chooses their own password
       from Firebase's password-setup email. Only the separate public signup
       screen still creates its own unapproved Firebase identity directly.

   CHANGELOG (high level — see KRC-Code-Guide.md for detail)
     2026-05-10  First React/Firebase prototype; deploy (Vercel + Namecheap);
                 brand, flyers, developer brief.
     2026-05-17  App live at kidsrecyclecycle.com; system spec v2.0–v2.6.
     2026-05-30  Fixed a live data-loss bug; spec to v2.17; began the "v2" app
                 — Phase 1 Step 1a (Groups) and 1b (Coordinators).
     2026-05-31  Phase 1: 1c (Sponsors) + 1d-1 / 1d-2a (Firebase Auth — secure
                 email logins, reset, secure account creation); spec → v2.20.
     2026-06-01  Phase 1 finished: 1d-2b (all "Add" forms create secure logins,
                 no plaintext) + secure change-password. Emoji list cleaned.
                 Phase 2 began — Securing the Data:
                   2a  public appMeta/status note (pick Setup vs Login screen
                       without reading the locked data)
                   2b  log in via Firebase FIRST, then load data
                   2c-1 a tiny public mirror, now per-bag docs (publicBags/
                       {randomToken}) holding only nickname + coarse status —
                       additive, no behaviour change
                 Next: 2c-2 (point neighbour + signup at the public part), then
                 2d (turn the database locks on).
   ════════════════════════════════════════════════════════════════════════ */
import React, { useState, useEffect, useRef } from "react";
import {
  db,
  auth,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  collection,
  addDoc,
  getDocs,
  query,
  writeBatch,
  where,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  createAuthAccount,
  callKrcFunction,
  updatePassword,
  verifyBeforeUpdateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "./firebase";
// Local QR generation (run: npm install qrcode.react). Replaces the external
// api.qrserver.com service, so printed-bag QR codes don't depend on a third
// party being up (and bag URLs are no longer sent to one).
import { QRCodeSVG } from "qrcode.react";

/* ═══════════════════════════════════════════════════════════════════════════
   KIDS RECYCLE CYCLE v5 — Complete Business System + Safety Features
   ═══════════════════════════════════════════════════════════════════════════ */

const uid = () => Math.random().toString(36).slice(2, 10);
// Crypto-strength token for PUBLIC bag ids (the QR security token). uid() is
// fine for internal ids, but the bag token guards a public endpoint, so it
// uses the browser's CSPRNG. 32 lowercase hex chars — matches the rules'
// ^[a-z0-9]+$ format and stays under the 64-char cap.
const secureToken = () => {
  // 16 CSPRNG bytes -> 32 lowercase hex chars. Matches the rules' strict
  // ^[a-f0-9]{32}$ format for the public bag token. We do NOT fall back to
  // Math.random()/uid() (base-36, non-hex, not crypto-strength) because this
  // token guards a public endpoint.
  const g = typeof window !== "undefined" ? window.crypto : undefined;
  if (g && g.getRandomValues) {
    const b = new Uint8Array(16);
    g.getRandomValues(b);
    return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  }
  if (g && g.randomUUID) return g.randomUUID().replace(/-/g, "");
  // Fail CLOSED: this token guards a public endpoint, so we never mint a
  // weak Math.random() token. Modern browsers all have getRandomValues;
  // callers surface a friendly error instead of a guessable QR.
  throw new Error("Secure random generation unavailable — cannot mint bag token");
};
// Non-blocking toast notice (replaces window.alert). Pure DOM so it works from
// any screen/component without threading state through the tree.
function toast(msg) {
  try {
    const el = document.createElement("div");
    el.textContent = msg;
    Object.assign(el.style, {
      position: "fixed",
      left: "50%",
      bottom: "28px",
      transform: "translateX(-50%)",
      maxWidth: "86vw",
      background: "#2e3a2f",
      color: "#fff",
      padding: "12px 18px",
      borderRadius: "12px",
      fontFamily: "system-ui, sans-serif",
      fontSize: "14px",
      lineHeight: "1.4",
      boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
      zIndex: 99999,
      opacity: "1",
      transition: "opacity 0.4s",
    });
    document.body.appendChild(el);
    const ms = Math.min(9000, 2500 + msg.length * 35);
    setTimeout(() => {
      el.style.opacity = "0";
      setTimeout(() => el.remove(), 450);
    }, ms);
  } catch (e) {
    window.alert(msg); // last resort
  }
}
const now = () => new Date().toISOString();

// Notifications are structured, deterministic workflow events. New clients do
// not persist free-text messages: the recipient's browser derives friendly text
// from the event plus records they are already authorised to read. The document
// id binds one recipient to one specific transition timestamp, so retries are
// idempotent and a single transition cannot mint multiple differently-id'ed
// notifications for the same recipient.
const notificationId = ({ eventType, refId, userId, ts }) =>
  [eventType, refId, userId, ts].join("|");
function makeNotification({
  userId,
  eventType,
  refType,
  refId,
  subjectUserId = "",
  groupId = "",
  actorId,
  ts,
}) {
  if (!userId || !eventType || !refType || !refId || !actorId || !ts) return null;
  const n = {
    userId,
    eventType,
    refType,
    refId,
    subjectUserId: subjectUserId || "",
    groupId: groupId || "",
    actorId,
    ts,
    read: false,
  };
  return { ...n, id: notificationId(n) };
}
function notificationMessage(n, d) {
  // Backward-compatible display for legacy notifications already stored before
  // v4.18a. Rules deny creating any new free-text notification.
  if (n?.msg) return n.msg;
  const pickup = n?.refType === "pickup" ? d.pickups.find((p) => p.id === n.refId) : null;
  const house = pickup ? d.houses.find((h) => h.id === pickup.houseId) : null;
  const subject = n?.subjectUserId
    ? d.users.find((u) => u.id === n.subjectUserId) ||
      (d.coordinatorParents || []).find((u) => u.id === n.subjectUserId)
    : null;
  const houseName = house?.nickname || house?.name || "a collection stop";
  const personName = subject?.firstName || "Your family member";
  switch (n?.eventType) {
    case "pickup_assigned":
      return `New pickup from ${houseName}!`;
    case "pickup_assigned_parent":
      return `Pickup from ${houseName} sent to ${personName}`;
    case "pickup_accepted_parent":
      return `${personName} accepted pickup from ${houseName}`;
    case "pickup_passed":
      return `Pickup from ${houseName} was passed to you`;
    case "pickup_passed_parent":
      return `Pickup from ${houseName} sent to ${personName}`;
    case "pickup_arrived_parent":
      return `📍 ${personName} has arrived at ${houseName}`;
    case "pickup_completed_parent":
      return `✅ ${personName} collected from ${houseName}`;
    case "pickup_approval_requested":
      return `${personName} wants to collect from ${houseName} — approve?`;
    case "pickup_approved":
      return `Parent approved pickup from ${houseName}!`;
    case "pickup_declined":
      return `Parent declined pickup from ${houseName}`;
    case "parent_signup_pending":
      return `${personName} has signed up and needs approval`;
    case "decline_requested":
      return `${personName} wants to stop collecting from ${houseName}`;
    default:
      return "A Kids Recycle Cycle update is available.";
  }
}
// ageFrom(year) -> a person's age in whole years, from their birth year.
const ageFrom = (y) => new Date().getFullYear() - y;
const BAG_SIZES = ["Small", "Medium", "Large"];
const BAG_TYPES = ["Standard", "Heavy Duty", "Premium Branded"];
const DEFAULT_EMOJI = "🙂";
const EMOJI_OPTIONS = [
  // Faces & people
  "🙂",
  "😊",
  "😄",
  "😃",
  "😎",
  "🧑",
  "👩",
  "👦",
  "👧",
  "👵",
  "👴",
  // Hearts
  "❤️",
  "💛",
  "💚",
  "💙",
  "💜",
  "💗",
  // Animals
  "🐶",
  "🐱",
  "🐰",
  "🐹",
  "🐸",
  "🐻",
  "🐵",
  "🐒",
  "🐴",
  "🐘",
  "🐼",
  "🦘",
  "🐧",
  "🐤",
  "🐥",
  "🐣",
  "🐢",
  "🐝",
  "🦋",
  "🦜",
  "😸",
  "🙉",
  "🐾",
  "🐠",
  "🐬",
  "🐳",
  // Nature & weather
  "🌸",
  "🌷",
  "🌻",
  "🌼",
  "🍀",
  "🍂",
  "🔥",
  "🌊",
  // Fun & activity
  "🚲",
  "🚴",
  "🛴",
  "🎯",
  "🎨",
  "💻",
  "👍",
  "👀",
  "⛄",
  "🌟",
  "⭐",
  "💥",
  // Music
  "🎵",
  "🎹",
  "🎻",
  "🎤",
  "🎺",
  "🎷",
  "🎸",
];
// getEmoji(user) -> the user's chosen avatar emoji, or the default.
const getEmoji = (u) => u?.emoji || DEFAULT_EMOJI;
// Friendly message for errors when creating a secure login for someone.
const authErrMsg = (e) => {
  const c = (e && e.code) || "";
  if (c === "auth/email-already-in-use")
    return "That email already has an account. Please use a different email.";
  if (c === "auth/invalid-email")
    return "That doesn't look like a valid email.";
  if (c === "auth/weak-password")
    return "Password must be at least 6 characters.";
  if (c === "auth/operation-not-allowed")
    return "Email sign-in isn't switched on in Firebase yet (enable Email/Password in the console).";
  return "Couldn't create the account. Please try again.";
};
// Internal account provisioning is server-authoritative. Staff and Parents
// provide identity/profile details only; the callable function creates the
// Firebase Auth user, Firestore identity, immutable access mapping, claims and
// audit record, rolling Auth back if Firestore provisioning fails. The browser
// then asks Firebase Auth to send the recipient a password-setup email.
const accountProvisionErrMsg = (e) => {
  const c = String((e && e.code) || "");
  if (c.includes("already-exists"))
    return "That email or login identity is already reserved in KRC.";
  if (c.includes("permission-denied"))
    return "You are not allowed to create this account or assign those Groups.";
  if (c.includes("invalid-argument"))
    return "Please check the name, email, year of birth and Group details.";
  if (c.includes("failed-precondition"))
    return "The account could not be created because a required Parent or Group is unavailable.";
  if (c.includes("unauthenticated"))
    return "Your session has expired. Please sign in again.";
  if (c.includes("app-check"))
    return "This device could not be verified. Refresh the app and try again.";
  return "The account could not be provisioned. No usable login was left behind.";
};
const accountLifecycleErrMsg = (e, action = "update") => {
  const c = String((e && e.code) || "");
  const m = String((e && e.message) || "").replace(/^.*?:\s*/, "");
  if (c.includes("failed-precondition") && m) return m;
  if (c.includes("permission-denied"))
    return `You are not allowed to ${action} this account.`;
  if (c.includes("unauthenticated"))
    return "Your session has expired. Please sign in again.";
  if (c.includes("app-check"))
    return "This device could not be verified. Refresh the app and try again.";
  return `The account could not be ${action}d safely. Please try again.`;
};
async function provisionAccountRemote(profile) {
  const result = await callKrcFunction("provisionAccount", { profile });
  const created = result?.user || null;
  const projection = result?.projection || null;
  const appUserId = created?.id || projection?.id || result?.appUserId || "";
  const authUid = created?.authUid || projection?.authUid || result?.authUid || "";
  const invitationEmail =
    created?.email || projection?.email || result?.invitationEmail || profile?.email || "";
  if (!appUserId || !authUid || !invitationEmail)
    throw Object.assign(new Error("Invalid provisioning response"), {
      code: "krc/invalid-provisioning-response",
    });
  // The callable has already committed these records. Fold the exact server
  // response into the persisted baseline BEFORE React mirrors it locally, so
  // the ordinary three-way saver sees a server echo rather than attempting a
  // second browser create (which Rules correctly deny).
  if (lastSaved) {
    if (created) {
      lastSaved.users = [
        ...(lastSaved.users || []).filter((u) => u.id !== created.id),
        deepCopy(created),
      ];
    }
    if (projection) {
      lastSaved.coordinatorParents = [
        ...(lastSaved.coordinatorParents || []).filter(
          (x) => x.id !== projection.id,
        ),
        deepCopy(projection),
      ];
    }
  }
  let invitationSent = false;
  try {
    await sendPasswordResetEmail(auth, invitationEmail);
    invitationSent = true;
  } catch (error) {
    console.warn(
      "Account provisioned but invitation email failed",
      appUserId,
      error?.code,
    );
  }
  return {
    user: created,
    projection,
    appUserId,
    authUid,
    email: invitationEmail,
    invitationSent,
  };
}
async function deactivateAccountRemote(appUserId) {
  const result = await callKrcFunction("deactivateAccount", { appUserId });
  if (lastSaved) {
    lastSaved.users = (lastSaved.users || []).map((u) =>
      u.id === appUserId ? { ...u, active: false } : u,
    );
    lastSaved.coordinatorParents = (lastSaved.coordinatorParents || []).map(
      (x) => (x.id === appUserId ? { ...x, active: false } : x),
    );
  }
  return result;
}
async function reactivateAccountRemote(appUserId) {
  const result = await callKrcFunction("reactivateAccount", { appUserId });
  if (lastSaved) {
    lastSaved.users = (lastSaved.users || []).map((u) =>
      u.id === appUserId ? { ...u, active: true } : u,
    );
    lastSaved.coordinatorParents = (lastSaved.coordinatorParents || []).map(
      (x) => (x.id === appUserId ? { ...x, active: true } : x),
    );
  }
  return result;
}
async function unlinkCoordinatorGroupRemote(appUserId, groupId) {
  const result = await callKrcFunction("unlinkCoordinatorGroup", {
    appUserId,
    groupId,
  });
  const groupIds = Array.isArray(result?.groupIds) ? result.groupIds : [];
  if (lastSaved) {
    lastSaved.users = (lastSaved.users || []).map((u) =>
      u.id === appUserId ? { ...u, groupIds: [...groupIds] } : u,
    );
  }
  return { ...result, groupIds };
}

async function syncOwnAccessEmailRemote(appUserId, email) {
  const result = await callKrcFunction("syncOwnAccessEmail", {});
  const synced = String(result?.email || email || "").toLowerCase();
  if (lastSaved?.users) {
    lastSaved.users = lastSaved.users.map((u) =>
      u.id === appUserId
        ? { ...u, email: synced, username: synced, pendingEmail: null }
        : u,
    );
  }
  if (lastSaved?.coordinatorParents) {
    lastSaved.coordinatorParents = lastSaved.coordinatorParents.map((x) =>
      x.id === appUserId ? { ...x, email: synced, username: synced } : x,
    );
  }
  return synced;
}
// Ensures a freshly-loaded data object has all expected fields, and that the
// default "KRC Crew" group always exists. Runs on EVERY path that loads data
// (initial load AND the live-sync listener) so nothing can strip these out.
function migrate(d) {
  if (!d) return d;
  if (!d.activityLog) d.activityLog = [];
  if (!d.declineRequests) d.declineRequests = [];
  if (!d.coordinatorParents) d.coordinatorParents = [];
  if (!d.familyStats) d.familyStats = [];
  if (!d.users) d.users = [];
  if (!d.houses) d.houses = [];
  if (!d.pickups) d.pickups = [];
  if (!d.notifications) d.notifications = [];
  if (!d.bags) d.bags = [];
  if (!d.groups) d.groups = [];
  if (!d.groups.some((g) => g.isDefault)) {
    d.groups.push({
      id: "grp-krc-crew",
      name: "KRC Crew",
      suburb: "Melbourne",
      isDefault: true,
      createdAt: now(),
    });
  }
  // Security cleanup: remove any legacy plaintext password fields left on user
  // records from before logins moved to Firebase Auth. Nothing reads them any
  // more, and they must not sit in the database. Stripping here means they're
  // gone on the next load and removed for good on the next save.
  d.users = (d.users || []).map((u) => {
    if (u && (u.password !== undefined || u.confirm !== undefined)) {
      const { password, confirm, ...rest } = u;
      return rest;
    }
    return u;
  });
  // Notification pruning: notifications were never cleaned up, which slowly
  // bloats the single data document (Firestore caps a doc at 1MB). Keep a
  // notification if it's unread or under 30 days old, then cap at the newest
  // 50 per user. (Pickups are NOT pruned — reports and scoreboards need them.)
  {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const fresh = (d.notifications || []).filter(
      (n) => !n.read || new Date(n.ts).getTime() > cutoff,
    );
    const perUser = {};
    const kept = [];
    // newest first so the cap keeps the most recent
    fresh
      .slice()
      .sort((a, b) => new Date(b.ts) - new Date(a.ts))
      .forEach((n) => {
        perUser[n.userId] = (perUser[n.userId] || 0) + 1;
        if (perUser[n.userId] <= 50) kept.push(n);
      });
    d.notifications = kept;
  }
  // ── Stage 4 scope backfills ──────────────────────────────────────────
  // Per-role security needs ownership fields ON the documents so rules and
  // queries can check them. Stamp anything created before this stage.
  {
    const byId = {};
    (d.users || []).forEach((u) => (byId[u.id] = u));
    // kids inherit their parent's group
    d.users = d.users.map((u) =>
      u.role === "kid" && !u.groupId && byId[u.parentId]
        ? { ...u, groupId: byId[u.parentId].groupId || null }
        : u,
    );
    // a parent's user doc lists every house their family uses (their kids'
    // assigned houses) — this is what the rules check for house/pickup reads
    d.users = d.users.map((u) => {
      if (u.role !== "parent") return u;
      const fam = new Set(u.houses || []);
      d.users.forEach((k) => {
        if (k.role === "kid" && k.parentId === u.id)
          (k.houses || []).forEach((h) => fam.add(h));
      });
      const arr = Array.from(fam);
      return arr.length !== (u.houses || []).length ? { ...u, houses: arr } : u;
    });
    // houses get an owning parent + group (inferred from an assigned kid)
    d.houses = (d.houses || []).map((h) => {
      if (h.parentId !== undefined && h.groupId !== undefined) return h;
      const kid = d.users.find(
        (u) => u.role === "kid" && (u.houses || []).includes(h.id),
      );
      const par = kid ? byId[kid.parentId] : null;
      return {
        ...h,
        parentId: h.parentId !== undefined ? h.parentId : par ? par.id : null,
        groupId:
          h.groupId !== undefined ? h.groupId : par ? par.groupId || null : null,
      };
    });
    // pickups inherit their house's group (coordinator-scoped queries)
    const houseById = {};
    (d.houses || []).forEach((h) => (houseById[h.id] = h));
    d.pickups = (d.pickups || []).map((p) =>
      p.groupId === undefined
        ? { ...p, groupId: houseById[p.houseId]?.groupId ?? null }
        : p,
    );
    // decline requests carry the kid's parent for parent-scoped queries
    d.declineRequests = (d.declineRequests || []).map((r) => {
      if (r.parentId !== undefined) return r;
      const kid = byId[r.kidId];
      return {
        ...r,
        parentId: kid ? kid.parentId || null : null,
        groupId: r.groupId !== undefined ? r.groupId : kid ? kid.groupId || null : null,
      };
    });
  }
  return d;
}

// Scoped role loads are intentionally PARTIAL datasets. They may be normalised
// for rendering, but must never run the database migration/backfill logic above:
// doing so can invent missing Groups or rewrite records the role is not allowed
// to manage. Real migrations run only against a complete dataset via Admin SDK
// or the legacy full-dataset upgrade path.
function normalizeScoped(d) {
  if (!d) return d;
  const out = { ...d };
  for (const key of [
    "activityLog",
    "declineRequests",
    "coordinatorParents",
    "familyStats",
    "users",
    "houses",
    "pickups",
    "notifications",
    "bags",
    "groups",
  ]) {
    if (!Array.isArray(out[key])) out[key] = [];
  }
  return out;
}

// Sanity check for year-of-birth inputs (e.g. blocks 199, 3000).
function validYob(y) {
  const yr = new Date().getFullYear();
  return Number.isInteger(y) && y >= yr - 110 && y <= yr - 3;
}
// Returns the short role label shown in the header, team list, etc.
const roleLabel = (u) => {
  if (!u) return "";
  if (u.role === "admin") return u.isMainOwner ? "👑 Main" : "👑 Admin";
  if (u.role === "coordinator") return `${getEmoji(u)} Coordinator`;
  if (u.role === "parent") return `${getEmoji(u)} Parent/Guardian`;
  if (u.role === "sponsor") return `${getEmoji(u)} Sponsor`;
  return `${getEmoji(u)} Kid`;
};
// Privacy-safe Coordinator projections. Coordinators no longer load Kid,
// House, Bag, Pickup or Decline Request collections. A Parent maintains one
// exact identity/approval projection plus one counts-only family snapshot.
const coordinatorParentProjection = (parent, kidCount = 0, updatedAt = now()) => ({
  id: parent.id,
  userId: parent.id,
  role: "parent",
  groupId: parent.groupId || "",
  firstName: parent.firstName || "",
  lastName: parent.lastName || "",
  email: parent.email || "",
  username: parent.username || parent.email || "",
  authUid: parent.authUid || "",
  status: parent.status || "pending",
  active: parent.active !== false,
  kidCount: Math.max(0, Number(kidCount) || 0),
  updatedAt,
});
const familyStatsProjection = (data, parent, updatedAt = now()) => {
  const kids = (data.users || []).filter(
    (u) => u.role === "kid" && u.parentId === parent.id,
  );
  const houseIds = new Set(parent.houses || []);
  kids.forEach((k) => (k.houses || []).forEach((hid) => houseIds.add(hid)));
  const bags = (data.bags || []).filter((b) => houseIds.has(b.houseId));
  const pickups = (data.pickups || []).filter((p) => houseIds.has(p.houseId));
  return {
    id: parent.id,
    parentId: parent.id,
    groupId: parent.groupId || "",
    kids: kids.length,
    houses: houseIds.size,
    bags: bags.length,
    activeBags: bags.filter((b) => b.status === "at_house").length,
    pickups: pickups.length,
    donePickups: pickups.filter((p) => p.status === "done").length,
    // Family browser projections are privacy-safe but provisional. Only a
    // trusted backend may ever publish verified counts.
    source: "family_projection",
    verified: false,
    updatedAt,
  };
};
// Coordinators aggregate counts-only family snapshots. Sponsors explicitly ask
// for the stored Group snapshot and never receive family-level contributions.
const groupStats = (data, groupId, useStoredSnapshot = false) => {
  const g = (data.groups || []).find((x) => x.id === groupId);
  if (useStoredSnapshot) return g?.stats || null;
  const parents = (data.coordinatorParents || []).filter(
    (p) => p.groupId === groupId && p.active !== false && p.status === "approved",
  );
  const parentIds = new Set(parents.map((p) => p.id));
  const contributions = (data.familyStats || []).filter(
    (x) => x.groupId === groupId && parentIds.has(x.parentId),
  );
  const sum = (key) => contributions.reduce((n, x) => n + (Number(x[key]) || 0), 0);
  return {
    parents: parents.length,
    reportingParents: contributions.length,
    statsComplete: contributions.length >= parents.length,
    kids: sum("kids"),
    houses: sum("houses"),
    bags: sum("bags"),
    activeBags: sum("activeBags"),
    pickups: sum("pickups"),
    donePickups: sum("donePickups"),
    // Phase 3b projections are privacy-safe but still browser-published. The
    // Sponsor UI labels them provisional until trusted backend aggregation is
    // deployed; a browser can never claim verified=true under the Rules.
    source: "family_projection",
    verified: false,
  };
};
const BAG_STATUS_LABEL = {
  in_stock: "📦 In Stock",
  allocated_parent: "📋 With Parent",
  allocated_kid: "📋 With Kid",
  at_house: "🏠 At House",
  lost: "❓ Lost",
  retired: "🔄 Retired",
};
const AGE_BRACKETS = ["Standard", "12+", "14+"];
// (QR codes are generated locally via qrcode.react — see QRTab.)
const BASE = "https://kidsrecyclecycle.com";
const DEFAULT_MAP_CENTER = { lat: -37.8429, lng: 145.2699 }; // Bayswater VIC 3153 area
// hasHouseLocation(house) -> true if the house has real map coordinates set.
const hasHouseLocation = (h) =>
  Number.isFinite(Number(h?.mapLat)) && Number.isFinite(Number(h?.mapLng));
// getHouseLocation(house) -> the house's {lat, lng} if set, else null.
const getHouseLocation = (h) =>
  hasHouseLocation(h)
    ? {
        lat: Number(h.mapLat),
        lng: Number(h.mapLng),
        label: h.mapLabel || "Saved map location",
      }
    : null;
// fallbackHouseLocation(i) -> a spread-out placeholder position for a house
// that has no real coordinates yet (so the map still shows something).
const fallbackHouseLocation = (i) => ({
  lat: DEFAULT_MAP_CENTER.lat + i * 0.0025 * Math.cos(i * 2.5),
  lng: DEFAULT_MAP_CENTER.lng + i * 0.0035 * Math.sin(i * 1.7),
  label: "Approximate Bayswater demo marker",
});

// fmt(iso) -> a short, human date/time string from an ISO timestamp.
const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-AU", {
      weekday: "short",
      day: "numeric",
      month: "short",
    }) +
    ", " +
    d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })
  );
};
// daysBetween(a, b) -> whole days between two dates.
const daysBetween = (a, b) =>
  Math.floor((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24));
// weekNum(iso) -> a year-week key like "2026-W22", for grouping things by week.
const weekNum = (iso) => {
  const d = new Date(iso);
  const s = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - s) / 86400000 + s.getDay() + 1) / 7);
};
const REMOVAL_MSG =
  "This location has been assessed as not currently suitable for collection by kids on bikes. Common reasons include traffic conditions, road layout, driveway access, or other safety considerations. No specific reason is provided. This decision is final. Thank you for your support of Kids Recycle Cycle.";

const C = {
  g: "#2e7d32",
  gl: "#4caf50",
  gp: "#e8f5e9",
  gd: "#1b5e20",
  gg: "#a5d6a7",
  gold: "#c8a415",
  w: "#fff",
  bg: "#f1f5ee",
  tx: "#1a1a1a",
  m: "#777",
  brd: "#d4ddd0",
  r: "#c62828",
  rp: "#ffebee",
  o: "#ef6c00",
  op: "#fff3e0",
  blue: "#1565c0",
  bp: "#e3f2fd",
  purple: "#6a1b9a",
  pp: "#f3e5f5",
};
const SK = "krc-v5-fresh4";
const EMPTY = {
  users: [],
  houses: [],
  pickups: [],
  notifications: [],
  bags: [],
  activityLog: [],
  declineRequests: [],
  coordinatorParents: [],
  familyStats: [],
  groups: [],
  setupDone: false,
  mainOwnerId: null,
};
//async function loadAll(){try{const r=await window.storage.get(SK);return r?JSON.parse(r.value):EMPTY;}catch{return EMPTY;}}
//async function saveAll(d){try{await window.storage.set(SK,JSON.stringify(d));}catch(e){console.error(e);}}
/* ── SPLIT STORAGE LAYER (Phase 3) ──────────────────────────────────────────
   The dataset used to live in ONE Firestore document, which meant: a 1MB hard
   ceiling, whole-document last-write-wins between users, and no way to write
   per-role security rules. It now lives in per-entity collections:

     users/{id} houses/{id} bags/{id} pickups/{id} groups/{id}
     notifications/{id} declineRequests/{id}
     meta/app      -> { setupDone, mainOwnerId, ... }
     meta/activity -> { entries: [...] }   (capped activity log)

   The UI keeps working off ONE assembled in-memory object (same shape as
   before), and saves still go through saveAll(data) — but saveAll now DIFFS
   against the last-known-saved state and writes only the documents that
   actually changed. So the 50+ existing update sites need no changes, and two
   people editing different things no longer overwrite each other.

   A legacy single-doc database is detected on load and auto-migrated to
   collections the first time an admin logs in (the old doc is kept, marked
   migratedAt, as a frozen backup). */

const ENTITY_KEYS = [
  "users",
  "houses",
  "bags",
  "pickups",
  "groups",
  "notifications",
  "declineRequests",
  "coordinatorParents",
  "familyStats",
  "activityLog", // stored in the de-identified 'activity' collection
];
const collName = (k) => (k === "activityLog" ? "activity" : k);

let storeMode = "split"; // "split" | "legacy" — set by loadAll()
let lastSaved = null; // exact role-scoped snapshot known to be persisted

// v4.19a persistence coordinator ------------------------------------------------
// Every signed-in write enters one session-bound queue. Ordinary React snapshots
// are COALESCED and saved as a three-way patch (captured base -> local snapshot,
// checked against the current persisted baseline). Dedicated commands may safely
// land between capture and execution: unrelated local edits are rebased, while a
// same-document collision is surfaced rather than silently overwriting either
// side. A failed/dirty revision blocks later workflow commands until the user
// explicitly retries or reloads the server copy.
let saveQueue = Promise.resolve();
let saveRevision = 0;
let commandRevision = 0;
let pendingOrdinarySave = null;
let ordinarySaveLoop = null;
let failedOrdinarySave = null;
const NO_PERSISTENCE_CHANGE = Symbol("krc-no-persistence-change");
const noPersistenceChange = (value) => ({
  [NO_PERSISTENCE_CHANGE]: true,
  value,
});
const deepCopy = (x) => JSON.parse(JSON.stringify(x));
const clean = (x) => JSON.parse(JSON.stringify(x)); // strips undefined for Firestore
const sameJson = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function queuePersistence(work, { bumpCommandRevision = false } = {}) {
  const authUid = auth.currentUser?.uid || null;
  const job = saveQueue.then(async () => {
    if (authUid && auth.currentUser?.uid !== authUid)
      throw new Error("The signed-in session changed before this write ran.");
    const result = await work();
    if (result?.[NO_PERSISTENCE_CHANGE]) return result.value;
    if (bumpCommandRevision) commandRevision += 1;
    return result;
  });
  saveQueue = job.catch(() => {});
  return job;
}
const runAtomicCommand = (work) =>
  queuePersistence(async () => {
    if (failedOrdinarySave) {
      const error = new Error(
        "An earlier edit has not been saved. Retry it or reload the server copy before running another workflow command.",
      );
      error.code = "krc/dirty-save";
      throw error;
    }
    return work();
  }, { bumpCommandRevision: true });
const getStoreMode = () => storeMode;
const hasUnresolvedPersistence = () =>
  !!failedOrdinarySave || !!pendingOrdinarySave || !!ordinarySaveLoop;
function clearPersistenceSession() {
  pendingOrdinarySave = null;
  failedOrdinarySave = null;
  ordinarySaveLoop = null;
  saveQueue = Promise.resolve();
  saveRevision = 0;
  commandRevision = 0;
}

async function loadAll() {
  // IMPORTANT: do NOT catch errors here and return EMPTY.
  // If a read fails (e.g. a network glitch) and we return EMPTY, the app can
  // then save that blank state over the real data and wipe everything.
  // Errors propagate so the caller can refuse to save.
  const metaSnap = await getDoc(doc(db, "meta", "app"));
  if (metaSnap.exists()) {
    // ── New split layout ──
    const colls = await Promise.all(
      ENTITY_KEYS.map((k) => getDocs(collection(db, collName(k)))),
    );
    const d = { ...EMPTY, ...metaSnap.data() };
    ENTITY_KEYS.forEach((k, i) => {
      d[k] = colls[i].docs.map((ds) => ds.data());
    });
    d.activityLog.sort((a, b) => (a.ts < b.ts ? 1 : -1));
    storeMode = "split";
    lastSaved = deepCopy(d);
    canWritePublicMirror = true; // main/admin
    return d;
  }
  // ── Legacy single document (pre-split) ──
  const legacy = await getDoc(doc(db, "appData", SK));
  if (legacy.exists()) {
    storeMode = "legacy";
    lastSaved = null;
    return legacy.data();
  }
  // Genuinely fresh project.
  storeMode = "split";
  lastSaved = deepCopy(EMPTY);
  return EMPTY;
}

// One-time fan-out of a legacy single-doc dataset into the collections.
// Runs automatically the first time an admin logs in on a legacy database.
// The legacy document is NOT deleted — it stays as a frozen backup.
async function migrateToCollections(d) {
  const writes = [];
  ENTITY_KEYS.forEach((k) => {
    (d[k] || []).forEach((item) => {
      if (item && item.id)
        writes.push(setDoc(doc(db, collName(k), item.id), clean(item)));
    });
  });
  writes.push(
    setDoc(doc(db, "meta", "app"), {
      setupDone: !!d.setupDone,
      mainOwnerId: d.mainOwnerId || null,
      migratedAt: now(),
    }),
  );

  await Promise.all(writes); // throws if any write fails
  // Mark (not delete) the legacy doc so it's clearly a frozen backup.
  await setDoc(
    doc(db, "appData", SK),
    { migratedAt: now() },
    { merge: true },
  ).catch(() => {});
  storeMode = "split";
  lastSaved = deepCopy(d);
}

// Build and commit a three-way patch. `base` is the persisted snapshot the UI
// was editing when `next` was captured. Each changed document is accepted only
// when the current baseline still equals that base (or already equals `next`).
// This prevents a stale local snapshot from undoing a completed command while
// still allowing unrelated edits to rebase safely.
async function diffSave(next, base = lastSaved || EMPTY) {
  const current = lastSaved || EMPTY;
  const ops = [];
  const conflicts = [];

  const byId = (items) => {
    const out = {};
    (items || []).forEach((x) => {
      if (x?.id) out[x.id] = x;
    });
    return out;
  };

  ENTITY_KEYS.forEach((k) => {
    const baseById = byId(base[k]);
    const nextById = byId(next[k]);
    const currentById = byId(current[k]);
    const ids = new Set([...Object.keys(baseById), ...Object.keys(nextById)]);
    ids.forEach((id) => {
      const before = baseById[id];
      const after = nextById[id];
      if (sameJson(before, after)) return; // not a local change
      const live = currentById[id];
      if (sameJson(live, after)) return; // command/earlier save already landed it
      if (!sameJson(live, before)) {
        conflicts.push(`${collName(k)}/${id}`);
        return;
      }
      if (after) ops.push(["set", collName(k), id, clean(after), k]);
      else ops.push(["del", collName(k), id, null, k]);
    });
  });

  const metaChanged =
    base.setupDone !== next.setupDone || base.mainOwnerId !== next.mainOwnerId;
  if (metaChanged) {
    const liveMeta = {
      setupDone: !!current.setupDone,
      mainOwnerId: current.mainOwnerId || null,
    };
    const baseMeta = {
      setupDone: !!base.setupDone,
      mainOwnerId: base.mainOwnerId || null,
    };
    const nextMeta = {
      setupDone: !!next.setupDone,
      mainOwnerId: next.mainOwnerId || null,
    };
    if (!sameJson(liveMeta, nextMeta)) {
      if (!sameJson(liveMeta, baseMeta)) conflicts.push("meta/app");
      else ops.push(["merge", "meta", "app", nextMeta, "meta"]);
    }
  }

  if (conflicts.length) {
    const error = new Error(
      `This edit conflicts with newer server changes in: ${conflicts.slice(0, 5).join(", ")}${conflicts.length > 5 ? "…" : ""}. Reload or discard the local edit before continuing.`,
    );
    error.code = "krc/save-conflict";
    error.conflicts = conflicts;
    throw error;
  }

  // General profile/configuration saves may be chunked. The baseline is updated
  // after EACH successful chunk, so a later failure never pretends that an
  // uncommitted chunk landed. Authoritative workflows use dedicated commands.
  for (let i = 0; i < ops.length; i += 450) {
    const chunk = ops.slice(i, i + 450);
    const batch = writeBatch(db);
    chunk.forEach(([kind, c, id, val]) => {
      if (kind === "set") batch.set(doc(db, c, id), val);
      else if (kind === "merge") batch.set(doc(db, c, id), val, { merge: true });
      else batch.delete(doc(db, c, id));
    });
    await batch.commit();

    const merged = deepCopy(lastSaved || EMPTY);
    chunk.forEach(([kind, _c, id, val, key]) => {
      if (key === "meta") {
        merged.setupDone = !!val.setupDone;
        merged.mainOwnerId = val.mainOwnerId || null;
        return;
      }
      const list = [...(merged[key] || [])];
      const index = list.findIndex((x) => x.id === id);
      if (kind === "del") {
        if (index >= 0) list.splice(index, 1);
      } else if (index >= 0) list[index] = deepCopy(val);
      else list.push(deepCopy(val));
      merged[key] = list;
    });
    lastSaved = merged;
  }
  return ops.length;
}

// ── Stage 4: role-scoped loading ─────────────────────────────────────────
// Every signed-in role loads only its permitted slice, via queries/gets that
// mirror firestore.rules. No browser role loads the complete database.
const chunk10 = (a) => {
  const out = [];
  for (let i = 0; i < a.length; i += 10) out.push(a.slice(i, i + 10));
  return out;
};
const snapArr = (s) => s.docs.map((ds) => ds.data());

async function loadForRole(role, appUserId) {
  if (["main", "admin"].includes(role)) {
    // Platform-only: Admins and Main see GROUPS (with their stats snapshot) and
    // STAFF accounts (admins/coordinators/sponsors) plus their own
    // notifications -- never family records (houses, pickups, declineRequests,
    // or parent/kid users). Group-level totals come from the group.stats
    // snapshot, so no family data is needed for reports. This is the committed
    // platform-only Admin model (spec 17.10); the rules enforce the same shape.
    const d = { ...deepCopy(EMPTY), setupDone: true };
    const [groupsS, metaS, notifS, adminsS, coordsS, sponsorsS] =
      await Promise.all([
        getDocs(collection(db, "groups")),
        getDoc(doc(db, "meta", "app")),
        getDocs(
          query(collection(db, "notifications"), where("userId", "==", appUserId)),
        ),
        // staff only -- each query is pinned to a single non-family role so the
        // scoped read rule permits it (a collection-wide users read is denied).
        getDocs(query(collection(db, "users"), where("role", "==", "admin"))),
        getDocs(query(collection(db, "users"), where("role", "==", "coordinator"))),
        getDocs(query(collection(db, "users"), where("role", "==", "sponsor"))),
      ]);
    d.groups = snapArr(groupsS);
    if (metaS.exists()) {
      d.setupDone = !!metaS.data().setupDone;
      d.mainOwnerId = metaS.data().mainOwnerId || null;
    }
    d.notifications = snapArr(notifS);
    const seen = {};
    d.users = [adminsS, coordsS, sponsorsS]
      .flatMap((s) => snapArr(s))
      .filter((u) => u && u.id && !seen[u.id] && (seen[u.id] = 1));
    storeMode = "split";
    lastSaved = deepCopy(d);
    canWritePublicMirror = false; // platform-only: Admins/Main touch no bags
    return d;
  }

  const meSnap = await getDoc(doc(db, "users", appUserId));
  if (!meSnap.exists()) {
    // Defensive: a scoped user whose own record is missing. Set the baseline
    // so a later save can't diff against a stale snapshot, and mark loaded so
    // the app shows an (empty) signed-in state rather than looping.
    const empty = { ...deepCopy(EMPTY), setupDone: true };
    storeMode = "split";
    lastSaved = deepCopy(empty);
    return empty;
  }
  const meDoc = meSnap.data();
  const d = { ...deepCopy(EMPTY), setupDone: true };

  if (role === "coordinator") {
    // Phase 3b parents-and-counts model. A Coordinator loads only:
    //   • their own user record;
    //   • their own Groups;
    //   • linked Sponsor staff records;
    //   • privacy-safe Parent identity/approval projections; and
    //   • counts-only family reporting projections.
    // Kid profiles, House addresses/notes, Bags, individual Pickups and
    // Decline Requests are not part of the Coordinator session.
    const gids = meDoc.groupIds || [];
    const groupSnapsP = Promise.all(
      gids.map((gid) => getDoc(doc(db, "groups", gid)).catch(() => null)),
    );
    const sponsorSnapsP = Promise.all(
      chunk10(gids).map((c) =>
        getDocs(
          query(
            collection(db, "users"),
            where("role", "==", "sponsor"),
            where("groupIds", "array-contains-any", c),
          ),
        ),
      ),
    );
    const parentSnapsP = Promise.all(
      chunk10(gids).map((c) =>
        getDocs(
          query(collection(db, "coordinatorParents"), where("groupId", "in", c)),
        ),
      ),
    );
    const familyStatsSnapsP = Promise.all(
      chunk10(gids).map((c) =>
        getDocs(query(collection(db, "familyStats"), where("groupId", "in", c))),
      ),
    );
    const [groupSnaps, sponsorSnaps, parentSnaps, statsSnaps, metaS, notifS] =
      await Promise.all([
        groupSnapsP,
        sponsorSnapsP,
        parentSnapsP,
        familyStatsSnapsP,
        getDoc(doc(db, "meta", "app")),
        getDocs(
          query(collection(db, "notifications"), where("userId", "==", appUserId)),
        ),
      ]);
    d.groups = groupSnaps
      .filter((snap) => snap && snap.exists())
      .map((snap) => snap.data());
    if (metaS.exists()) {
      d.setupDone = !!metaS.data().setupDone;
      d.mainOwnerId = metaS.data().mainOwnerId || null;
    }
    d.notifications = snapArr(notifS);
    const dedupe = (items) => {
      const seen = {};
      return items.filter((x) => x && x.id && !seen[x.id] && (seen[x.id] = 1));
    };
    d.users = dedupe([meDoc, ...sponsorSnaps.flatMap(snapArr)]);
    d.coordinatorParents = dedupe(parentSnaps.flatMap(snapArr));
    d.familyStats = dedupe(statsSnaps.flatMap(snapArr));
    storeMode = "split";
    lastSaved = deepCopy(d);
    canWritePublicMirror = false; // relay writes one explicit mirror atomically
    return d;
  }

  // Shared, low-sensitivity collections. Sponsors receive only the Groups
  // explicitly linked on their own account; other roles may load the Group
  // directory needed for navigation and group-level summaries.
  const groupsP =
    role === "sponsor"
      ? Promise.all(
          (meDoc.groupIds || []).map((gid) =>
            getDoc(doc(db, "groups", gid)).catch(() => null),
          ),
        )
      : getDocs(collection(db, "groups"));
  const [groupsS, bagsS, metaS] = await Promise.all([
    groupsP,
    // bags scoped to the family (rules enforce the same): their own
    // allocation plus any bag at one of their houses. Sponsors load none.
    role === "sponsor"
      ? Promise.resolve({ docs: [] })
      : Promise.all([
          getDocs(
            query(
              collection(db, "bags"),
              where(role === "parent" ? "parentId" : "kidId", "==", appUserId),
            ),
          ),
          ...chunk10(meDoc.houses || []).map((c) =>
            getDocs(query(collection(db, "bags"), where("houseId", "in", c))),
          ),
        ]).then((snaps) => ({ docs: snaps.flatMap((s) => s.docs) })),
    getDoc(doc(db, "meta", "app")),
  ]);
  d.groups = Array.isArray(groupsS)
    ? groupsS.filter((snap) => snap && snap.exists()).map((snap) => snap.data())
    : snapArr(groupsS);
  {
    const seen = {};
    d.bags = snapArr(bagsS).filter((b) => {
      if (!b || !b.id || seen[b.id]) return false;
      seen[b.id] = 1;
      return true;
    });
  }
  if (metaS.exists()) {
    d.setupDone = !!metaS.data().setupDone;
    d.mainOwnerId = metaS.data().mainOwnerId || null;
  }

  if (role === "sponsor") {
    d.users = [meDoc];
    storeMode = "split";
    lastSaved = deepCopy(d);
    canWritePublicMirror = false;
    return d;
  }

  const myHouseIds = meDoc.houses || [];
  const houseSnaps = await Promise.all(
    myHouseIds.map((h) => getDoc(doc(db, "houses", h)).catch(() => null)),
  );
  d.houses = houseSnaps.filter((s) => s && s.exists()).map((s) => s.data());

  const pickupQs = chunk10(myHouseIds).map((c) =>
    getDocs(query(collection(db, "pickups"), where("houseId", "in", c))),
  );
  const notifQ = getDocs(
    query(collection(db, "notifications"), where("userId", "==", appUserId)),
  );

  if (role === "parent") {
    const [kidsS, declS, ...pkS] = await Promise.all([
      getDocs(query(collection(db, "users"), where("parentId", "==", appUserId))),
      getDocs(
        query(
          collection(db, "declineRequests"),
          where("parentId", "==", appUserId),
        ),
      ),
      ...pickupQs,
    ]);
    d.users = [meDoc, ...snapArr(kidsS)];
    d.declineRequests = snapArr(declS);
    const seen = {};
    d.pickups = pkS.flatMap(snapArr).filter((p) => {
      if (seen[p.id]) return false;
      seen[p.id] = 1;
      return true;
    });
  } else {
    // kid: self (+ parent doc for display), own/assigned pickups, own declines
    const [parS, declS, mineS, pendS, ...pkS] = await Promise.all([
      meDoc.parentId
        ? getDoc(doc(db, "users", meDoc.parentId)).catch(() => null)
        : Promise.resolve(null),
      getDocs(
        query(collection(db, "declineRequests"), where("kidId", "==", appUserId)),
      ),
      getDocs(
        query(collection(db, "pickups"), where("currentKidId", "==", appUserId)),
      ),
      getDocs(
        query(collection(db, "pickups"), where("pendingKidId", "==", appUserId)),
      ),
      ...pickupQs,
    ]);
    d.users = [meDoc];
    if (parS && parS.exists()) d.users.push(parS.data());
    d.declineRequests = snapArr(declS);
    const seen = {};
    d.pickups = [...snapArr(mineS), ...snapArr(pendS), ...pkS.flatMap(snapArr)]
      .filter((p) => {
        if (seen[p.id]) return false;
        seen[p.id] = 1;
        return true;
      });
  }
  if (role === "parent") {
    const [parentProjectionS, familyStatsS] = await Promise.all([
      getDoc(doc(db, "coordinatorParents", meDoc.id)).catch(() => null),
      getDoc(doc(db, "familyStats", meDoc.id)).catch(() => null),
    ]);
    d.coordinatorParents =
      parentProjectionS && parentProjectionS.exists()
        ? [parentProjectionS.data()]
        : [];
    d.familyStats =
      familyStatsS && familyStatsS.exists() ? [familyStatsS.data()] : [];
  }
  d.notifications = snapArr(await notifQ);
  storeMode = "split";
  lastSaved = deepCopy(d);
  canWritePublicMirror = true; // family may refresh only its own routed Bag mirrors
  return d;
}

// ── Public mirror (Phase 2, Step 2c-1, tightened) ────────────────────────
// When a household scans a bag, the page needs only the printed bag number and
// a coarse pickup status. We keep one tiny document per active bag at
// publicBags/{token}. The random token is printed on the physical bag; the
// collection itself is not listable. Each document contains no household name,
// address, person, note or pickup detail.
//
// A bag is reachable ONLY by its random QR token, b.id (the same token the
// printed QR encodes: /bag/<id>). We deliberately do NOT publish docs under the
// bag number or bag code, because those are sequential and could be guessed to
// browse other bags.

// Firestore document ids cannot contain "/". Keep everything else as-is so the
// token printed on the bag matches the document we look up.
function publicBagDocId(token) {
  return String(token).replace(/\//g, "_");
}

// Coarse, non-identifying status for the household's own bag screen.
// Coarse, non-identifying status for one specific BAG. Prefers a pickup stamped
// with this bag's id (so a multi-bag household — e.g. a cafe — shows the right
// status per bag). Falls back to any active pickup for the house only for old
// pickups created before bagId existed.
function publicStateForBag(d, bagId, houseId) {
  const pickups = d.pickups || [];
  let active = pickups.find(
    (p) => p.bagId === bagId && !["done", "cancelled"].includes(p.status),
  );
  if (!active) {
    const houseHasBagStamped = pickups.some(
      (p) => p.houseId === houseId && p.bagId,
    );
    // Only use the house-wide fallback when this house has no bag-stamped
    // pickups at all (legacy data); otherwise we'd leak bag B's status onto A.
    if (!houseHasBagStamped) {
      active = pickups.find(
        (p) => p.houseId === houseId && !["done", "cancelled"].includes(p.status),
      );
    }
  }
  if (!active) return { status: "none", activePickupId: null };
  if (active.arrivedAt)
    return { status: "arrived", activePickupId: active.id };
  if (active.status === "accepted")
    return { status: "on_the_way", activePickupId: active.id };
  return { status: "requested", activePickupId: active.id };
}
// Build the map of {docId -> public payload} for every bag that is at a house.
// A bag without an active household assignment is not published.
function derivePublicBags(d) {
  const out = {};
  (d.bags || []).forEach((b) => {
    // Only bags actually AT a house are public. A lost/retired bag drops
    // out of this set, so the sync deletes its public doc — the printed
    // QR token is effectively revoked (the pickup-request rule requires
    // the public doc to exist).
    if (!b.houseId || b.status !== "at_house") return;
    const house = (d.houses || []).find((h) => h.id === b.houseId);
    if (!house) return;
    const publicState = publicStateForBag(d, b.id, house.id);
    const payload = {
      // ONLY the bag number, opaque Group route, coarse status and current
      // Pickup id. The route is required so Firestore can prove that a
      // Coordinator query is limited to their own Group; it is not a name,
      // address or household identifier and the collection is not listable.
      bagNumber: b.bagNumber || "",
      groupId: b.groupId || house.groupId || "",
      status: publicState.status,
      activePickupId: publicState.activePickupId,
      updatedAt: now(),
    };
    // Key ONLY by the random, unguessable bag id (the same token the printed
    // QR code uses: /bag/<id>). We deliberately do NOT publish a doc under the
    // human-readable bag number (e.g. KRC-001), because those are sequential
    // and could be guessed to browse other bags' statuses.
    if (b.id) out[publicBagDocId(b.id)] = payload;
  });
  return out;
}

// ── Home-printed QR (override for the branded-bag flow) ─────────────────
// Branded bags stay the standard, but a parent can generate a QR for one of
// THEIR OWN houses and print/email it at home — so a household can start
// collecting weeks before a physical bag reaches them. The bag record and its
// public mirror doc are written in ONE atomic batch (the rules verify the
// pair with getAfter), so the QR scans the moment it's created.
async function createHomeQr(house, user, allBags) {
  const homeCount = (allBags || []).filter((b) => b.selfPrinted).length;
  const bag = {
    id: secureToken(),
    bagNumber: `HOME-${String(homeCount + 1).padStart(3, "0")}`,
    size: "Home",
    type: "Self-printed",
    selfPrinted: true,
    status: "at_house",
    parentId: user.role === "parent" ? user.id : null,
    kidId: null,
    houseId: house.id,
    groupId: house.groupId || null,
    pickupCount: 0,
    addedAt: now(),
    lastScanDate: null,
  };
  const batch = writeBatch(db);
  batch.set(doc(db, "bags", bag.id), clean(bag));
  batch.set(doc(db, "publicBags", publicBagDocId(bag.id)), {
    bagNumber: bag.bagNumber,
    groupId: bag.groupId || house.groupId || "",
    status: "none", // fresh bag, no active pickup
    activePickupId: null,
    updatedAt: now(),
  });
  await runAtomicCommand(async () => {
    await batch.commit(); // throws if denied
    // Fold into the diff baseline so the next save doesn't re-write it.
    if (lastSaved) lastSaved.bags = [...(lastSaved.bags || []), deepCopy(bag)];
  });
  return bag;
}

// Update a bag AND its public QR doc in ONE atomic batch. Scoped family
// sessions may refresh only their own mirror slice, and even then a
// REVOCATION (lost/retired) must not depend on a best-effort post-save sync
// — so every transition into or out of at_house goes through here.
// publicPayload = null deletes the public doc (revokes the printed token:
// the pickup-request rule requires the doc to exist).
async function setBagAtomic(bag, patch, publicPayload) {
  const batch = writeBatch(db);
  batch.update(doc(db, "bags", bag.id), clean(patch));
  const pbRef = doc(db, "publicBags", publicBagDocId(bag.id));
  if (publicPayload) batch.set(pbRef, publicPayload);
  else batch.delete(pbRef);
  await runAtomicCommand(async () => {
    await batch.commit(); // throws if denied — callers surface the error
    // Fold into the diff baseline so the next save doesn't re-write it.
    if (lastSaved && lastSaved.bags)
      lastSaved.bags = lastSaved.bags.map((b) =>
        b.id === bag.id ? { ...b, ...patch } : b,
      );
  });
}

// We remember what we last wrote to each public bag doc, so on every save we
// only write the ones that actually changed (and remove ones no longer needed),
// instead of rewriting every bag's doc on every single save.
let _lastPublicBags = {};

// Set at load time: Coordinators and family roles refresh only the Bag mirror
// slice the Rules prove they own. Admin/Main/Sponsor never write this collection.
let canWritePublicMirror = false;

async function syncPublicBags(d) {
  try {
    if (!canWritePublicMirror) return;
    const next = derivePublicBags(d);
    // Write changed / new docs.
    for (const id of Object.keys(next)) {
      const prev = _lastPublicBags[id];
      const cur = next[id];
      if (
        !prev ||
        prev.bagNumber !== cur.bagNumber ||
        prev.groupId !== cur.groupId ||
        prev.status !== cur.status ||
        prev.activePickupId !== cur.activePickupId
      ) {
        await setDoc(doc(db, "publicBags", id), cur);
      }
    }
    // Remove docs for bags that are no longer published (e.g. bag taken back).
    for (const id of Object.keys(_lastPublicBags)) {
      if (!next[id]) {
        await deleteDoc(doc(db, "publicBags", id)).catch(() => {});
      }
    }
    _lastPublicBags = next;
  } catch (e) {
    // The public mirror is a convenience; never let a mirror error block the
    // real save above or crash the app.
    console.error("Public bag mirror sync failed (non-critical)", e);
  }
}

// A complete publicBags reconciliation is intentionally NOT available in
// the browser. No signed-in role loads the complete Bag/House dataset, so a
// collection-wide delete/rebuild here would be unsafe. Per-bag atomic writes
// keep active QR records current; any future full reconciliation belongs in a
// protected backend job.

async function saveAll(d, base = lastSaved || EMPTY) {
  if (storeMode === "split") {
    await diffSave(d, base); // writes only local profile/configuration changes
  } else {
    // Legacy single-doc mode (pre-migration): old behaviour.
    await setDoc(doc(db, "appData", SK), d);
  }
  // Do NOT derive workflow status from this snapshot. Pickup/Bag/public-mirror
  // lifecycle changes use dedicated atomic commands, so an older ordinary edit
  // can never overwrite a newer public QR state.
}

// Ordinary React-state saves are coalesced: while one save is waiting/running,
// later renders replace the pending snapshot instead of creating a stale queue
// of whole-role states. At execution time, edits captured before a command are
// three-way merged against the current baseline; unrelated edits survive and
// same-document collisions become visible errors.
function scheduleOrdinarySaveLoop() {
  if (ordinarySaveLoop || failedOrdinarySave || !pendingOrdinarySave) return;
  ordinarySaveLoop = queuePersistence(async () => {
    try {
      while (pendingOrdinarySave && !failedOrdinarySave) {
        const item = pendingOrdinarySave;
        pendingOrdinarySave = null;
        try {
          const base =
            item.commandRevision === commandRevision
              ? deepCopy(lastSaved || EMPTY)
              : item.base;
          await saveAllStrict(item.snapshot, base);
          item.onState?.({ status: "saved", revision: item.revision, error: null });
        } catch (error) {
          failedOrdinarySave = { ...item, error };
          console.error("Queued save failed", error);
          item.onState?.({ status: "error", revision: item.revision, error });
        }
      }
    } finally {
      ordinarySaveLoop = null;
      if (pendingOrdinarySave && !failedOrdinarySave) scheduleOrdinarySaveLoop();
    }
  });
}

function enqueueSave(d, onState) {
  const revision = ++saveRevision;
  pendingOrdinarySave = {
    snapshot: deepCopy(d),
    base: deepCopy(lastSaved || EMPTY),
    revision,
    commandRevision,
    authUid: auth.currentUser?.uid || null,
    onState,
  };
  onState?.({ status: "saving", revision, error: null });
  scheduleOrdinarySaveLoop();
  return ordinarySaveLoop;
}

function retryFailedOrdinarySave(onState) {
  const item = pendingOrdinarySave || failedOrdinarySave;
  if (!item) return false;
  failedOrdinarySave = null;
  pendingOrdinarySave = {
    ...item,
    revision: ++saveRevision,
    onState: onState || item.onState,
  };
  pendingOrdinarySave.onState?.({
    status: "saving",
    revision: pendingOrdinarySave.revision,
    error: null,
  });
  scheduleOrdinarySaveLoop();
  return true;
}

function discardFailedOrdinarySave() {
  failedOrdinarySave = null;
  pendingOrdinarySave = null;
}

// Strict ordinary save: it THROWS if persistence fails so the caller can keep
// the dirty revision visible for Retry/Reload. Public workflow state is never
// derived here; Pickup/Bag/public-mirror changes use dedicated atomic commands.
async function saveAllStrict(d, base = lastSaved || EMPTY) {
  await saveAll(d, base);
}

// ── Public read for the bag-scan path (Phase 2, Step 2c-2) ───────────────
// Read ONE bag's public doc by the token printed on it. This is the only thing
// a not-logged-in household needs, and the only private-free read on that path.
async function loadPublicBag(token) {
  const snap = await getDoc(doc(db, "publicBags", publicBagDocId(token)));
  return snap.exists() ? snap.data() : null;
}

// ── Public request inboxes (Phase 2, Step 2c-2) ──────────────────────────
// A not-logged-in household can do exactly two safe things: ask for a pickup,
// and ask to sign up as a parent. Pickup requests use one deterministic routed
// document per Bag; a repeat write is a reminder and Rules require 24 hours.
// A logged-in Coordinator relay reads only its own Group requests and folds
// each new timestamp version once. The request persists as the 24-hour clock.
async function requestPickupRemote(bagToken, publicBag) {
  const token = publicBagDocId(bagToken);
  const groupId = String(publicBag?.groupId || "");
  if (!groupId)
    throw new Error(
      "This Bag needs a Coordinator refresh before it can route a pickup request.",
    );
  const requestDate = new Date(); // Firestore Timestamp; Rules enforce 24h reminders.
  const requestedAt = requestDate.toISOString();
  const requestVersion = String(requestDate.getTime());
  const batch = writeBatch(db);
  // One deterministic inbox document per Bag. A second press is an UPDATE and
  // Firestore permits it only after 24 hours, so there can never be multiple
  // active public requests for the same Bag.
  batch.set(doc(db, "pickupRequests", token), {
    bagId: token,
    groupId,
    type: "pickup",
    createdAt: requestDate,
    requestedAt,
    requestVersion,
  });
  // Mark the public page requested in the same batch. The Rules verify the
  // matching pickupRequests/{token} document and preserve all routing fields.
  batch.set(doc(db, "publicBags", token), {
    bagNumber: String(publicBag?.bagNumber || ""),
    groupId,
    // A reminder for an active Pickup updates only the clock; it must not
    // downgrade on_the_way/arrived back to requested.
    status: publicBag?.activePickupId
      ? publicBag?.status || "requested"
      : "requested",
    activePickupId: publicBag?.activePickupId || null,
    updatedAt: now(),
  });
  return batch.commit();
}

async function submitSignupRemote(req) {
  return addDoc(collection(db, "parentSignups"), {
    ...req,
    type: "parentSignup",
    createdAt: now(),
  });
}

// Relay one public Parent signup as one dedicated atomic command. The pending
// Parent identity, minimum-data Coordinator projection, structured Notification
// and inbox deletion either all commit or none do. The deterministic app-user id
// also makes an exact request retry idempotent. Global email/UID uniqueness and
// Firebase Auth lifecycle remain trusted-backend work.
async function relayParentSignupAtomic(reqId, req, coordinator, currentData) {
  if (!reqId || !req?.groupId || !(coordinator?.groupIds || []).includes(req.groupId))
    return { relayed: false, reason: "foreign" };
  const email = String(req.email || "").trim().toLowerCase();
  if (!email) return { relayed: false, reason: "invalid" };
  const duplicate = [
    ...(currentData?.users || []),
    ...(currentData?.coordinatorParents || []),
  ].some(
    (u) =>
      (req.authUid && u.authUid === req.authUid) ||
      (email && String(u.email || "").toLowerCase() === email),
  );
  if (duplicate)
    return { relayed: false, reason: "duplicate" };

  const relayAt = now();
  const userId = `parent_${reqId}`;
  const newUser = {
    id: userId,
    role: "parent",
    username: email,
    email,
    authUid: req.authUid || null,
    firstName: String(req.firstName || "").trim(),
    lastName: String(req.lastName || "").trim(),
    yearOfBirth: req.yearOfBirth || null,
    groupId: req.groupId,
    active: true,
    status: "pending",
    houses: [],
    notes: {},
    declineCount: 0,
    emoji: DEFAULT_EMOJI,
    sourceSignupId: reqId,
    createdAt: relayAt,
  };
  const projection = coordinatorParentProjection(newUser, 0, relayAt);
  const notification = makeNotification({
    userId: coordinator.id,
    eventType: "parent_signup_pending",
    refType: "user",
    refId: newUser.id,
    subjectUserId: newUser.id,
    groupId: req.groupId,
    actorId: coordinator.id,
    ts: relayAt,
  });

  return runAtomicCommand(async () => {
    const batch = writeBatch(db);
    batch.set(doc(db, "users", newUser.id), clean(newUser));
    batch.set(doc(db, "coordinatorParents", projection.id), clean(projection));
    if (notification)
      batch.set(doc(db, "notifications", notification.id), clean(notification));
    batch.delete(doc(db, "parentSignups", reqId));
    await batch.commit();
    if (lastSaved) {
      lastSaved.users = [...(lastSaved.users || []), deepCopy(newUser)];
      lastSaved.coordinatorParents = [
        ...(lastSaved.coordinatorParents || []),
        deepCopy(projection),
      ];
      if (notification)
        lastSaved.notifications = [
          ...(lastSaved.notifications || []),
          deepCopy(notification),
        ];
    }
    return { relayed: true, newUser, projection, notification };
  });
}

// ── Relay engine (Phase 2, Step 2c-2) ────────────────────────────────────
// Build the exact minimum-data Pickup created by the logged-in relay. It is
// always unallocated: no Coordinator browser loads Kid profiles or chooses a
// collector. bagId and sourceRequestId preserve public-route traceability and
// idempotency while family members claim the Pickup through their own views.
function buildUnallocatedPickupForBag(bag, sourceRequestId, pickupId, requestedAt) {
  return {
    id: pickupId,
    houseId: bag.houseId,
    groupId: bag.groupId || "",
    bagId: bag.id,
    sourceRequestId,
    requestedAt,
    status: "unallocated",
    currentKidId: null,
    pendingKidId: null,
    assignedAt: null,
    history: [{ action: "requested", ts: requestedAt }],
  };
}

// Relay one routed public request without loading the Group's family dataset.
// The private Bag is fetched by its opaque id; its houseId/groupId provide the
// minimum route required to create an unallocated Pickup. The Pickup and public
// mirror are committed atomically. Family members authorised for that House can
// then claim it through their normal scoped views.
async function relayPickupRequestAtomic(reqId, req, requestVersion, coordinator) {
  return runAtomicCommand(async () => {
    if (!req?.bagId || !req?.groupId || !req?.requestedAt || !requestVersion)
      return noPersistenceChange({ relayed: false, reason: "invalid" });
    if (!(coordinator.groupIds || []).includes(req.groupId))
      return noPersistenceChange({ relayed: false, reason: "foreign" });
    const bagSnap = await getDoc(doc(db, "bags", req.bagId));
    if (!bagSnap.exists())
      return noPersistenceChange({ relayed: false, reason: "missing_bag" });
    const bag = bagSnap.data();
    if (
      bag.id !== req.bagId ||
      bag.groupId !== req.groupId ||
      !bag.houseId ||
      bag.status !== "at_house"
    )
      return noPersistenceChange({ relayed: false, reason: "unroutable" });
    const publicRef = doc(db, "publicBags", publicBagDocId(bag.id));
    const publicSnap = await getDoc(publicRef);
    if (!publicSnap.exists())
      return noPersistenceChange({ relayed: false, reason: "missing_public" });
    const publicBag = publicSnap.data();
    // A 24-hour reminder while an active Pickup exists is informational only.
    // It must not invalidate an unrelated queued edit merely because the relay
    // listener observed the already-processed request again after a reload.
    if (publicBag.activePickupId)
      return noPersistenceChange({ relayed: false, reason: "already_active" });
    const sourceRequestId = `${reqId}:${requestVersion}`;
    const pickupId = `pk_${reqId}_${requestVersion}`;
    const requestedAt = req.requestedAt;
    const pickup = buildUnallocatedPickupForBag(
      bag,
      sourceRequestId,
      pickupId,
      requestedAt,
    );
    const batch = writeBatch(db);
    batch.set(doc(db, "pickups", pickup.id), clean(pickup));
    batch.set(publicRef, {
      bagNumber: publicBag.bagNumber || bag.bagNumber || "",
      groupId: bag.groupId,
      status: "requested",
      activePickupId: pickup.id,
      updatedAt: now(),
    });
    await batch.commit();
    return { relayed: true, pickup };
  });
}

// Commit one non-terminal Pickup transition as a dedicated atomic command.
// The private Pickup, structured Notifications, optional de-identified Activity
// and any coarse public QR status change land together. This removes the Pickup
// lifecycle from the general whole-role diff engine while the eventual trusted
// backend command service is being built.
async function transitionPickupAtomic({
  data,
  pickupBefore,
  pickupAfter,
  notifications = [],
  activityType = null,
  publicStatus = null,
  transitionAt,
}) {
  if (!pickupBefore?.id || !pickupAfter?.id || pickupBefore.id !== pickupAfter.id)
    return null;
  const activity = activityType
    ? { id: uid(), type: activityType, ts: transitionAt }
    : null;
  const bag = pickupAfter.bagId
    ? (data?.bags || []).find((b) => b.id === pickupAfter.bagId) || null
    : null;
  await runAtomicCommand(async () => {
    const batch = writeBatch(db);
    batch.set(doc(db, "pickups", pickupAfter.id), clean(pickupAfter));
    notifications.filter(Boolean).forEach((notification) =>
      batch.set(doc(db, "notifications", notification.id), clean(notification)),
    );
    if (activity)
      batch.set(doc(db, "activity", activity.id), clean(activity));
    if (bag && publicStatus) {
      batch.set(doc(db, "publicBags", publicBagDocId(bag.id)), {
        bagNumber: bag.bagNumber || "",
        groupId: bag.groupId || pickupAfter.groupId || "",
        status: publicStatus,
        activePickupId: pickupAfter.id,
        updatedAt: transitionAt,
      });
    }
    await batch.commit();
    if (lastSaved) {
      lastSaved.pickups = (lastSaved.pickups || []).map((x) =>
        x.id === pickupAfter.id ? deepCopy(pickupAfter) : x,
      );
      if (notifications.length)
        lastSaved.notifications = [
          ...(lastSaved.notifications || []),
          ...notifications.filter(Boolean).map(deepCopy),
        ];
      if (activity)
        lastSaved.activityLog = [
          deepCopy(activity),
          ...(lastSaved.activityLog || []),
        ].slice(0, 200);
    }
  });
  return { pickupAfter, notifications: notifications.filter(Boolean), activity };
}

// Create one family Decline Request and its structured Parent notification
// together. This workflow is identity-bearing and must never rely on a later
// whole-state diff chunk to deliver the notification.
async function requestDeclineAtomic(decline, notification = null) {
  if (!decline?.id || !decline?.kidId || !decline?.parentId) return null;
  await runAtomicCommand(async () => {
    const batch = writeBatch(db);
    batch.set(doc(db, "declineRequests", decline.id), clean(decline));
    if (notification)
      batch.set(doc(db, "notifications", notification.id), clean(notification));
    await batch.commit();
    if (lastSaved) {
      lastSaved.declineRequests = [
        ...(lastSaved.declineRequests || []),
        deepCopy(decline),
      ];
      if (notification)
        lastSaved.notifications = [
          ...(lastSaved.notifications || []),
          deepCopy(notification),
        ];
    }
  });
  return { decline, notification };
}

// Review a family Decline Request as one dedicated command. The request state
// and any resulting House/Kid/Parent restrictions land in the same batch.
async function reviewDeclineAtomic({ data, decline, action, ageBracket, parentId }) {
  if (!decline?.id || decline.status !== "pending" || !parentId) return null;
  const nextData = applyDeclineReview(data, decline, action, ageBracket, parentId);
  const beforeUsers = new Map((data.users || []).map((u) => [u.id, u]));
  const beforeHouses = new Map((data.houses || []).map((h) => [h.id, h]));
  await runAtomicCommand(async () => {
    const batch = writeBatch(db);
    batch.update(doc(db, "declineRequests", decline.id), { status: "reviewed" });
    (nextData.users || []).forEach((u) => {
      const before = beforeUsers.get(u.id);
      if (before && !sameJson(before, u))
        batch.set(doc(db, "users", u.id), clean(u));
    });
    (nextData.houses || []).forEach((h) => {
      const before = beforeHouses.get(h.id);
      if (before && !sameJson(before, h))
        batch.set(doc(db, "houses", h.id), clean(h));
    });
    await batch.commit();
    if (lastSaved) {
      lastSaved.declineRequests = (lastSaved.declineRequests || []).map((x) =>
        x.id === decline.id ? { ...x, status: "reviewed" } : x,
      );
      lastSaved.users = deepCopy(nextData.users || []);
      lastSaved.houses = deepCopy(nextData.houses || []);
    }
  });
  return nextData;
}

// Complete one Pickup as a dedicated atomic command. The private Pickup,
// Bag credit, public QR reset, structured Notification and de-identified
// Activity event land together or not at all.
async function completePickupAtomic({
  pickup,
  bag,
  completedAt,
  notification = null,
}) {
  if (!pickup?.id || !pickup?.currentKidId || pickup.status !== "accepted")
    return null;
  const pickupAfter = {
    ...pickup,
    status: "done",
    doneAt: completedAt,
    doneBy: pickup.currentKidId,
    history: [
      ...(pickup.history || []),
      { action: "done", kidId: pickup.currentKidId, ts: completedAt },
    ],
  };
  const bagAfter = bag
    ? {
        ...bag,
        pickupCount: (bag.pickupCount || 0) + 1,
        lastScanDate: completedAt,
        lastPickupId: pickup.id,
      }
    : null;
  const activity = { id: uid(), type: "collected", ts: completedAt };
  await runAtomicCommand(async () => {
    const batch = writeBatch(db);
    batch.update(doc(db, "pickups", pickup.id), clean({
      status: pickupAfter.status,
      doneAt: pickupAfter.doneAt,
      doneBy: pickupAfter.doneBy,
      history: pickupAfter.history,
    }));
    if (bagAfter) {
      batch.update(doc(db, "bags", bagAfter.id), clean({
        pickupCount: bagAfter.pickupCount,
        lastScanDate: bagAfter.lastScanDate,
        lastPickupId: bagAfter.lastPickupId,
      }));
      batch.set(doc(db, "publicBags", publicBagDocId(bagAfter.id)), {
        bagNumber: bagAfter.bagNumber || "",
        groupId: bagAfter.groupId || pickup.groupId || "",
        status: "none",
        activePickupId: null,
        updatedAt: completedAt,
      });
    }
    if (notification)
      batch.set(doc(db, "notifications", notification.id), clean(notification));
    batch.set(doc(db, "activity", activity.id), activity);
    await batch.commit();
    if (lastSaved) {
      lastSaved.pickups = (lastSaved.pickups || []).map((x) =>
        x.id === pickup.id ? deepCopy(pickupAfter) : x,
      );
      if (bagAfter)
        lastSaved.bags = (lastSaved.bags || []).map((x) =>
          x.id === bagAfter.id ? deepCopy(bagAfter) : x,
        );
      if (notification)
        lastSaved.notifications = [
          ...(lastSaved.notifications || []),
          deepCopy(notification),
        ];
      lastSaved.activityLog = [
        deepCopy(activity),
        ...(lastSaved.activityLog || []),
      ].slice(0, 200);
    }
  });
  return { pickupAfter, bagAfter, notification, activity };
}

// Create one Group through a dedicated command. A Coordinator's self-link is
// written in the same batch because the Rules verify that relationship with
// getAfter(); platform Admin/Main create only the Group identity.
async function createGroupAtomic(group, actor) {
  if (!group?.id || !actor?.id) return null;
  const actorAfter =
    actor.role === "coordinator"
      ? {
          ...actor,
          groupIds: [...new Set([...(actor.groupIds || []), group.id])],
        }
      : actor;
  await runAtomicCommand(async () => {
    const batch = writeBatch(db);
    batch.set(doc(db, "groups", group.id), clean(group));
    if (actor.role === "coordinator")
      batch.update(doc(db, "users", actor.id), {
        groupIds: actorAfter.groupIds,
      });
    await batch.commit();
    if (lastSaved) {
      lastSaved.groups = [...(lastSaved.groups || []), deepCopy(group)];
      if (actor.role === "coordinator")
        lastSaved.users = (lastSaved.users || []).map((u) =>
          u.id === actor.id ? deepCopy(actorAfter) : u,
        );
    }
  });
  return { group, actorAfter };
}

// ── userAccess seeding / reconcile (Phase 2, Steps 2d support) ───────────
// Writes the allowlist entry that the locked rules require before a user can
// read private data. We only ever write entries the CURRENT user is allowed to:
// Main/Admin -> staff roles, Coordinator -> parent/sponsor, Parent -> own kids.
// Under locked rules a write outside that chain is denied.
function rolesIGrant(meRole) {
  // Platform-only model: Admin/Main govern STAFF access only (Main manages
  // admins; both manage coordinators/sponsors). They never grant or revoke
  // family (parent/kid) access -- that belongs to Coordinators (parents) and
  // Parents (kids). Keeping parent/kid OUT of the Admin scope is what stops the
  // access reconciler from revoking every family login when an Admin (who now
  // loads only staff) signs in. The reconciler further strips admin/main for an
  // ordinary (non-Main) admin.
  if (meRole === "main" || meRole === "admin")
    return ["admin", "coordinator", "sponsor"];
  if (meRole === "coordinator") return ["parent", "sponsor"];
  if (meRole === "parent") return ["kid"];
  return [];
}

// App-level scoping for who may be granted database access by the current user.
// The single shared document means rules can't verify "own group / own kid",
// so this guard stops the app itself from over-granting: a Coordinator only
// grants parents/sponsors in groups they run; a Parent only their own kid.
function canGrantAccessTo(me, target) {
  if (!me || !target) return false;
  // Admins/Main operate at platform level: they approve Coordinators (and
  // Community Sponsors), but a Parent is approved ONLY by a Coordinator in the
  // parent's own group -- never by an Admin. See spec §3.1 / §3.2.
  if (me.role === "main" || me.role === "admin") {
    // Only Main may grant/approve an Admin (or Main) account; ordinary Admins
    // grant Coordinators and Community Sponsors. A Parent is never Admin-
    // approved (a Coordinator in the parent's own group approves parents).
    if (target.role === "admin" || target.role === "main")
      return me.isMainOwner === true;
    // Explicit staff allowlist: an ordinary admin grants ONLY Coordinators and
    // Community Sponsors -- never a Parent OR a Kid (families are granted by
    // their Coordinator/Parent). Matches the userAccess rules.
    return ["coordinator", "sponsor"].includes(target.role);
  }
  if (me.role === "coordinator") {
    const mine = me.groupIds || [];
    const theirs =
      target.groupIds || (target.groupId ? [target.groupId] : []);
    return (
      ["parent", "sponsor"].includes(target.role) &&
      theirs.some((g) => mine.includes(g))
    );
  }
  if (me.role === "parent")
    return target.role === "kid" && target.parentId === me.id;
  return false;
}

// Create/refresh a user record AND its userAccess allowlist entry in ONE
// atomic Firestore batch -- both land or neither does, so the user doc and
// its access doc can never disagree, and the rules verify the mapping with
// getAfter() (the access doc's appUserId must resolve, post-batch, to a user
// whose authUid equals the doc id and whose role matches).
// NOTE: the Firebase AUTH account is created earlier and is NOT part of this
// batch -- Firestore cannot roll back Auth. If Auth succeeds but this batch
// fails, an Auth login can exist with no user/access records (an orphan).
// That's why callers grant FIRST and only then commit local UI state, and why
// server-side Auth cleanup of orphaned logins remains a backend-phase task.
async function grantAccessAtomic(userObj, role) {
  if (!userObj || !userObj.authUid || !userObj.id) return false;
  try {
    const batch = writeBatch(db);
    batch.set(doc(db, "users", userObj.id), clean(userObj));
    if ((role || userObj.role) === "parent") {
      batch.set(
        doc(db, "coordinatorParents", userObj.id),
        coordinatorParentProjection(userObj, userObj.kidCount || 0),
      );
    }
    batch.set(doc(db, "userAccess", userObj.authUid), {
      role: role || userObj.role,
      email: (userObj.email || userObj.username || "").toLowerCase(),
      appUserId: userObj.id,
      createdAt: now(),
    });
    await runAtomicCommand(async () => {
      await batch.commit(); // throws if denied -- caller surfaces the failure
      if (lastSaved && Array.isArray(lastSaved.users)) {
        const i = lastSaved.users.findIndex((u) => u.id === userObj.id);
        if (i >= 0) lastSaved.users[i] = deepCopy(userObj);
        else lastSaved.users.push(deepCopy(userObj));
        if ((role || userObj.role) === "parent") {
          const summary = coordinatorParentProjection(
            userObj,
            userObj.kidCount || 0,
          );
          const j = (lastSaved.coordinatorParents || []).findIndex(
            (x) => x.id === userObj.id,
          );
          if (j >= 0) lastSaved.coordinatorParents[j] = deepCopy(summary);
          else
            lastSaved.coordinatorParents = [
              ...(lastSaved.coordinatorParents || []),
              deepCopy(summary),
            ];
        }
      }
    });
    return true;
  } catch (e) {
    console.warn(
      "grantAccessAtomic failed for",
      userObj && userObj.id,
      e && e.code,
    );
    return false;
  }
}

async function approveCoordinatorParentAtomic(summary) {
  if (!summary?.id || !summary?.authUid || summary.role !== "parent") return false;
  try {
    const approvedAt = now();
    const batch = writeBatch(db);
    batch.update(doc(db, "users", summary.id), {
      status: "approved",
      active: true,
    });
    batch.update(doc(db, "coordinatorParents", summary.id), {
      status: "approved",
      active: true,
      updatedAt: approvedAt,
    });
    batch.set(doc(db, "userAccess", summary.authUid), {
      role: "parent",
      email: (summary.email || summary.username || "").toLowerCase(),
      appUserId: summary.id,
      createdAt: approvedAt,
    });
    await runAtomicCommand(async () => {
      await batch.commit();
      if (lastSaved) {
        lastSaved.coordinatorParents = (lastSaved.coordinatorParents || []).map((x) =>
          x.id === summary.id
            ? { ...x, status: "approved", active: true, updatedAt: approvedAt }
            : x,
        );
        lastSaved.users = (lastSaved.users || []).map((x) =>
          x.id === summary.id ? { ...x, status: "approved", active: true } : x,
        );
      }
    });
    return true;
  } catch (e) {
    console.warn("approveCoordinatorParentAtomic failed", summary.id, e?.code);
    return false;
  }
}
async function denyPendingCoordinatorParentAtomic(summary) {
  if (!summary?.id || summary.role !== "parent" || summary.status !== "pending")
    return false;
  try {
    const batch = writeBatch(db);
    batch.delete(doc(db, "users", summary.id));
    batch.delete(doc(db, "coordinatorParents", summary.id));
    // The immutable userAccess identity tombstone, if one exists from a
    // legacy flow, is retained. With the user record gone, approved() fails;
    // only an Admin-SDK recovery process may clean or rebind the mapping.
    await runAtomicCommand(async () => {
      await batch.commit();
      if (lastSaved) {
        lastSaved.coordinatorParents = (lastSaved.coordinatorParents || []).filter(
          (x) => x.id !== summary.id,
        );
        lastSaved.users = (lastSaved.users || []).filter((x) => x.id !== summary.id);
      }
    });
    return true;
  } catch (e) {
    console.warn("denyPendingCoordinatorParentAtomic failed", summary.id, e?.code);
    return false;
  }
}

async function ensureUserAccess(user, role) {
  if (!user || !user.authUid) return false;
  try {
    await runAtomicCommand(() =>
      setDoc(doc(db, "userAccess", user.authUid), {
        role: role || user.role,
        email: (user.email || user.username || "").toLowerCase(),
        appUserId: user.id,
        createdAt: now(),
      }),
    );
    return true;
  } catch (e) {
    // Denied (e.g. not allowed to grant this role) or offline — non-fatal for
    // background reconcile, but direct callers should surface the failure.
    console.warn("ensureUserAccess skipped for", user && user.id, e && e.code);
    return false;
  }
}

// Backfill missing login mappings and refresh permitted bookkeeping within
// the roles the current user may manage. Existing mappings are permanent
// identity tombstones: they are never client-deleted or rebound. Deactivated or
// deleted app users fail approved(), while genuine recovery/cleanup belongs to
// an audited Admin-SDK process.
async function reconcileUserAccess(d, meUser) {
  // Automatic reconciliation is Main-only because only Main may enumerate the
  // staff mapping slice and distinguish CREATE from UPDATE safely. Coordinator
  // and Parent mappings are written by their dedicated grant/approval command;
  // blind setDoc "reconciliation" would repeatedly hit immutable tombstones.
  if (!d || !meUser || meUser.isMainOwner !== true) return;

  const wantByUid = {};
  (d.users || []).forEach((u) => {
    if (
      u.authUid &&
      u.active !== false &&
      u.status !== "pending" &&
      ["admin", "coordinator", "sponsor"].includes(u.role)
    )
      wantByUid[u.authUid] = u;
  });

  let existing = {};
  try {
    const snap = await getDocs(
      query(
        collection(db, "userAccess"),
        where("role", "in", ["admin", "coordinator", "sponsor"]),
      ),
    );
    snap.docs.forEach((ds) => {
      existing[ds.id] = ds.data() || {};
    });
  } catch (error) {
    console.warn("Staff access reconciliation skipped", error?.code || error);
    return;
  }

  for (const authUid in wantByUid) {
    const u = wantByUid[authUid];
    if (!canGrantAccessTo(meUser, u)) continue;
    const cur = existing[authUid];
    if (cur && (cur.appUserId !== u.id || cur.role !== u.role)) {
      console.warn(
        "Immutable login mapping mismatch requires secure migration",
        authUid,
      );
      continue;
    }
    if (!cur) {
      await ensureUserAccess(u, u.role);
      continue;
    }
    const wantedEmail = (u.email || u.username || "").toLowerCase();
    if (wantedEmail && cur.email !== wantedEmail) {
      try {
        await runAtomicCommand(() =>
          updateDoc(doc(db, "userAccess", authUid), { email: wantedEmail }),
        );
      } catch (error) {
        console.warn("Staff access email refresh skipped", authUid, error?.code);
      }
    }
  }
}

// ── Public setup-status meta doc (Phase 2, Step 2a) ──────────────────────
// A tiny, NON-sensitive document that only records whether the business has
// been set up yet. It is kept separate from the main data so it can stay
// publicly readable later, once the main data is locked behind login. The app
// reads it before anyone logs in, to decide whether to show Setup or Login.
async function loadSetupStatus() {
  const snap = await getDoc(doc(db, "appMeta", "status"));
  if (!snap.exists() || snap.data().setupDone !== true) return "unconfigured";
  return "configured";
}
async function writeSetupStatus() {
  try {
    await runAtomicCommand(() =>
      setDoc(doc(db, "appMeta", "status"), { setupDone: true }),
    );
  } catch (e) {
    console.error("Could not write setup status", e);
  }
}

// Fonts -> injects the app's web fonts into the page <head>.
const Fonts = () => (
  <link
    href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&family=Quicksand:wght@400;500;600;700&display=swap"
    rel="stylesheet"
  />
);
const ff = { h: "'Baloo 2',cursive", b: "'Quicksand',sans-serif" };
const inp = {
  padding: "10px 14px",
  border: `2px solid ${C.brd}`,
  borderRadius: 10,
  fontSize: 14,
  fontFamily: ff.b,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};
const btnG = {
  background: `linear-gradient(135deg,${C.gl},${C.g})`,
  color: C.w,
  border: "none",
  borderRadius: 10,
  padding: "10px 20px",
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: ff.b,
  fontSize: 14,
  boxShadow: "0 3px 12px rgba(46,125,50,0.3)",
};
const btnB = {
  ...btnG,
  background: `linear-gradient(135deg,${C.blue},#1976d2)`,
  boxShadow: "0 3px 12px rgba(21,101,192,0.3)",
};
const btnD = {
  background: C.rp,
  color: C.r,
  border: `1px solid ${C.r}`,
  borderRadius: 10,
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: ff.b,
};

// ── Streak calc ──
// calcStreak(pickups, userId) -> how many days in a row this kid has done a pickup.
function calcStreak(pickups, userId) {
  const dones = pickups
    .filter((p) => p.status === "done" && p.doneBy === userId && p.doneAt)
    .sort((a, b) => new Date(b.doneAt) - new Date(a.doneAt));
  if (dones.length === 0) return { current: 0, best: 0 };
  const weeks = new Set();
  dones.forEach((p) => {
    const d = new Date(p.doneAt);
    weeks.add(`${d.getFullYear()}-${weekNum(p.doneAt)}`);
  });
  const sorted = [...weeks].sort().reverse();
  let current = 0;
  const nowWk = `${new Date().getFullYear()}-${weekNum(now())}`;
  let ck = nowWk;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i] === ck || sorted[i] === prevWk(ck)) {
      current++;
      ck = prevWk(sorted[i]);
    } else break;
  }
  let best = 0,
    run = 1;
  const asc = [...weeks].sort();
  for (let i = 1; i < asc.length; i++) {
    if (asc[i] === nxtWk(asc[i - 1])) run++;
    else run = 1;
    if (run > best) best = run;
  }
  if (current > best) best = current;
  if (best === 0 && dones.length > 0) best = 1;
  return { current, best };
}
// prevWk(w) -> step a "YYYY-Www" week key back one week.
function prevWk(w) {
  const [y, n] = w.split("-").map(Number);
  return n <= 1 ? `${y - 1}-52` : `${y}-${n - 1}`;
}
// nxtWk(w) -> step a "YYYY-Www" week key forward one week.
function nxtWk(w) {
  const [y, n] = w.split("-").map(Number);
  return n >= 52 ? `${y + 1}-1` : `${y}-${n + 1}`;
}

// Activity telemetry helper. Activity is deliberately de-identified: it
// records only a short event type and timestamp. Child/house IDs, names,
// addresses, notes and messages belong in the family collections, never in a
// platform-wide telemetry stream.
function addActivity(d, entry) {
  const allowed = new Set([
    "pickup_requested",
    "accepted",
    "arrived",
    "collected",
  ]);
  const type = allowed.has(entry?.type) ? entry.type : "pickup_requested";
  return {
    ...d,
    activityLog: [{ id: uid(), type, ts: now() }, ...d.activityLog].slice(
      0,
      500,
    ),
  };
}

// Public QR bag URL helper: /bag/<bagId>
function getBagIdFromPath() {
  const m = window.location.pathname.match(/^\/bag\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

// Address lookup is intentionally not sent to a third-party geocoder in the
// pilot build. Parents place the pin manually on the map; a future geocoding
// service must use explicit consent and an approved privacy-safe proxy.

// Leaflet is bundled with the application and exposed as window.L by
// src/index.jsx. No authenticated page executes third-party CDN JavaScript.
function loadLeafletOnce(setLoaded) {
  if (window.L) {
    setLoaded(true);
    return;
  }
  console.error(
    "The bundled Leaflet dependency is unavailable. Rebuild the complete v4.19b project before deploying.",
  );
}

// ━━━ MAIN APP ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function App() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [me, setMe] = useState(null);
  const [mode, setMode] = useState("split"); // "split" | "legacy" storage
  // Effects (relay, group-stat) must see the user's CURRENT record -- a
  // coordinator who just created their first group has new groupIds that the
  // login-time `me` object doesn't carry. Prefer the live record from data,
  // fall back to the login object before data loads.
  const meLive = React.useMemo(() => {
    if (me && data && Array.isArray(data.users)) {
      const fresh = data.users.find((u) => u.id === me.id);
      if (fresh) return fresh;
    }
    return me;
  }, [me, data]);
  const myGroupKey = (meLive && (meLive.groupIds || []).join(",")) || "";
  const [view, setView] = useState("loading");
  const [demoHouse, setDH] = useState(null);
  const [bagError, setBagError] = useState("");
  const [bagToken, setBagToken] = useState(null);
  const [publicBag, setPublicBag] = useState(null);
  const [saveState, setSaveState] = useState({
    status: "idle",
    revision: 0,
    error: null,
  });
  const remoteUpdate = useRef(false);
  // Always-current data, so the relay can read the latest without re-subscribing.
  const dataRef = useRef(null);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    // BEFORE LOGIN we touch only PUBLIC data, so the app works for a logged-out
    // visitor under locked rules. Private data is loaded after Firebase sign-in
    // (see the login handler) — never here.
    (async () => {
      // 1. A QR bag link -> resolve it from the PUBLIC per-bag mirror only,
      //    keyed by the random token (never a guessable bag number / code).
      const bagId = getBagIdFromPath();
      if (bagId) {
        try {
          const pb = await loadPublicBag(bagId);
          if (!pb) {
            setBagError(
              "Sorry, we couldn't find this recycling bag. Please contact Kids Recycle Cycle.",
            );
            setView("bagError");
            return;
          }
          setPublicBag(pb);
          setBagToken(bagId);
          setView("neighbour");
        } catch (e) {
          console.error("Could not load public bag.", e);
          setBagError(
            "Sorry, we couldn't load this recycling bag right now. Please try again.",
          );
          setView("bagError");
        }
        return;
      }

      // 2. Otherwise decide setup-vs-login from the PUBLIC setup flag (appMeta).
      try {
        const setupStatus = await loadSetupStatus();
        setView(setupStatus === "configured" ? "login" : "bootstrapRequired");
      } catch (e) {
        // Setup flag unreadable — default to the login screen.
        console.error("Could not read setup status.", e);
        setView("login");
      }
    })();
  }, []);
  useEffect(() => {
    // Never save until we have successfully loaded real data first.
    // This is the key guard that prevents a blank state from overwriting data.
    if (!me) return; // not logged in -> nothing to save
    if (!loaded) return;
    if (!data) return;

    if (remoteUpdate.current) {
      remoteUpdate.current = false;
      // In split mode, do NOT skip: incoming slices are already folded into
      // lastSaved, so the diff produces zero writes for pure echoes — but a
      // local change racing an echo must still be persisted.
      if (mode === "legacy") return;
    }

    enqueueSave(data, (next) =>
      setSaveState((prev) =>
        next.revision >= prev.revision ? next : prev,
      ),
    );
  }, [data, loaded, me, mode]);

  // Once a logged-in scoped role is present and data is loaded, refresh the
  // Bag mirrors it is permitted to own. This backfills the v4.17b opaque
  // groupId route and activePickupId onto older public docs before a household
  // next scans them. It is NOT a collection-wide reconciliation: each role
  // writes only the Bag slice already authorised by Firestore Rules.
  const publicSeeded = useRef(false);
  useEffect(() => {
    if (publicSeeded.current) return;
    if (!loaded || !data || !me || !canWritePublicMirror) return;
    publicSeeded.current = true;
    syncPublicBags(data);
  }, [loaded, data, me]);

  // A Parent maintains the only Coordinator-visible family projections:
  // one identity/approval summary and one counts-only reporting document. The
  // Coordinator receives no Kid profile, address, note, Bag or Pickup record.
  useEffect(() => {
    if (!loaded || !data || !meLive || meLive.role !== "parent") return;
    const nextParent = coordinatorParentProjection(
      meLive,
      (data.users || []).filter(
        (u) => u.role === "kid" && u.parentId === meLive.id,
      ).length,
    );
    const nextStats = familyStatsProjection(data, meLive);
    const oldParent = (data.coordinatorParents || []).find((x) => x.id === meLive.id);
    const oldStats = (data.familyStats || []).find((x) => x.id === meLive.id);
    const sameExceptTime = (a, b) => {
      if (!a || !b) return false;
      const { updatedAt: _a, ...aa } = a;
      const { updatedAt: _b, ...bb } = b;
      return JSON.stringify(aa) === JSON.stringify(bb);
    };
    if (sameExceptTime(oldParent, nextParent) && sameExceptTime(oldStats, nextStats))
      return;
    up((d) => ({
      ...d,
      coordinatorParents: [
        ...(d.coordinatorParents || []).filter((x) => x.id !== meLive.id),
        nextParent,
      ],
      familyStats: [
        ...(d.familyStats || []).filter((x) => x.id !== meLive.id),
        nextStats,
      ],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loaded, meLive]);

  // Phase 3b: Coordinators materialise Group totals only from exact Parent
  // identity projections plus counts-only family contributions. They no longer
  // calculate from Kid, House, Bag, Pickup or Decline Request records.
  useEffect(() => {
    if (!loaded || !data || !meLive) return;
    if (meLive.role !== "coordinator") return;
    const mine = new Set(meLive.groupIds || []);
    let changed = false;
    const groups = (data.groups || []).map((g) => {
      if (!mine.has(g.id)) return g; // only Groups I run
      const live = groupStats(data, g.id);
      const core = {
        parents: live.parents,
        reportingParents: live.reportingParents,
        statsComplete: live.statsComplete,
        kids: live.kids,
        houses: live.houses,
        bags: live.bags,
        activeBags: live.activeBags,
        pickups: live.pickups,
        donePickups: live.donePickups,
        source: live.source,
        verified: live.verified,
      };
      const old = g.stats || {};
      const same = Object.keys(core).every((k) => old[k] === core[k]);
      if (same) return g;
      changed = true;
      return { ...g, stats: { ...core, updatedAt: now() } };
    });
    if (changed) up((d) => ({ ...d, groups }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loaded, meLive, myGroupKey]);

  // Keep the allowlist in step with the user list: whenever users change
  // (approve, add, role change, deactivate, delete) this adds/corrects/revokes
  // userAccess for the roles this user may grant. Direct revoke calls at the
  // action sites cover the cases this can't list (a coordinator/parent removing
  // someone); this effect is the safety net and handles the admin case fully.
  useEffect(() => {
    if (!loaded || !me || !data) return;
    // Use the freshest copy of the current user — me can be stale right after
    // the user changes their own record (e.g. a coordinator who just created a
    // group), and canGrantAccessTo is group-aware.
    const currentMe = (data.users || []).find((u) => u.id === me.id) || me;
    reconcileUserAccess(data, currentMe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && data.users, me, loaded]);

  // ── Relay (Phase 2, Step 2c-2) ──────────────────────────────────────────
  // While a user is logged in, fold the public request inboxes into the private
  // data: a household's pickup request becomes a real pickup; a public parent
  // signup becomes a "pending" parent for approval. Each request is processed
  // once per timestamp version (sourceRequestId guards against duplicates).
  // The deterministic inbox document is retained as the 24-hour reminder clock.
  const processingReqs = useRef(new Set());
  useEffect(() => {
    if (!me) return;
    // Relay processing is COORDINATOR-only under the platform-only model. A
    // coordinator resolves one routed request by opaque Bag id without loading
    // the Group's family collections, and is the role that approves parents;
    // Admins/Main load none of that (and
    // the rules deny Admin-created parents), so an Admin relay could neither
    // resolve pickups nor persist signups -- it is removed here. (A backend/
    // Cloud Function relay remains the cleaner long-term design.)
    const canRelayPickups = me.role === "coordinator";
    const canRelaySignups = me.role === "coordinator";
    if (!canRelayPickups && !canRelaySignups) return;

    let unsubPickups = () => {};
    let unsubSignups = () => {};

    if (canRelayPickups) {
      // One rule-provable listener per 10-Group chunk. pickupRequests now carry
      // a route copied from the private-free public Bag projection and the
      // Rules verify that route, closing cross-Group inbox enumeration.
      const pickupSources = chunk10(meLive.groupIds || []).map((c) =>
        query(collection(db, "pickupRequests"), where("groupId", "in", c)),
      );
      const pickupSubs = pickupSources.map((sourceQ) =>
        onSnapshot(
          sourceQ,
          async (snap) => {
          const tasks = snap.docs.map(async (ds) => {
            const reqId = ds.id;
            const req = ds.data() || {};
            const requestVersion = String(req.requestVersion || "");
            if (!requestVersion || !req.requestedAt) {
              console.warn(
                "Legacy Pickup request requires the v4.18c request migration",
                reqId,
              );
              return;
            }
            const key = `pickup:${reqId}:${requestVersion}`;
            if (processingReqs.current.has(key)) return;
            if (!(meLive.groupIds || []).includes(req.groupId)) return;
            processingReqs.current.add(key);
            try {
              await relayPickupRequestAtomic(reqId, req, requestVersion, meLive);
              // Keep the version key for this session. A valid 24-hour reminder
              // changes createdAt and therefore produces a new key.
            } catch (e) {
              console.error("Minimum-data Pickup relay failed", e);
              processingReqs.current.delete(key);
            }
          });
          await Promise.all(tasks);
        },
          (e) => console.error("pickupRequests relay error", e),
        ),
      );
      unsubPickups = () => pickupSubs.forEach((u) => u());
    }

    if (canRelaySignups) {
      // Firestore rules reject a collection-wide signup listen, so a
      // Coordinator opens one group-filtered listener per 10-group chunk.
      const signupSources = chunk10(meLive.groupIds || []).map((c) =>
        query(collection(db, "parentSignups"), where("groupId", "in", c)),
      );
      const signupSubs = signupSources.map((sourceQ) =>
        onSnapshot(
          sourceQ,
          async (snap) => {
            const tasks = snap.docs.map(async (ds) => {
              const reqId = ds.id;
              const key = `signup:${reqId}`;
              if (processingReqs.current.has(key)) return;
              const req = ds.data() || {};
              if (!(meLive.groupIds || []).includes(req.groupId)) return;
              processingReqs.current.add(key);
              try {
                const cur = dataRef.current;
                if (!cur) return;
                const result = await relayParentSignupAtomic(
                  reqId,
                  req,
                  meLive,
                  cur,
                );
                if (!result?.relayed) {
                  // A duplicate needs trusted uniqueness/cleanup handling. Keep
                  // the request rather than deleting PII without creating the
                  // matching pending identity.
                  if (result?.reason === "duplicate")
                    console.warn("Duplicate Parent signup needs secure review", reqId);
                  return;
                }
                remoteUpdate.current = true;
                setData((d) => ({
                  ...d,
                  users: [...(d.users || []), result.newUser],
                  coordinatorParents: [
                    ...(d.coordinatorParents || []),
                    result.projection,
                  ],
                  notifications: result.notification
                    ? [...(d.notifications || []), result.notification]
                    : d.notifications,
                }));
              } catch (error) {
                console.error("Atomic Parent signup relay failed", error);
                processingReqs.current.delete(key);
              }
            });
            await Promise.all(tasks);
        },
          (e) => console.error("parentSignups relay error", e),
        ),
      );
      unsubSignups = () => signupSubs.forEach((u) => u());
    }

    return () => {
      unsubPickups();
      unsubSignups();
    };
  }, [me, meLive, myGroupKey]);

  useEffect(() => {
    if (!me) return; // private live-sync only after login

    if (mode === "split") {
      // Every role is scoped. Re-run its exact loader periodically instead of
      // retaining a dormant full-collection listener branch that could be
      // accidentally re-enabled against a partial dataset.
      const iv = setInterval(async () => {
        if (document.hidden) return;
        if (hasUnresolvedPersistence()) return;
        if (Date.now() - lastEditRef.current < 8000) return;
        try {
          const fresh = normalizeScoped(await loadForRole(me.role, me.id));
          remoteUpdate.current = true;
          setData(fresh);
        } catch (e) {
          console.error("scoped refresh failed", e);
        }
      }, 25000);
      return () => clearInterval(iv);
    }

    // ── Legacy mode (pre-migration): the old single-doc listener.
    const ref = doc(db, "appData", SK);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        const incoming = migrate(snap.data());

        // Safety guard: once setup is done it should never go back to a blank
        // state. If a snapshot arrives that looks like a reset (setup not done
        // and no users) but we already have real data on screen, ignore it.
        // This stops a blank state from another device from wiping the display.
        setData((prev) => {
          const prevHasData =
            prev && (prev.setupDone || (prev.users && prev.users.length > 0));
          const incomingIsBlank =
            !incoming.setupDone &&
            (!incoming.users || incoming.users.length === 0);
          if (prevHasData && incomingIsBlank) {
            console.warn("Ignoring a blank live update to protect data.");
            return prev;
          }
          remoteUpdate.current = true;
          return incoming;
        });
      },
      (err) => {
        console.error("Firestore live sync error", err);
      },
    );

    return () => unsub();
  }, [me, mode]);

  // Pickup timeout/reallocation is intentionally not run by the Coordinator
  // browser after privacy narrowing. It requires a trusted scheduled backend
  // command so it can inspect active Pickups without exposing them to staff.

  const lastEditRef = useRef(0);
  const up = (fn) => {
    lastEditRef.current = Date.now();
    setData((prev) => fn(prev));
  };
  const retryLatestSave = () =>
    retryFailedOrdinarySave((next) =>
      setSaveState((prev) => (next.revision >= prev.revision ? next : prev)),
    );
  const discardLatestSave = async () => {
    if (!me) return;
    try {
      discardFailedOrdinarySave();
      const fresh =
        mode === "split"
          ? normalizeScoped(await loadForRole(me.role, me.id))
          : migrate(await loadAll());
      remoteUpdate.current = true;
      setData(fresh);
      setSaveState({ status: "idle", revision: saveRevision, error: null });
      toast("Unsaved local changes were discarded and the server copy was reloaded.");
    } catch (error) {
      console.error("Could not reload after discarding local changes", error);
      setSaveState((prev) => ({ ...prev, status: "error", error }));
      toast("Couldn't reload the server copy just now. Please try again before making more changes.");
    }
  };
  if (view === "loadError")
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          fontFamily: "Quicksand, sans-serif",
          padding: 24,
          textAlign: "center",
          background: "#f1f8f4",
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔌</div>
        <h2 style={{ color: "#1B5E20", margin: "0 0 8px" }}>
          Couldn't connect
        </h2>
        <p style={{ color: "#444", maxWidth: 360, margin: "0 0 16px" }}>
          We couldn't load your data just now. Your information is safe — please
          check your internet connection and try again.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: "#2E7D32",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "12px 24px",
            fontSize: 16,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "Quicksand, sans-serif",
          }}
        >
          Try Again
        </button>
      </div>
    );
  // Only the explicit loading view blocks here. The public screens
  // (setup / login / signup / bagError / neighbour) render with data === null;
  // private data is loaded at login, before the dashboard is shown.
  if (view === "loading") return <Loading />;
  if (view === "bootstrapRequired") return <BootstrapRequiredScreen />;
  if (view === "login")
    return (
      <LoginScreen
        data={data}
        onLogin={async (loginId) => {
          // Called AFTER a successful Firebase sign-in. Now that we are signed
          // in, load the data fresh (under locked database rules this read only
          // works once authenticated) and find the matching account.
          const lc = (loginId || "").toLowerCase();
          let d = data;
          // The access doc's appUserId is the AUTHORITATIVE identity mapping for
          // this login. We resolve the account by it first, so a stray user
          // record that happens to share this Firebase UID can never shadow the
          // real account the access doc points to.
          let accessAppUserId = null;
          try {
            // Stage 4: find MY access doc first (role + app user id), then
            // load only the slice that role is allowed to read. Staff load
            // everything; parents/kids/sponsors load their scoped queries.
            const fbU = auth.currentUser && auth.currentUser.uid;
            const accessSnap = fbU
              ? await getDoc(doc(db, "userAccess", fbU)).catch(() => null)
              : null;
            if (accessSnap && accessSnap.exists())
              accessAppUserId = accessSnap.data().appUserId || null;
            const fresh =
              accessSnap && accessSnap.exists()
                ? normalizeScoped(
                    await loadForRole(
                      accessSnap.data().role,
                      accessSnap.data().appUserId,
                    ),
                  )
                : migrate(await loadAll()); // bootstrap / legacy fallback
            remoteUpdate.current = true;
            setData(fresh);
            setLoaded(true);
            d = fresh;
          } catch (e) {
            console.error("Could not load data after login.", e);
          }
          const users = (d && d.users) || [];
          // Resolve by the access doc's appUserId first (the authoritative
          // mapping), then by Firebase UID (survives an email change), then by
          // typed email/username for bootstrap accounts with no authUid yet.
          const uidNow = auth.currentUser && auth.currentUser.uid;
          let acct =
            (accessAppUserId && users.find((x) => x.id === accessAppUserId)) ||
            (uidNow && users.find((x) => x.authUid === uidNow)) ||
            users.find(
              (x) =>
                (x.email || "").toLowerCase() === lc ||
                (x.username || "").toLowerCase() === lc,
            );
          if (!acct) {
            try {
              await signOut(auth);
            } catch (e) {}
            return "Signed in, but we couldn't find your account. Please contact Kids Recycle Cycle.";
          }
          if (acct.status === "pending") {
            try {
              await signOut(auth);
            } catch (e) {}
            return "Account pending owner approval.";
          }
          if (!acct.active) {
            try {
              await signOut(auth);
            } catch (e) {}
            return "Account deactivated.";
          }
          // Identity mappings are immutable in v4.18c. Legacy accounts whose
          // user.authUid does not match Firebase Auth must be migrated with the
          // Admin SDK before these Rules are deployed; the browser must never
          // silently rebind a login to another KRC identity.
          const fbUid = auth.currentUser && auth.currentUser.uid;
          if (!fbUid || acct.authUid !== fbUid) {
            try {
              await signOut(auth);
            } catch (e) {}
            return "This account needs a secure identity migration before it can sign in. Please contact Kids Recycle Cycle.";
          }
          // Firebase Auth's verified email remains the credential of record.
          // Refresh the user and the email bookkeeping on the existing immutable
          // userAccess mapping in one atomic batch.
          const fbEmail = (
            (auth.currentUser && auth.currentUser.email) || ""
          ).toLowerCase();
          const emailStale =
            fbEmail && (acct.email || "").toLowerCase() !== fbEmail;
          const pendingDone =
            fbEmail && (acct.pendingEmail || "").toLowerCase() === fbEmail;
          if (emailStale || pendingDone) {
            const userPatch = {
              ...(emailStale ? { email: fbEmail, username: fbEmail } : {}),
              ...(pendingDone ? { pendingEmail: null } : {}),
            };
            try {
              const syncedEmail = await runAtomicCommand(() =>
                syncOwnAccessEmailRemote(acct.id, fbEmail),
              );
              userPatch.email = syncedEmail;
              userPatch.username = syncedEmail;
              userPatch.pendingEmail = null;
            } catch (e) {
              console.error("Verified email refresh failed", e);
              try {
                await signOut(auth);
              } catch (signOutError) {}
              return "Your verified email could not be synchronised safely. Please try again or contact Kids Recycle Cycle.";
            }
            acct = { ...acct, ...userPatch };
            d = {
              ...d,
              users: d.users.map((u) => (u.id === acct.id ? acct : u)),
            };
            remoteUpdate.current = true;
            setData(d);
          }
          // Self-heal the public setup flag (so a set-up business never shows
          // the Setup screen again), and seed/refresh this user's allowlist
          // entry now that we know their authUid.
          await writeSetupStatus();
          if (
            acct.authUid &&
            ["main", "admin"].includes(acct.role)
          )
            await ensureUserAccess(acct, acct.role);

          // ── One-time upgrade: legacy single-doc -> split collections ──
          // Runs automatically on the first admin login on a legacy database.
          // The old doc is kept (marked migratedAt) as a frozen backup.
          if (
            getStoreMode() === "legacy" &&
            ["main", "admin"].includes(acct.role)
          ) {
            try {
              await migrateToCollections(d);
              setMode("split");
              toast("Database upgraded to the new format ✅");
            } catch (e) {
              console.error("Migration to collections failed", e);
              toast(
                "Database upgrade didn't finish — it will retry on your next login.",
              );
            }
          } else {
            setMode(getStoreMode());
          }

          setMe(acct);
          setView("dash");
          return null;
        }}
        onSignup={() => setView("signup")}
      />
    );
  if (view === "signup")
    return <SignupScreen data={data} up={up} onBack={() => setView("login")} />;
  if (view === "bagError") return <BagErrorScreen message={bagError} />;
  if (view === "neighbour" && (publicBag || demoHouse))
    return (
      <NeighbourScreen
        house={demoHouse}
        data={data}
        up={up}
        bagToken={bagToken}
        publicBag={publicBag}
        onBack={me ? () => setView("dash") : null}
      />
    );
  if (view === "dash" && me) {
    const liveMe = data.users.find((u) => u.id === me.id) || me;
    return (
      <Dashboard
        data={data}
        user={liveMe}
        up={up}
        onLogout={() => {
          signOut(auth).catch(() => {});
          setMe(null);
          setSaveState({ status: "idle", revision: 0, error: null });
          // Clear private data from memory so logout leaves nothing behind.
          setData(null);
          setLoaded(false);
          dataRef.current = null;
          lastSaved = null; // module-level persisted baseline holds data too
          clearPersistenceSession();
          _lastPublicBags = {}; // never carry one login's mirror cache into another
          publicSeeded.current = false;
          setView("login");
        }}
        saveState={saveState}
        onRetrySave={retryLatestSave}
        onDiscardSave={discardLatestSave}
        onDemo={(h) => {
          setDH(h);
          setBagToken(null);
          setView("neighbour");
        }}
      />
    );
  }
  return <Loading />;
}

// ━━━ LOADING ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Loading -> a simple full-screen "loading..." view.
function Loading() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: C.bg,
      }}
    >
      <Fonts />
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 56 }}>♻️🚲</div>
        <div
          style={{ fontFamily: ff.h, fontSize: 22, color: C.g, marginTop: 8 }}
        >
          Loading...
        </div>
      </div>
    </div>
  );
}

// BagErrorScreen -> shown to a neighbour when a scanned bag QR can't be found.
function BagErrorScreen({ message }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: `radial-gradient(ellipse at 50% 0%,${C.gp} 0%,${C.bg} 60%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: ff.b,
      }}
    >
      <Fonts />
      <Card style={{ maxWidth: 380, textAlign: "center", padding: 28 }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>♻️🚲</div>
        <h1
          style={{
            fontFamily: ff.h,
            fontSize: 24,
            color: C.gd,
            margin: "0 0 8px",
          }}
        >
          Kids Recycle Cycle
        </h1>
        <p
          style={{
            color: C.m,
            fontSize: 14,
            lineHeight: 1.6,
            margin: "0 0 16px",
          }}
        >
          {message}
        </p>
        <a
          href="/"
          style={{ ...btnG, display: "inline-block", textDecoration: "none" }}
        >
          Go to homepage
        </a>
      </Card>
    </div>
  );
}

// HouseLocationPicker -> a map dialog for setting or clearing a house's location.
function HouseLocationPicker({ house, onSave, onClear, onClose }) {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markerRef = useRef(null);
  const existing = getHouseLocation(house);
  const [started, setStarted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loc, setLoc] = useState(existing);
  const [msg, setMsg] = useState(
    existing
      ? "Saved location loaded. Drag the marker or click the map to change it."
      : "Click the map to place the pin, then drag it to adjust the location.",
  );

  useEffect(() => {
    loadLeafletOnce(setLoaded);
  }, []);
  useEffect(() => {
    if (!loaded || !mapRef.current || !window.L || mapInst.current) return;
    const start = loc || DEFAULT_MAP_CENTER;
    const map = window.L.map(mapRef.current).setView(
      [start.lat, start.lng],
      loc ? 17 : 14,
    );
    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OSM",
    }).addTo(map);
    map.on("click", (e) => {
      setLoc({
        lat: e.latlng.lat,
        lng: e.latlng.lng,
        label: "Selected by parent on map",
      });
      setMsg("Location selected. Click Save location when it looks right.");
    });
    mapInst.current = map;
    setStarted(true);
    return () => {
      if (mapInst.current) {
        mapInst.current.remove();
        mapInst.current = null;
        markerRef.current = null;
      }
    };
  }, [loaded]);

  useEffect(() => {
    if (!started || !mapInst.current || !window.L || !loc) return;
    const p = [loc.lat, loc.lng];
    if (!markerRef.current) {
      markerRef.current = window.L.marker(p, { draggable: true }).addTo(
        mapInst.current,
      );
      markerRef.current.on("dragend", (e) => {
        const ll = e.target.getLatLng();
        setLoc({
          lat: ll.lat,
          lng: ll.lng,
          label: "Selected by parent on map",
        });
        setMsg("Marker moved. Click Save location when it looks right.");
      });
    } else markerRef.current.setLatLng(p);
    mapInst.current.setView(p, 17);
  }, [loc, started]);



  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: ff.b,
      }}
    >
      <Fonts />
      <div
        style={{
          background: C.w,
          borderRadius: 20,
          width: "min(760px,100%)",
          maxHeight: "92vh",
          overflow: "auto",
          boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
          padding: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            marginBottom: 10,
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: ff.h,
                color: C.gd,
                fontSize: 22,
                margin: "0 0 2px",
              }}
            >
              📍 Set house map location
            </h2>
            <p style={{ margin: 0, color: C.m, fontSize: 13 }}>
              For {house.nickname || house.name}. The Parent chooses the pin
              used on the pickup map.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#f5f5f5",
              border: `1px solid ${C.brd}`,
              borderRadius: 10,
              padding: "8px 12px",
              cursor: "pointer",
              fontFamily: ff.b,
              fontWeight: 700,
            }}
          >
            ✕
          </button>
        </div>
        <div
          style={{
            background: C.bp,
            border: `1px solid ${C.blue}33`,
            borderRadius: 12,
            padding: "9px 11px",
            marginBottom: 10,
            color: C.blue,
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          Address search is disabled in the pilot so a Household address is not
          sent to an external geocoding service. Pan or zoom the map and click
          the correct location manually.
        </div>
        <p
          style={{
            margin: "0 0 10px",
            fontSize: 12,
            color: msg.startsWith("Could") || msg.startsWith("No ") ? C.r : C.m,
          }}
        >
          {msg}
        </p>
        <div
          ref={mapRef}
          style={{
            height: 420,
            borderRadius: 16,
            overflow: "hidden",
            border: `2px solid ${C.brd}`,
            background: C.bg,
          }}
        />
        {loc && (
          <p style={{ margin: "8px 0 0", fontSize: 11, color: C.m }}>
            Selected: {loc.lat.toFixed(6)}, {loc.lng.toFixed(6)}
          </p>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            marginTop: 14,
            flexWrap: "wrap",
          }}
        >
          <button onClick={onClear} style={btnD}>
            Clear saved location
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                background: "#eee",
                border: "none",
                borderRadius: 10,
                padding: "10px 16px",
                cursor: "pointer",
                fontFamily: ff.b,
                fontWeight: 700,
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => loc && onSave(loc)}
              disabled={!loc}
              style={{ ...btnG, opacity: loc ? 1 : 0.55 }}
            >
              Save location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ━━━ BOOTSTRAP REQUIRED ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// The first Main account must be provisioned out of band with trusted Admin-SDK
// tooling. The production browser must never create an Auth account first and
// then attempt privileged Firestore bootstrap writes that locked rules deny.
function BootstrapRequiredScreen() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: `radial-gradient(ellipse at 50% 20%,${C.gp} 0%,${C.bg} 70%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: ff.b,
      }}
    >
      <Fonts />
      <Card>
        <div style={{ textAlign: "center", maxWidth: 460 }}>
          <div style={{ fontSize: 42, marginBottom: 8 }}>🔒</div>
          <h2 style={{ margin: "0 0 8px", color: C.gd, fontFamily: ff.h }}>
            Secure setup required
          </h2>
          <p style={{ margin: "0 0 10px", color: C.m, lineHeight: 1.55 }}>
            This deployment has not been securely provisioned yet. The first
            Main account must be created using the protected deployment
            bootstrap process, not from the public website.
          </p>
          <p style={{ margin: 0, color: C.m, fontSize: 13 }}>
            No login account has been created by this screen. Contact the KRC
            deployment administrator to complete setup.
          </p>
        </div>
      </Card>
    </div>
  );
}

// ━━━ LOGIN ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SCREEN: LoginScreen -> email + password sign-in, plus "Forgot password?".
// Signs in via Firebase FIRST; the App then loads the data and finds the account.
function LoginScreen({ data, onLogin, onSignup }) {
  const [u, sU] = useState("");
  const [p, sP] = useState("");
  const [err, sE] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState("");

  const go = async () => {
    sE("");
    setResetMsg("");
    const email = u.trim().toLowerCase();
    const pw = p;
    if (!email || !pw) {
      sE("Please enter your email and password.");
      return;
    }
    if (!email.includes("@")) {
      sE("Please enter your email address.");
      return;
    }
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, pw);
      const loginErr = await onLogin(email);
      if (loginErr) sE(loginErr);
    } catch (e) {
      const code = (e && e.code) || "";
      if (code === "auth/operation-not-allowed") {
        sE(
          "Email/password sign-in isn't switched on in Firebase yet. (Admin: turn it on in the Firebase console under Authentication → Sign-in method.)",
        );
      } else if (
        [
          "auth/user-not-found",
          "auth/invalid-credential",
          "auth/invalid-login-credentials",
          "auth/wrong-password",
        ].includes(code)
      ) {
        sE("Wrong email or password.");
      } else if (code === "auth/too-many-requests") {
        sE("Too many tries. Please wait a moment and try again.");
      } else if (code === "auth/invalid-email") {
        sE("That doesn't look like a valid email.");
      } else {
        sE("Couldn't sign in. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    sE("");
    setResetMsg("");
    const email = u.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      sE('Type your email above first, then tap "Forgot password?".');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMsg(
        "If an account uses that email, a reset link is on its way — check your inbox (and spam).",
      );
    } catch (e) {
      const code = (e && e.code) || "";
      if (code === "auth/user-not-found") {
        setResetMsg(
          "If you've never logged in yet, sign in once with the password you were given, then you can reset it.",
        );
      } else if (code === "auth/operation-not-allowed") {
        sE("Email/password sign-in isn't switched on in Firebase yet.");
      } else if (code === "auth/invalid-email") {
        sE("That doesn't look like a valid email.");
      } else {
        sE("Couldn't send a reset email. Please try again.");
      }
    }
  };
  return (
    <div
      style={{
        minHeight: "100vh",
        background: `radial-gradient(ellipse at 50% 20%,${C.gp} 0%,${C.bg} 70%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <Fonts />
      <div
        style={{
          background: C.w,
          borderRadius: 24,
          padding: 36,
          maxWidth: 380,
          width: "100%",
          boxShadow: "0 12px 48px rgba(46,125,50,0.12)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 48 }}>♻️🚲</div>
          <h1
            style={{
              fontFamily: ff.h,
              color: C.gd,
              fontSize: 24,
              margin: "8px 0 0",
            }}
          >
            Kids Recycle Cycle
          </h1>
          <p
            style={{
              color: C.gold,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              margin: "4px 0 0",
            }}
          >
            Powered by Pedals not Petrol
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <input
            placeholder="Email"
            type="email"
            autoComplete="email"
            value={u}
            onChange={(e) => {
              sU(e.target.value);
              sE("");
              setResetMsg("");
            }}
            onKeyDown={(e) => e.key === "Enter" && go()}
            style={inp}
          />
          <input
            placeholder="Password"
            type="password"
            value={p}
            onChange={(e) => {
              sP(e.target.value);
              sE("");
            }}
            onKeyDown={(e) => e.key === "Enter" && go()}
            style={inp}
          />
          {err && <p style={{ color: C.r, fontSize: 13, margin: 0 }}>{err}</p>}
          {resetMsg && (
            <p style={{ color: C.g, fontSize: 13, margin: 0 }}>{resetMsg}</p>
          )}
          <button onClick={go} style={btnG} disabled={busy}>
            {busy ? "Logging in…" : "Log In"}
          </button>
          <div style={{ textAlign: "center", marginTop: 2 }}>
            <button
              onClick={forgot}
              style={{
                background: "none",
                border: "none",
                color: C.m,
                fontFamily: ff.b,
                fontSize: 13,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Forgot password?
            </button>
          </div>
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <button
              onClick={onSignup}
              style={{
                background: "none",
                border: "none",
                color: C.g,
                fontFamily: ff.b,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Create Account (Parent)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ━━━ SIGNUP ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SCREEN: SignupScreen -> a parent self-registers. Creates a secure login and
// leaves the account pending owner approval.
function SignupScreen({ data, up, onBack }) {
  const [f, sF] = useState({
    email: "",
    password: "",
    confirm: "",
    firstName: "",
    lastName: "",
    yearOfBirth: "",
  });
  const [err, sE] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, sD] = useState(false);
  const go = async () => {
    if (
      !f.email ||
      !f.password ||
      !f.firstName ||
      !f.lastName ||
      !f.yearOfBirth
    ) {
      sE("Fill in all fields");
      return;
    }
    if (!f.email.includes("@")) {
      sE("Please enter a valid email address");
      return;
    }
    if (f.password.length < 6) {
      sE("Password must be at least 6 characters");
      return;
    }
    if (f.password !== f.confirm) {
      sE("Passwords don't match");
      return;
    }
    if (!validYob(parseInt(f.yearOfBirth))) {
      sE("Please enter a real year of birth (e.g. 1985)");
      return;
    }
    if (ageFrom(parseInt(f.yearOfBirth)) < 18) {
      sE("Must be 18+ for parent account");
      return;
    }
    const email = f.email.trim().toLowerCase();
    // No private-data duplicate check here: a logged-out visitor no longer loads
    // private data. Duplicates are caught by Firebase Auth (auth/email-already-
    // in-use, handled below) and by the relay, which skips a signup whose email
    // or authUid already matches a user.
    setBusy(true);
    let authUid = null;
    try {
      // Create the secure login now, without disturbing anyone's session.
      authUid = await createAuthAccount(email, f.password);
    } catch (e) {
      const code = (e && e.code) || "";
      setBusy(false);
      if (code === "auth/email-already-in-use") {
        sE("An account with that email already exists.");
      } else if (code === "auth/invalid-email") {
        sE("That doesn't look like a valid email.");
      } else if (code === "auth/weak-password") {
        sE("Password must be at least 6 characters.");
      } else if (code === "auth/operation-not-allowed") {
        sE(
          "Email sign-in isn't switched on in Firebase yet (enable Email/Password in the console).",
        );
      } else {
        sE("Couldn't create the account. Please try again.");
      }
      return;
    }
    // Send the signup to the public inbox (the only write a not-signed-in
    // visitor makes). A logged-in admin/coordinator's relay turns it into a
    // "pending" parent for approval — no access is granted until then. The
    // secure login was already created above. Defaults to the KRC Crew group
    // for unaffiliated parents (coordinator-QR group pre-fill is a later add).
    try {
      await submitSignupRemote({
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        yearOfBirth: parseInt(f.yearOfBirth),
        email,
        authUid,
        groupId: "grp-krc-crew",
      });
    } catch (e) {
      // The login was created but the signup request didn't save. Do NOT show
      // success (that would leave the person thinking they're signed up with an
      // orphaned login). Tell them to contact us rather than sign up again.
      console.error("Could not submit signup request", e);
      setBusy(false);
      sE(
        "Your login was created, but we couldn't finish your sign-up. Please contact Kids Recycle Cycle — don't sign up again with this email.",
      );
      return;
    }
    setBusy(false);
    sD(true);
  };
  if (done)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: `radial-gradient(ellipse at 50% 20%,${C.gp} 0%,${C.bg} 70%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <Fonts />
        <div
          style={{
            background: C.w,
            borderRadius: 24,
            padding: 36,
            maxWidth: 400,
            width: "100%",
            boxShadow: "0 12px 48px rgba(46,125,50,0.12)",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <h2 style={{ fontFamily: ff.h, color: C.gd, fontSize: 22 }}>
            Account Created!
          </h2>
          <p style={{ color: C.m, fontSize: 14, margin: "8px 0 20px" }}>
            Pending owner approval.
          </p>
          <button onClick={onBack} style={btnG}>
            Back to Login
          </button>
        </div>
      </div>
    );
  return (
    <div
      style={{
        minHeight: "100vh",
        background: `radial-gradient(ellipse at 50% 20%,${C.gp} 0%,${C.bg} 70%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <Fonts />
      <div
        style={{
          background: C.w,
          borderRadius: 24,
          padding: 36,
          maxWidth: 400,
          width: "100%",
          boxShadow: "0 12px 48px rgba(46,125,50,0.12)",
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: C.g,
            fontSize: 13,
            cursor: "pointer",
            fontFamily: ff.b,
            marginBottom: 12,
          }}
        >
          ← Back
        </button>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 36 }}>♻️🚲</div>
          <h2
            style={{
              fontFamily: ff.h,
              color: C.gd,
              fontSize: 22,
              margin: "8px 0 0",
            }}
          >
            Parent Sign Up
          </h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <input
            placeholder="Email"
            type="email"
            value={f.email}
            onChange={(e) => sF({ ...f, email: e.target.value })}
            style={inp}
          />
          <input
            placeholder="Password (at least 6 characters)"
            type="password"
            value={f.password}
            onChange={(e) => sF({ ...f, password: e.target.value })}
            style={inp}
          />
          <input
            placeholder="Confirm password"
            type="password"
            value={f.confirm}
            onChange={(e) => sF({ ...f, confirm: e.target.value })}
            style={inp}
          />
          <input
            placeholder="First name"
            value={f.firstName}
            onChange={(e) => sF({ ...f, firstName: e.target.value })}
            style={inp}
          />
          <input
            placeholder="Last name"
            value={f.lastName}
            onChange={(e) => sF({ ...f, lastName: e.target.value })}
            style={inp}
          />
          <input
            placeholder="Year of birth"
            type="number"
            value={f.yearOfBirth}
            onChange={(e) => sF({ ...f, yearOfBirth: e.target.value })}
            style={inp}
          />
          {err && <p style={{ color: C.r, fontSize: 13, margin: 0 }}>{err}</p>}
          <button onClick={go} style={btnG} disabled={busy}>
            {busy ? "Creating…" : "Create Account"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ━━━ NEIGHBOUR SCREEN ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SCREEN: NeighbourScreen -> the PUBLIC page a neighbour reaches from a bag's QR
// code, to request a pickup. (No login required.)
function NeighbourScreen({ house, data, up, bagToken, publicBag, onBack }) {
  const [pressed, setP] = useState(false);
  // PUBLIC scan: render purely from the public payload {bagNumber,status} — no
  // private data is read. The in-app "demo" preview (no publicBag) still derives
  // from the live private data so staff can see real status while testing.
  const isPublic = !!publicBag;
  let displayName, hasPending, accepted, arrived;
  if (isPublic) {
    // The public page shows ONLY the bag number — never a household label.
    displayName = publicBag.bagNumber || "Your KRC bag";
    const st = publicBag.status || "none";
    arrived = st === "arrived";
    accepted = st === "on_the_way";
    hasPending = st !== "none"; // requested / on_the_way / arrived
  } else {
    const activePU = data.pickups.filter(
      (p) => p.houseId === house.id && !["done", "cancelled"].includes(p.status),
    );
    hasPending = activePU.length > 0;
    accepted = activePU.some((p) => p.status === "accepted");
    arrived = activePU.some((p) => p.arrivedAt);
    // Staff demo preview mirrors the real public page: bag number only.
    const demoBag = (data.bags || []).find(
      (b) => b.houseId === house.id && b.status === "at_house",
    );
    displayName = (demoBag && demoBag.bagNumber) || "Your KRC bag";
  }
  const handlePress = () => {
    if (hasPending || pressed) return;
    setP(true);
    if (bagToken) {
      // Real bag scan: send the request to the public inbox. A logged-in client
      // (the relay) turns it into a pickup. This is the only write a
      // not-signed-in household makes, and it touches no private data.
      requestPickupRemote(bagToken, publicBag).catch((e) => {
        console.error("Could not send pickup request", e);
        // The write failed — don't leave the household thinking it worked.
        setP(false);
        const msg = String(e?.message || "");
        toast(
          msg.includes("Coordinator refresh")
            ? msg
            : "A pickup is already active, or a reminder was sent less than 24 hours ago. Please try again later if the Bag is still waiting.",
        );
      });
      return;
    }
    // In-app QR preview is display-only. Pickup creation is no longer a
    // family-browser write; real requests go through the public request inbox.
    toast("Preview only — no pickup request was created.");
  };
  return (
    <div
      style={{
        minHeight: "100vh",
        background: `radial-gradient(ellipse at 50% 0%,${C.gp} 0%,${C.bg} 60%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 20px",
        fontFamily: ff.b,
      }}
    >
      <Fonts />
      {onBack && (
        <button
          onClick={onBack}
          style={{
            position: "absolute",
            top: 16,
            left: 16,
            background: "none",
            border: "none",
            color: C.g,
            fontSize: 13,
            cursor: "pointer",
            fontFamily: ff.b,
          }}
        >
          ← Dashboard (demo)
        </button>
      )}
      <img
        src="/logo.png"
        alt="Kids Recycle Cycle"
        style={{
          width: 160,
          height: 160,
          borderRadius: "50%",
          boxShadow: "0 8px 32px rgba(46,125,50,0.3)",
          marginBottom: 20,
        }}
      />
      <Card style={{ textAlign: "center", maxWidth: 360 }}>
        <p style={{ margin: 0, color: C.m, fontSize: 13 }}>Hello!</p>
        <p
          style={{
            margin: "4px 0 0",
            fontFamily: ff.h,
            fontSize: 22,
            color: C.tx,
          }}
        >
          {displayName}
        </p>
      </Card>
      <div style={{ marginTop: 16, textAlign: "center" }}>
        {arrived ? (
          <div>
            <div
              style={{
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: `linear-gradient(180deg,${C.gg},${C.gl})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                border: `6px solid ${C.gd}`,
                boxShadow: "0 8px 0 #388e3c",
                margin: "0 auto",
              }}
            >
              <span style={{ fontSize: 40 }}>🚲</span>
              <span
                style={{
                  fontFamily: ff.h,
                  fontSize: 15,
                  color: C.w,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                Collector{"\n"}arrived!
              </span>
            </div>
            <p
              style={{
                color: C.g,
                fontSize: 14,
                fontWeight: 600,
                marginTop: 16,
              }}
            >
              Our collector is at your door!
            </p>
          </div>
        ) : accepted ? (
          <div>
            <div
              style={{
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: `linear-gradient(180deg,${C.gg},${C.gl})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                border: `6px solid ${C.gd}`,
                boxShadow: "0 8px 0 #388e3c",
                margin: "0 auto",
              }}
            >
              <span style={{ fontSize: 40 }}>🚲</span>
              <span
                style={{
                  fontFamily: ff.h,
                  fontSize: 16,
                  color: C.w,
                  fontWeight: 700,
                }}
              >
                On the way!
              </span>
            </div>
            <p
              style={{
                color: C.g,
                fontSize: 14,
                fontWeight: 600,
                marginTop: 16,
              }}
            >
              Someone is heading over!
            </p>
          </div>
        ) : hasPending || pressed ? (
          <div>
            <div
              style={{
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: `linear-gradient(180deg,${C.gg},${C.gl})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                border: `6px solid ${C.gd}`,
                boxShadow: "0 8px 0 #388e3c",
                margin: "0 auto",
              }}
            >
              <span style={{ fontSize: 40 }}>✅</span>
              <span
                style={{
                  fontFamily: ff.h,
                  fontSize: 15,
                  color: C.w,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                Pickup{"\n"}Requested!
              </span>
            </div>
            <p
              style={{ color: C.m, fontSize: 13, marginTop: 16, maxWidth: 280 }}
            >
              We'll be around within 2 days on our bikes! 🚲
            </p>
            <p
              style={{
                color: C.g,
                fontSize: 14,
                fontWeight: 600,
                marginTop: 8,
              }}
            >
              Thanks for recycling with us! ♻️
            </p>
          </div>
        ) : (
          <div>
            <button
              onClick={handlePress}
              style={{
                width: 200,
                height: 200,
                borderRadius: "50%",
                background:
                  "linear-gradient(180deg,#8ee68e 0%,#43a047 50%,#2e7d32 100%)",
                border: `6px solid ${C.gd}`,
                boxShadow: "0 10px 0 #1b5e20,0 14px 40px rgba(46,125,50,0.4)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                outline: "none",
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = "translateY(6px)";
                e.currentTarget.style.boxShadow = "0 4px 0 #1b5e20";
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = "";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = "";
              }}
            >
              <span style={{ fontSize: 36 }}>🚲</span>
              <span
                style={{
                  fontFamily: ff.h,
                  fontSize: 18,
                  color: C.w,
                  fontWeight: 700,
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  textAlign: "center",
                  lineHeight: 1.2,
                }}
              >
                Kids{"\n"}Pick Up!
              </span>
            </button>
            <p style={{ color: C.m, fontSize: 13, marginTop: 16 }}>
              Tap when your bag is full!
            </p>
          </div>
        )}
      </div>
      <div style={{ marginTop: "auto", paddingTop: 40, textAlign: "center" }}>
        <p style={{ color: "#bbb", fontSize: 11, margin: "0 0 4px" }}>
          Kids Can Do
        </p>
        <button
          onClick={() =>
            toast(
              "In the real app, an email would be sent to you to update your details securely.",
            )
          }
          style={{
            background: "none",
            border: "none",
            color: "#ccc",
            fontSize: 10,
            cursor: "pointer",
            fontFamily: ff.b,
            textDecoration: "underline",
          }}
        >
          Update my details
        </button>
      </div>
    </div>
  );
}

// ━━━ DASHBOARD ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Dashboard -> the logged-in shell. Shows the right tabs for the user's role
// and routes between them.
function Dashboard({
  data,
  user,
  up,
  onLogout,
  onDemo,
  saveState,
  onRetrySave,
  onDiscardSave,
}) {
  const [showN, setShowN] = useState(false);
  const [tab, setTab] = useState(
    user.role === "coordinator"
      ? "coordhome"
      : user.role === "sponsor"
        ? "sponsorhome"
        : user.role === "admin"
          ? "approvals"
          : "pickups",
  );
  const myNotifs = data.notifications.filter(
    (n) => n.userId === user.id && !n.read,
  );
  const clearN = () =>
    up((d) => ({
      ...d,
      notifications: d.notifications.map((n) =>
        n.userId === user.id ? { ...n, read: true } : n,
      ),
    }));
  const isOwner = user.role === "admin";
  // Badge count must match the Approvals tab exactly — use the same helper,
  // which also handles both single groupId and groupIds[].
  const pendingApprovals = [
    ...(data.users || []),
    ...(data.coordinatorParents || []),
  ].filter((u) => u.status === "pending" && canGrantAccessTo(user, u));
  const myKidIds =
    user.role === "parent"
      ? data.users.filter((u) => u.parentId === user.id).map((u) => u.id)
      : [];
  const declineReqs =
    user.role === "parent"
      ? data.declineRequests.filter(
          (dr) => dr.status === "pending" && myKidIds.includes(dr.kidId),
        )
      : isOwner
        ? data.declineRequests.filter((dr) => dr.status === "pending")
        : [];

  const allTabs = [
    { key: "coordhome", label: "🏫 My Group", roles: ["coordinator"] },
    { key: "sponsorhome", label: "💚 My Sponsorship", roles: ["sponsor"] },
    // Admins operate at platform level and do not collect pickups (they may
    // not even be in the group), so they get neither the Pickups nor the
    // Unclaimed tab. Only a Kid, Parent, or Coordinator nominates to collect.
    { key: "pickups", label: "🚲 Pickups", roles: ["parent", "kid"] },
    { key: "unclaimed", label: "📭 Unclaimed", roles: ["parent", "kid"] },
    { key: "approvals", label: "✅ Approve", roles: ["admin", "coordinator"] },
    { key: "groups", label: "🏫 Groups", roles: ["admin"] },
    { key: "sponsors", label: "💚 Sponsors", roles: ["admin", "coordinator"] },
    // Activity tab hidden for the pilot: a parent's scoped load doesn't fetch
    // activity docs (rules allow only admins to read them), so the tab could
    // only show session-local entries and would empty on refresh. Re-enable
    // when a properly-scoped parent activity feed exists.
    // { key: "activity", label: "📋 Activity", roles: ["parent"] },
    { key: "houses", label: "🏠 Houses", roles: ["parent", "kid"] },
    { key: "bags", label: "🎒 Bags", roles: ["parent"] },
    { key: "team", label: "👪 Team", roles: ["admin", "parent"] },
    { key: "score", label: "🏆 Family Score", roles: ["parent", "kid"] },
    { key: "map", label: "🗺️ Map", roles: ["parent", "kid"] },
    { key: "qr", label: "📱 QR", roles: ["parent"] },
    {
      key: "settings",
      label: "⚙️",
      roles: ["admin", "coordinator", "sponsor", "parent", "kid"],
    },
  ];
  const tabs = allTabs.filter((t) => t.roles.includes(user.role));
  const pCount = data.pickups.filter((p) => {
    if (user.role === "kid")
      return p.currentKidId === user.id && p.status === "waiting";
    if (user.role === "parent")
      return myKidIds.includes(p.currentKidId) && p.status === "waiting";
    return false;
  }).length;
  const uCount = data.pickups.filter((p) => p.status === "unallocated").length;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: ff.b }}>
      <Fonts />
      <div
        style={{
          background: `linear-gradient(135deg,${C.gd},${C.g})`,
          padding: "14px 16px 10px",
          color: C.w,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 24 }}>♻️🚲</span>
            <div>
              <h1 style={{ margin: 0, fontFamily: ff.h, fontSize: 18 }}>
                Kids Recycle Cycle
              </h1>
              <div
                role="status"
                aria-live="polite"
                style={{
                  fontSize: 10,
                  opacity: 0.95,
                  marginTop: 1,
                  color: saveState?.status === "error" ? "#ffdddd" : "#eef8ef",
                }}
              >
                {saveState?.status === "saving"
                  ? "Saving…"
                  : saveState?.status === "error"
                    ? saveState?.error?.code === "krc/save-conflict"
                      ? "Save conflict — reload or retry after reviewing"
                      : "Save failed — your latest change remains local"
                    : saveState?.status === "saved"
                      ? "Saved"
                      : ""}
              </div>
              {saveState?.status === "error" && (
                <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={onRetrySave}
                    style={{
                      border: "1px solid rgba(255,255,255,.7)",
                      background: "rgba(255,255,255,.16)",
                      color: "#fff",
                      borderRadius: 6,
                      padding: "2px 7px",
                      fontSize: 9,
                      cursor: "pointer",
                    }}
                  >
                    Retry
                  </button>
                  <button
                    type="button"
                    onClick={onDiscardSave}
                    style={{
                      border: "1px solid rgba(255,255,255,.7)",
                      background: "transparent",
                      color: "#fff",
                      borderRadius: 6,
                      padding: "2px 7px",
                      fontSize: 9,
                      cursor: "pointer",
                    }}
                  >
                    Reload server copy
                  </button>
                </div>
              )}
              <p
                style={{
                  margin: 0,
                  fontSize: 9,
                  color: C.gold,
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                Powered by Pedals not Petrol
              </p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {myNotifs.length > 0 && (
              <button
                onClick={() => setShowN((v) => !v)}
                title="Notifications"
                style={{
                  background: C.o,
                  border: "none",
                  borderRadius: 12,
                  padding: "3px 8px",
                  fontSize: 11,
                  color: C.w,
                  cursor: "pointer",
                  fontFamily: ff.b,
                  fontWeight: 700,
                }}
              >
                🔔 {myNotifs.length}
              </button>
            )}
            <div style={{ textAlign: "right" }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700 }}>
                {user.username}
              </p>
              <p style={{ margin: 0, fontSize: 9, opacity: 0.8 }}>
                {roleLabel(user)}
              </p>
            </div>
            <button
              onClick={onLogout}
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "none",
                borderRadius: 8,
                padding: "5px 8px",
                color: C.w,
                cursor: "pointer",
                fontSize: 11,
              }}
            >
              ↗
            </button>
          </div>
        </div>
        {myNotifs.length > 0 && (
          <div
            style={{
              marginTop: 8,
              background: "rgba(255,255,255,0.12)",
              borderRadius: 10,
              padding: "6px 10px",
              maxHeight: showN ? 260 : undefined,
              overflowY: showN ? "auto" : undefined,
            }}
          >
            {(showN ? myNotifs : myNotifs.slice(0, 3)).map((n) => (
              <p
                key={n.id}
                style={{ margin: "2px 0", fontSize: 11, opacity: 0.95 }}
              >
                🔔 {notificationMessage(n, data)}
                {showN && (
                  <span style={{ opacity: 0.65 }}>
                    {" "}
                    · {new Date(n.ts).toLocaleDateString()}
                  </span>
                )}
              </p>
            ))}
            {!showN && myNotifs.length > 3 && (
              <button
                onClick={() => setShowN(true)}
                style={{
                  background: "none",
                  border: "none",
                  color: C.w,
                  textDecoration: "underline",
                  fontSize: 11,
                  cursor: "pointer",
                  padding: 0,
                  fontFamily: ff.b,
                }}
              >
                Show all {myNotifs.length}…
              </button>
            )}
            {showN && (
              <button
                onClick={() => {
                  clearN();
                  setShowN(false);
                }}
                style={{
                  marginTop: 6,
                  background: "rgba(255,255,255,0.25)",
                  border: "none",
                  borderRadius: 8,
                  color: C.w,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "4px 10px",
                  cursor: "pointer",
                  fontFamily: ff.b,
                }}
              >
                ✓ Mark all read
              </button>
            )}
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          overflowX: "auto",
          background: C.w,
          borderBottom: `2px solid ${C.brd}`,
        }}
      >
        {tabs.map((t) => {
          const badge =
            t.key === "pickups"
              ? pCount
              : t.key === "unclaimed"
                ? uCount
                : t.key === "approvals"
                  ? pendingApprovals.length
                  : t.key === "houses"
                    ? declineReqs.length
                    : 0;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                flex: "0 0 auto",
                padding: "10px 11px",
                background: tab === t.key ? C.gp : "transparent",
                border: "none",
                borderBottom:
                  tab === t.key ? `3px solid ${C.g}` : "3px solid transparent",
                fontFamily: ff.b,
                fontSize: 11,
                fontWeight: 600,
                color: tab === t.key ? C.gd : C.m,
                cursor: "pointer",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 3,
              }}
            >
              {t.label}
              {badge > 0 && (
                <span
                  style={{
                    background: C.r,
                    color: C.w,
                    borderRadius: 10,
                    padding: "1px 5px",
                    fontSize: 9,
                    fontWeight: 700,
                  }}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ padding: 14, maxWidth: 640, margin: "0 auto" }}>
        {tab === "pickups" && <PickupsTab data={data} user={user} up={up} />}
        {tab === "unclaimed" && (
          <UnclaimedTab data={data} user={user} up={up} />
        )}
        {tab === "approvals" && (
          <ApprovalsTab data={data} user={user} up={up} />
        )}
        {tab === "groups" && <GroupsTab data={data} user={user} up={up} />}
        {tab === "coordhome" && (
          <CoordinatorTab data={data} user={user} up={up} />
        )}
        {tab === "sponsorhome" && (
          <SponsorHomeTab data={data} user={user} up={up} />
        )}
        {tab === "sponsors" && <SponsorsTab data={data} user={user} up={up} />}
        {tab === "houses" && (
          <HousesTab data={data} user={user} up={up} onDemo={onDemo} />
        )}
        {tab === "bags" && <BagsTab data={data} user={user} up={up} />}
        {tab === "team" && <TeamTab data={data} user={user} up={up} />}
        {tab === "score" && <ScoreTab data={data} user={user} />}
        {tab === "map" && <MapTab data={data} user={user} />}
        {tab === "qr" && <QRTab data={data} user={user} />}
        {tab === "settings" && <SettingsTab data={data} user={user} up={up} />}
      </div>
    </div>
  );
}

// ━━━ PICKUPS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: Pickups -> see and manage pickups (request, assign, complete, cancel).
function PickupsTab({ data, user, up }) {
  const [showPass, setSP] = useState({});
  const hn = (hId) => {
    const h = data.houses.find((x) => x.id === hId);
    return h?.nickname || h?.name || "Unknown";
  };
  const kn = (kId) => data.users.find((u) => u.id === kId)?.firstName || "—";
  const myHouseIds = ["kid", "parent"].includes(user.role)
    ? user.houses || []
    : [];
  const myKidIds =
    user.role === "parent"
      ? data.users.filter((u) => u.parentId === user.id).map((u) => u.id)
      : [];
  const myKidHouseIds =
    user.role === "parent"
      ? data.users
          .filter((u) => u.parentId === user.id)
          .flatMap((u) => u.houses || [])
      : [];
  const canAct = (p) =>
    (user.role === "kid" &&
      (p.currentKidId === user.id ||
        (["waiting", "unallocated"].includes(p.status) &&
          myHouseIds.includes(p.houseId)))) ||
    // A parent can run a pickup end to end: one they claimed themselves,
    // one held by one of their kids, or an unassigned one at their own
    // house (spec v4.11: with no kid available, the pickup goes to the
    // Parent). All of these are pickups the rules let a parent update.
    (user.role === "parent" &&
      (p.currentKidId === user.id ||
        myKidIds.includes(p.currentKidId) ||
        (["waiting", "unallocated"].includes(p.status) &&
          myHouseIds.includes(p.houseId))));

  const relevant = data.pickups.filter((p) => {
    if (user.role === "kid")
      return (
        p.currentKidId === user.id ||
        p.pendingKidId === user.id ||
        myHouseIds.includes(p.houseId) ||
        p.history?.some((h) => h.kidId === user.id)
      );
    if (user.role === "parent")
      return (
        p.currentKidId === user.id ||
        p.pendingKidId === user.id ||
        myKidIds.includes(p.currentKidId) ||
        myKidIds.includes(p.pendingKidId) ||
        myHouseIds.includes(p.houseId) ||
        myKidHouseIds.includes(p.houseId) ||
        p.history?.some((h) => myKidIds.includes(h.kidId))
      );
    return false;
  });
  const parentApproval = relevant.filter((p) => p.status === "parent_approval");
  const waiting = relevant.filter((p) => p.status === "waiting");
  const accepted = relevant.filter((p) => p.status === "accepted");
  const done = relevant.filter((p) => p.status === "done");

  const applyTransitionResult = (result) => {
    if (!result) return;
    up((d) => ({
      ...d,
      pickups: d.pickups.map((x) =>
        x.id === result.pickupAfter.id ? result.pickupAfter : x,
      ),
      notifications: result.notifications.length
        ? [...d.notifications, ...result.notifications]
        : d.notifications,
      activityLog: result.activity
        ? [result.activity, ...(d.activityLog || [])].slice(0, 200)
        : d.activityLog,
    }));
  };

  const accept = async (pId) => {
    const p = data.pickups.find((x) => x.id === pId);
    if (!p) return;
    const actorId =
      user.role === "kid"
        ? user.id
        : p.currentKidId || (user.role === "parent" ? user.id : null);
    if (!actorId) return;
    const acceptedAt = now();
    const pickupAfter = {
      ...p,
      status: "accepted",
      currentKidId: actorId,
      acceptedAt,
      history: [
        ...(p.history || []),
        { action: "accepted", kidId: actorId, ts: acceptedAt },
      ],
    };
    const notifications = [];
    const kid = data.users.find((u) => u.id === actorId);
    if (kid?.parentId) {
      const par = data.users.find((u) => u.id === kid.parentId);
      const n = par
        ? makeNotification({
            userId: par.id,
            eventType: "pickup_accepted_parent",
            refType: "pickup",
            refId: p.id,
            subjectUserId: kid.id,
            groupId: p.groupId,
            actorId: user.id,
            ts: acceptedAt,
          })
        : null;
      if (n) notifications.push(n);
    }
    try {
      applyTransitionResult(
        await transitionPickupAtomic({
          data,
          pickupBefore: p,
          pickupAfter,
          notifications,
          activityType: "accepted",
          publicStatus: "on_the_way",
          transitionAt: acceptedAt,
        }),
      );
    } catch (error) {
      console.error("Pickup acceptance failed", error);
      toast("Couldn't accept this pickup just now. Please try again.");
    }
  };

  const pass = async (pId) => {
    const p = data.pickups.find((x) => x.id === pId);
    if (!p) return;
    const transitionAt = now();
    const tried = [
      ...(p.history || [])
        .filter((h) => h.action === "passed" || h.action === "sent_to")
        .map((h) => h.kidId),
      p.currentKidId,
    ];
    const next = data.users.find(
      (u) =>
        u.role === "kid" &&
        u.active &&
        u.houses?.includes(p.houseId) &&
        !tried.includes(u.id),
    );
    const history = [
      ...(p.history || []),
      { action: "passed", kidId: p.currentKidId, ts: transitionAt },
    ];
    const notifications = [];
    let pickupAfter;
    if (next) {
      history.push({ action: "sent_to", kidId: next.id, ts: transitionAt });
      pickupAfter = {
        ...p,
        status: "waiting",
        currentKidId: next.id,
        pendingKidId: null,
        assignedAt: transitionAt,
        acceptedAt: null,
        arrivedAt: null,
        approvalRequestedAt: null,
        history,
      };
      const kidNotif = makeNotification({
        userId: next.id,
        eventType: "pickup_passed",
        refType: "pickup",
        refId: p.id,
        subjectUserId: next.id,
        groupId: p.groupId,
        actorId: user.id,
        ts: transitionAt,
      });
      if (kidNotif) notifications.push(kidNotif);
      const par = data.users.find((u) => u.id === next.parentId);
      const parNotif = par
        ? makeNotification({
            userId: par.id,
            eventType: "pickup_passed_parent",
            refType: "pickup",
            refId: p.id,
            subjectUserId: next.id,
            groupId: p.groupId,
            actorId: user.id,
            ts: transitionAt,
          })
        : null;
      if (parNotif) notifications.push(parNotif);
    } else {
      pickupAfter = {
        ...p,
        status: "unallocated",
        currentKidId: null,
        pendingKidId: null,
        acceptedAt: null,
        arrivedAt: null,
        assignedAt: null,
        approvalRequestedAt: null,
        history,
      };
    }
    try {
      applyTransitionResult(
        await transitionPickupAtomic({
          data,
          pickupBefore: p,
          pickupAfter,
          notifications,
          publicStatus: "requested",
          transitionAt,
        }),
      );
      setSP((x) => ({ ...x, [pId]: undefined }));
    } catch (error) {
      console.error("Pickup pass failed", error);
      toast("Couldn't pass this pickup just now. Please try again.");
    }
  };

  const imHere = async (pId) => {
    const p = data.pickups.find((x) => x.id === pId);
    if (!p) return;
    const arrivedAt = now();
    const pickupAfter = {
      ...p,
      arrivedAt,
      history: [
        ...(p.history || []),
        { action: "arrived", kidId: p.currentKidId, ts: arrivedAt },
      ],
    };
    const notifications = [];
    const kid = data.users.find((u) => u.id === p.currentKidId);
    if (kid?.parentId) {
      const par = data.users.find((u) => u.id === kid.parentId);
      const n = par
        ? makeNotification({
            userId: par.id,
            eventType: "pickup_arrived_parent",
            refType: "pickup",
            refId: p.id,
            subjectUserId: kid.id,
            groupId: p.groupId,
            actorId: user.id,
            ts: arrivedAt,
          })
        : null;
      if (n) notifications.push(n);
    }
    try {
      applyTransitionResult(
        await transitionPickupAtomic({
          data,
          pickupBefore: p,
          pickupAfter,
          notifications,
          activityType: "arrived",
          publicStatus: "arrived",
          transitionAt: arrivedAt,
        }),
      );
    } catch (error) {
      console.error("Pickup arrival failed", error);
      toast("Couldn't mark arrival just now. Please try again.");
    }
  };

  const markDone = async (pId) => {
    const p = data.pickups.find((x) => x.id === pId);
    if (!p) return;
    const bag = data.bags.find((b) => b.id === p.bagId) || null;
    const completedAt = now();
    const kid = data.users.find((u) => u.id === p.currentKidId);
    const parent = kid?.parentId
      ? data.users.find((u) => u.id === kid.parentId)
      : null;
    const notification = parent
      ? makeNotification({
          userId: parent.id,
          eventType: "pickup_completed_parent",
          refType: "pickup",
          refId: p.id,
          subjectUserId: p.currentKidId,
          groupId: p.groupId,
          actorId: user.id,
          ts: completedAt,
        })
      : null;
    try {
      const result = await completePickupAtomic({
        pickup: p,
        bag,
        completedAt,
        notification,
      });
      if (!result) throw new Error("Pickup is not ready to complete.");
      up((d) => ({
        ...d,
        pickups: d.pickups.map((x) =>
          x.id === pId ? result.pickupAfter : x,
        ),
        bags: result.bagAfter
          ? d.bags.map((b) =>
              b.id === result.bagAfter.id ? result.bagAfter : b,
            )
          : d.bags,
        notifications: result.notification
          ? [...d.notifications, result.notification]
          : d.notifications,
        activityLog: [result.activity, ...(d.activityLog || [])].slice(0, 200),
      }));
    } catch (e) {
      console.error("Pickup completion failed", e);
      toast("Couldn't complete this pickup just now. Nothing was changed; please try again.");
    }
  };

  const approveGrab = async (pId) => {
    const p = data.pickups.find((x) => x.id === pId);
    if (!p?.pendingKidId) return;
    const decisionAt = now();
    const pickupAfter = {
      ...p,
      status: "accepted",
      currentKidId: p.pendingKidId,
      pendingKidId: null,
      acceptedAt: decisionAt,
      history: [
        ...(p.history || []),
        { action: "parent_approved", kidId: p.pendingKidId, ts: decisionAt },
      ],
    };
    const notification = makeNotification({
      userId: p.pendingKidId,
      eventType: "pickup_approved",
      refType: "pickup",
      refId: p.id,
      subjectUserId: p.pendingKidId,
      groupId: p.groupId,
      actorId: user.id,
      ts: decisionAt,
    });
    try {
      applyTransitionResult(
        await transitionPickupAtomic({
          data,
          pickupBefore: p,
          pickupAfter,
          notifications: notification ? [notification] : [],
          publicStatus: "on_the_way",
          transitionAt: decisionAt,
        }),
      );
    } catch (error) {
      console.error("Pickup approval failed", error);
      toast("Couldn't approve this pickup just now. Please try again.");
    }
  };

  const denyGrab = async (pId) => {
    const p = data.pickups.find((x) => x.id === pId);
    if (!p?.pendingKidId) return;
    const pendingKidId = p.pendingKidId;
    const decisionAt = now();
    const pickupAfter = {
      ...p,
      status: "unallocated",
      pendingKidId: null,
      history: [
        ...(p.history || []),
        { action: "parent_denied", kidId: pendingKidId, ts: decisionAt },
      ],
    };
    const notification = makeNotification({
      userId: pendingKidId,
      eventType: "pickup_declined",
      refType: "pickup",
      refId: p.id,
      subjectUserId: pendingKidId,
      groupId: p.groupId,
      actorId: user.id,
      ts: decisionAt,
    });
    try {
      applyTransitionResult(
        await transitionPickupAtomic({
          data,
          pickupBefore: p,
          pickupAfter,
          notifications: notification ? [notification] : [],
          publicStatus: "requested",
          transitionAt: decisionAt,
        }),
      );
    } catch (error) {
      console.error("Pickup denial failed", error);
      toast("Couldn't decline this pickup just now. Please try again.");
    }
  };

  const getMyNotes = (houseId) => {
    const me = data.users.find((u) => u.id === user.id);
    return me?.notes?.[houseId] || "";
  };

  return (
    <div>
      <SectionTitle>Pickup Requests</SectionTitle>
      {parentApproval.length === 0 &&
        waiting.length === 0 &&
        accepted.length === 0 &&
        done.length === 0 && <Empty icon="📭" text="No pickups yet!" />}
      {parentApproval.map((p) => (
        <Card key={p.id} accent={C.blue}>
          <Tag color={C.blue} bg={C.bp}>
            🔒 Parent Approval Needed
          </Tag>
          <p style={{ margin: "6px 0 2px", fontWeight: 700, fontSize: 15 }}>
            🏠 {hn(p.houseId)}
          </p>
          <p style={{ margin: 0, color: C.m, fontSize: 12 }}>
            {kn(p.pendingKidId)} wants to grab this
          </p>
          {((user.role === "parent" &&
            data.users.some(
              (u) => u.id === p.pendingKidId && u.parentId === user.id,
            )) ||
            user.role === "admin") && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button
                onClick={() => approveGrab(p.id)}
                style={{ ...btnG, padding: "8px 14px", fontSize: 13 }}
              >
                ✅ Approve
              </button>
              <button onClick={() => denyGrab(p.id)} style={btnD}>
                ✕ Deny
              </button>
            </div>
          )}
        </Card>
      ))}
      {waiting.map((p) => (
        <Card key={p.id} accent={C.o}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <div>
              <Tag color={C.o} bg={C.op}>
                ⏳ Waiting
              </Tag>
              <p style={{ margin: "6px 0 2px", fontWeight: 700, fontSize: 15 }}>
                🏠 {hn(p.houseId)}
              </p>
              <p style={{ margin: 0, color: C.m, fontSize: 12 }}>
                Requested: {fmt(p.requestedAt)}
              </p>
              {p.currentKidId && (
                <p style={{ margin: "2px 0", fontSize: 12, color: C.g }}>
                  Assigned: {kn(p.currentKidId)}
                </p>
              )}
            </div>
            {canAct(p) && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  minWidth: 120,
                }}
              >
                <button
                  onClick={() => accept(p.id)}
                  style={{ ...btnG, padding: "8px 14px", fontSize: 13 }}
                >
                  ✅ Accept
                </button>
                {showPass[p.id] === undefined ? (
                  <button
                    onClick={() => setSP((x) => ({ ...x, [p.id]: true }))}
                    style={{
                      background: C.op,
                      color: C.o,
                      border: `1px solid ${C.o}`,
                      borderRadius: 10,
                      padding: "8px 14px",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: ff.b,
                    }}
                  >
                    ↪ Pass
                  </button>
                ) : (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <button
                      onClick={() => pass(p.id)}
                      style={{
                        background: C.rp,
                        color: C.r,
                        border: "none",
                        borderRadius: 8,
                        padding: "5px 10px",
                        fontSize: 11,
                        cursor: "pointer",
                        fontFamily: ff.b,
                        fontWeight: 600,
                      }}
                    >
                      Yes, pass it on
                    </button>
                    <button
                      onClick={() =>
                        setSP((x) => ({ ...x, [p.id]: undefined }))
                      }
                      style={{
                        background: C.gp,
                        color: C.g,
                        border: "none",
                        borderRadius: 8,
                        padding: "5px 10px",
                        fontSize: 11,
                        cursor: "pointer",
                        fontFamily: ff.b,
                        fontWeight: 600,
                      }}
                    >
                      Keep it
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {p.history?.filter((h) => h.action === "passed").length > 0 && (
            <div
              style={{
                marginTop: 8,
                padding: "6px 10px",
                background: "#fafafa",
                borderRadius: 8,
              }}
            >
              {p.history
                .filter((h) => h.action === "passed")
                .map((h, i) => (
                  <p
                    key={i}
                    style={{ margin: "2px 0", fontSize: 11, color: C.m }}
                  >
                    ↪ {kn(h.kidId)} passed
                  </p>
                ))}
            </div>
          )}
        </Card>
      ))}
      {accepted.map((p) => {
        const myNotes = getMyNotes(p.houseId);
        return (
          <Card key={p.id} accent={C.g}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div>
                <Tag color={C.g} bg={C.gp}>
                  🚲 Accepted
                </Tag>
                <p
                  style={{ margin: "6px 0 2px", fontWeight: 700, fontSize: 15 }}
                >
                  🏠 {hn(p.houseId)}
                </p>
                <p style={{ margin: 0, color: C.m, fontSize: 12 }}>
                  Collector: {kn(p.currentKidId)}
                </p>
                {p.arrivedAt && (
                  <p style={{ margin: "2px 0", fontSize: 12, color: C.g }}>
                    📍 Arrived: {fmt(p.arrivedAt)}
                  </p>
                )}
              </div>
              {canAct(p) && (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  {!p.arrivedAt &&
                    (data.users.find((u) => u.id === p.currentKidId) || {})
                      .imHereEnabled && (
                      <button
                        onClick={() => imHere(p.id)}
                        style={{ ...btnB, padding: "8px 14px", fontSize: 13 }}
                      >
                        📍 I'm Here
                      </button>
                    )}
                  <button
                    onClick={() => markDone(p.id)}
                    style={{ ...btnG, padding: "8px 14px", fontSize: 13 }}
                  >
                    ✅ Collected
                  </button>
                  <button
                    onClick={() => pass(p.id)}
                    style={{
                      background: C.op,
                      color: C.o,
                      border: "none",
                      borderRadius: 10,
                      padding: "6px 14px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: ff.b,
                    }}
                  >
                    ↪ I can't do this one
                  </button>
                </div>
              )}
            </div>
            {canAct(p) && myNotes && (
              <div
                style={{
                  marginTop: 8,
                  padding: "8px 10px",
                  background: "#f0f7ee",
                  borderRadius: 8,
                  border: `1px solid ${C.gg}`,
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: 11,
                    color: C.g,
                    fontWeight: 700,
                  }}
                >
                  📝 Your notes:
                </p>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.tx }}>
                  {myNotes}
                </p>
              </div>
            )}
          </Card>
        );
      })}
      {done.length > 0 && (
        <div>
          <h3
            style={{
              fontFamily: ff.h,
              fontSize: 16,
              color: C.m,
              margin: "20px 0 8px",
            }}
          >
            Completed ({done.length})
          </h3>
          {done.slice(0, 10).map((p) => (
            <Card key={p.id} muted>
              <p style={{ margin: 0, fontSize: 12, color: C.m }}>
                ✅ {hn(p.houseId)} — {kn(p.doneBy)} · {fmt(p.doneAt)}
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ━━━ UNCLAIMED ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: Unclaimed -> pickups no kid has claimed yet, so they can be picked up.
function UnclaimedTab({ data, user, up }) {
  const unall = data.pickups.filter((p) => p.status === "unallocated");
  const hn = (hId) => {
    const h = data.houses.find((x) => x.id === hId);
    return h?.nickname || h?.name || "Unknown";
  };
  const grab = async (pId) => {
    // Only a Kid or Parent may nominate to collect a pickup. Coordinators relay
    // and time out requests but do not become collectors in the current UI.
    if (!["kid", "parent"].includes(user.role)) return;
    const p = data.pickups.find((x) => x.id === pId);
    if (!p || p.status !== "unallocated") return;
    const transitionAt = now();
    const notifications = [];
    let pickupAfter;
    let publicStatus = "requested";
    if (user.role === "parent") {
      pickupAfter = {
        ...p,
        status: "accepted",
        currentKidId: user.id,
        acceptedAt: transitionAt,
        history: [
          ...(p.history || []),
          { action: "grabbed", kidId: user.id, ts: transitionAt },
        ],
      };
      publicStatus = "on_the_way";
    } else {
      pickupAfter = {
        ...p,
        status: "parent_approval",
        pendingKidId: user.id,
        approvalRequestedAt: transitionAt,
        history: [
          ...(p.history || []),
          { action: "grab_requested", kidId: user.id, ts: transitionAt },
        ],
      };
      const par = data.users.find((u) => u.id === user.parentId);
      const notification = par
        ? makeNotification({
            userId: par.id,
            eventType: "pickup_approval_requested",
            refType: "pickup",
            refId: p.id,
            subjectUserId: user.id,
            groupId: p.groupId,
            actorId: user.id,
            ts: transitionAt,
          })
        : null;
      if (notification) notifications.push(notification);
    }
    try {
      const result = await transitionPickupAtomic({
        data,
        pickupBefore: p,
        pickupAfter,
        notifications,
        publicStatus,
        transitionAt,
      });
      if (!result) return;
      up((d) => ({
        ...d,
        pickups: d.pickups.map((x) =>
          x.id === pickupAfter.id ? pickupAfter : x,
        ),
        notifications: notifications.length
          ? [...d.notifications, ...notifications]
          : d.notifications,
      }));
    } catch (error) {
      console.error("Pickup nomination failed", error);
      toast("Couldn't claim this pickup just now. Please try again.");
    }
  };
  return (
    <div>
      <SectionTitle>Unclaimed Pickups</SectionTitle>
      <p style={{ color: C.m, fontSize: 13, margin: "0 0 12px" }}>
        {user.role === "kid"
          ? "Your parent will need to approve."
          : "Grab one if you're free."}
      </p>
      {unall.length === 0 && <Empty icon="🎉" text="All pickups covered!" />}
      {unall.map((p) => (
        <Card key={p.id} accent={C.o}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                🏠 {hn(p.houseId)}
              </p>
              <p style={{ margin: "2px 0", color: C.m, fontSize: 12 }}>
                Requested: {fmt(p.requestedAt)}
              </p>
            </div>
            {(user.role === "kid" ||
              user.role === "admin" ||
              user.role === "parent") && (
              <button
                onClick={() => grab(p.id)}
                style={{ ...btnG, padding: "8px 14px", fontSize: 13 }}
              >
                🙋 I'll do it!
              </button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ━━━ COORDINATOR HOME ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: My Group (coordinator) -> the parents in the coordinator's group(s),
// and a form to add new parents/guardians (each gets a secure email login).
function CoordinatorTab({ data, user, up }) {
  const myGroupIds = user.groupIds || [];
  const myGroups = (data.groups || []).filter((g) => myGroupIds.includes(g.id));
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({
    firstName: "",
    lastName: "",
    email: "",
    yearOfBirth: "",
    groupId: myGroupIds[0] || "",
  });
  // Coordinators create their own groups/schools (self-serve). A new group is
  // added to data.groups AND assigned to this coordinator's own groupIds.
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", suburb: "" });
  const createGroup = async () => {
    if (!newGroup.name.trim()) {
      toast("Please enter a name for the group/school.");
      return;
    }
    const group = {
      id: uid(),
      createdBy: user.id,
      name: newGroup.name.trim(),
      suburb: newGroup.suburb.trim() || "Melbourne",
      isDefault: false,
      createdAt: now(),
    };
    try {
      const result = await createGroupAtomic(group, user);
      if (!result) return;
      up((d) => ({
        ...d,
        groups: [...(d.groups || []), result.group],
        users: d.users.map((u) =>
          u.id === user.id ? result.actorAfter : u,
        ),
      }));
      setNewGroup({ name: "", suburb: "" });
      setShowGroupForm(false);
    } catch (error) {
      console.error("Coordinator Group creation failed", error);
      toast("Couldn't create this group just now. Please try again.");
    }
  };

  const parentsInMyGroups = (data.coordinatorParents || []).filter(
    (u) => u.role === "parent" && myGroupIds.includes(u.groupId),
  );

  const groupName = (gId) =>
    (data.groups || []).find((g) => g.id === gId)?.name || "—";

  const addParent = async () => {
    if (!f.firstName.trim() || !f.email.trim() || !f.yearOfBirth) {
      toast("Please fill in first name, email and year of birth.");
      return;
    }
    if (!f.email.includes("@")) {
      toast("Please enter a valid email address.");
      return;
    }
    if (!validYob(parseInt(f.yearOfBirth)) || ageFrom(parseInt(f.yearOfBirth)) < 18) {
      toast("A Parent / Guardian must have a valid year of birth and be 18+.");
      return;
    }
    if (!f.groupId) {
      toast("Please choose which group this parent/guardian belongs to.");
      return;
    }
    const email = f.email.trim().toLowerCase();
    if (
      [...(data.users || []), ...(data.coordinatorParents || [])].some(
        (u) =>
          (u.email || "").toLowerCase() === email ||
          (u.username || "").toLowerCase() === email,
      )
    ) {
      toast("An account with that email already exists.");
      return;
    }
    let result;
    try {
      result = await provisionAccountRemote({
        role: "parent",
        email,
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        yearOfBirth: parseInt(f.yearOfBirth),
        groupId: f.groupId,
        emoji: DEFAULT_EMOJI,
      });
    } catch (e) {
      toast(accountProvisionErrMsg(e));
      return;
    }
    const newProjection = result.projection;
    if (!newProjection) {
      toast("The Parent account was created, but its Coordinator projection is unavailable. Reload before continuing.");
      return;
    }
    // Coordinators never receive the full Parent record. The callable returns
    // only the same minimum-data projection the Coordinator can read normally.
    up((d) => ({
      ...d,
      coordinatorParents: [
        ...(d.coordinatorParents || []).filter((x) => x.id !== newProjection.id),
        newProjection,
      ],
    }));
    toast(
      result.invitationSent
        ? `Parent account created. Firebase sent a password-setup email to ${email}.`
        : `Parent account created, but the password-setup email could not be sent. Use Firebase Authentication to resend it to ${email}.`,
    );
    setF({
      firstName: "",
      lastName: "",
      email: "",
      yearOfBirth: "",
      groupId: myGroupIds[0] || "",
    });
    setAdding(false);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <SectionTitle>My Groups</SectionTitle>
        {!showGroupForm && (
          <button
            onClick={() => setShowGroupForm(true)}
            style={{ ...btnG, padding: "8px 14px", fontSize: 13 }}
          >
            ➕ New group
          </button>
        )}
      </div>

      {showGroupForm && (
        <Card accent={C.blue}>
          <p style={{ margin: "0 0 8px", fontWeight: 700, fontSize: 14 }}>
            Create a group
          </p>
          <input
            placeholder="Group name (school, club, troop&hellip;)"
            value={newGroup.name}
            onChange={(e) =>
              setNewGroup({ ...newGroup, name: e.target.value })
            }
            style={inp}
          />
          <input
            placeholder="Suburb (optional)"
            value={newGroup.suburb}
            onChange={(e) =>
              setNewGroup({ ...newGroup, suburb: e.target.value })
            }
            style={inp}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button onClick={createGroup} style={btnG}>
              Create
            </button>
            <button
              onClick={() => {
                setShowGroupForm(false);
                setNewGroup({ name: "", suburb: "" });
              }}
              style={{ ...btnG, background: "#9e9e9e", boxShadow: "none" }}
            >
              Cancel
            </button>
          </div>
        </Card>
      )}

      {myGroups.length === 0 && !showGroupForm && (
        <Empty
          icon="🏫"
          text="You don't run any groups yet. Tap “New group” to create your first school, club, or community group."
        />
      )}

      {myGroups.map((g) => (
        <Card key={g.id} accent={C.blue}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>
            🏫 {g.name}
          </p>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: C.m }}>
            📍 {g.suburb}
          </p>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: C.g }}>
            {parentsInMyGroups.filter((p) => p.groupId === g.id).length}{" "}
            parent(s) ·{" "}
            {parentsInMyGroups
              .filter((p) => p.groupId === g.id)
              .reduce((n, p) => n + (Number(p.kidCount) || 0), 0)}{" "}
            kid(s)
          </p>
          {g.stats && (
            <p style={{ margin: "3px 0 0", fontSize: 10, color: C.m }}>
              {g.stats.reportingParents || 0}/{g.stats.parents || 0} families reporting
              {g.stats.verified === true ? " · Backend verified" : " · Provisional"}
            </p>
          )}
        </Card>
      ))}

      {myGroups.length > 0 && (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "18px 0 8px",
            }}
          >
            <SectionTitle>Parents / Guardians</SectionTitle>
            {!adding && (
              <button
                onClick={() => {
                  setAdding(true);
                  setF({
                    firstName: "",
                    lastName: "",
                    email: "",
                    yearOfBirth: "",
                    groupId: myGroupIds[0] || "",
                  });
                }}
                style={{ ...btnG, padding: "8px 16px", fontSize: 13 }}
              >
                + Add Parent
              </button>
            )}
          </div>

          {adding && (
            <Card accent={C.g}>
              <p
                style={{
                  margin: "0 0 4px",
                  fontWeight: 700,
                  fontSize: 14,
                  color: C.g,
                }}
              >
                Add a Parent / Guardian
              </p>
              <p style={{ margin: "0 0 10px", fontSize: 12, color: C.m }}>
                They'll be added to your group and receive a Firebase email to
                choose their own password. You never create or see their password.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <input
                  placeholder="First name"
                  value={f.firstName}
                  onChange={(e) => setF({ ...f, firstName: e.target.value })}
                  style={inp}
                />
                <input
                  placeholder="Last name (optional)"
                  value={f.lastName}
                  onChange={(e) => setF({ ...f, lastName: e.target.value })}
                  style={inp}
                />
                <input
                  placeholder="Email (this is their login)"
                  type="email"
                  value={f.email}
                  onChange={(e) => setF({ ...f, email: e.target.value })}
                  style={inp}
                />
                <input
                  placeholder="Year of birth (Parent / Guardian must be 18+)"
                  type="number"
                  value={f.yearOfBirth}
                  onChange={(e) => setF({ ...f, yearOfBirth: e.target.value })}
                  style={inp}
                />
                {myGroups.length > 1 && (
                  <select
                    value={f.groupId}
                    onChange={(e) => setF({ ...f, groupId: e.target.value })}
                    style={inp}
                  >
                    {myGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={addParent} style={btnG}>
                    Create &amp; Send Invite
                  </button>
                  <button
                    onClick={() => setAdding(false)}
                    style={{
                      background: "#eee",
                      border: "none",
                      borderRadius: 10,
                      padding: "10px 20px",
                      cursor: "pointer",
                      fontFamily: ff.b,
                      fontSize: 14,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Card>
          )}

          {parentsInMyGroups.length === 0 && !adding && (
            <Empty icon="👪" text="No parents in your group yet." />
          )}

          {parentsInMyGroups.map((p) => (
            <Card key={p.id}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>
                    {getEmoji(p)} {p.firstName} {p.lastName || ""}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: C.m }}>
                    @{p.username}
                    {p.email ? ` · ${p.email}` : ""}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: C.blue }}>
                    {groupName(p.groupId)}
                  </p>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: C.g }}>
                    {p.kidCount || 0} kid(s)
                  </p>
                </div>
                {!p.active && (
                  <Tag color={C.r} bg={C.rp}>
                    INACTIVE
                  </Tag>
                )}
              </div>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

// ━━━ SPONSORS (management: admin + coordinator) ━━━━━━━━━━━━━━━━━━━━━━
// TAB: Sponsors -> admins/coordinators create and manage sponsor accounts and
// choose which groups each sponsor supports.
function SponsorsTab({ data, user, up }) {
  const isAdmin = user.role === "admin";
  // Groups this user can allocate a sponsor to.
  const myGroups = isAdmin
    ? data.groups || []
    : (data.groups || []).filter((g) => (user.groupIds || []).includes(g.id));
  const myGroupIds = new Set(myGroups.map((g) => g.id));

  // Sponsors this user can see: admin sees all; coordinator sees sponsors that
  // support at least one of their groups.
  const sponsors = data.users.filter((u) => {
    if (u.role !== "sponsor") return false;
    if (isAdmin) return true;
    return (u.groupIds || []).some((gid) => myGroupIds.has(gid));
  });

  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({
    name: "",
    email: "",
    groupIds: [],
  });

  const toggleGroup = (gid) =>
    setF((prev) => ({
      ...prev,
      groupIds: prev.groupIds.includes(gid)
        ? prev.groupIds.filter((x) => x !== gid)
        : [...prev.groupIds, gid],
    }));

  const addSponsor = async () => {
    if (!f.name.trim() || !f.email.trim()) {
      toast("Please fill in company name and email.");
      return;
    }
    if (!f.email.includes("@")) {
      toast("Please enter a valid email address.");
      return;
    }
    if (f.groupIds.length === 0) {
      toast("Please choose at least one group this sponsor supports.");
      return;
    }
    const email = f.email.trim().toLowerCase();
    if (
      data.users.some(
        (u) =>
          (u.email || "").toLowerCase() === email ||
          (u.username || "").toLowerCase() === email,
      )
    ) {
      toast("An account with that email already exists.");
      return;
    }
    let result;
    try {
      result = await provisionAccountRemote({
        role: "sponsor",
        name: f.name.trim(),
        firstName: f.name.trim(),
        email,
        groupIds: f.groupIds,
        emoji: "💚",
      });
    } catch (e) {
      toast(accountProvisionErrMsg(e));
      return;
    }
    const newSponsor = result.user;
    up((d) => ({ ...d, users: [...d.users, newSponsor] }));
    toast(
      result.invitationSent
        ? `Sponsor account created. Firebase sent a password-setup email to ${email}.`
        : `Sponsor account created, but the password-setup email could not be sent. Use Firebase Authentication to resend it to ${email}.`,
    );
    setF({ name: "", email: "", groupIds: [] });
    setAdding(false);
  };

  // Deleting a sponsor ACCOUNT is admin-only. Coordinators (and admins)
  // unlink individual groups via the chips on the card instead — a sponsor
  // may support groups run by other coordinators.
  const removeSponsor = async (s) => {
    if (
      !window.confirm(
        `Deactivate sponsor "${s.name || s.firstName}"? Their KRC access and Firebase login will be disabled; their immutable identity and reporting history remain reserved.`,
      )
    )
      return;
    try {
      const result = await deactivateAccountRemote(s.id);
      up((d) => ({
        ...d,
        users: d.users.map((u) => (u.id === s.id ? { ...u, active: false } : u)),
      }));
      toast(
        result?.authDisabled === false
          ? "KRC access is inactive, but Firebase Auth still needs an operator retry."
          : "Sponsor account and Firebase login deactivated.",
      );
    } catch (e) {
      toast(accountLifecycleErrMsg(e, "deactivate"));
    }
  };
  const unlinkSponsorGroup = (s, gid) => {
    if (
      !window.confirm(
        `Unlink "${s.name || s.firstName}" from ${groupName(gid)}? Their account and other groups are not affected.`,
      )
    )
      return;
    up((d) => ({
      ...d,
      users: d.users.map((u) =>
        u.id === s.id
          ? { ...u, groupIds: (u.groupIds || []).filter((g) => g !== gid) }
          : u,
      ),
    }));
  };

  const groupName = (gid) =>
    (data.groups || []).find((g) => g.id === gid)?.name || "—";

  return (
    <div>
      <SectionTitle>Sponsors</SectionTitle>
      <Card muted>
        <p style={{ margin: 0, fontSize: 13, color: C.m }}>
          Sponsors support a group and can log in to see privacy-safe summary
          reports (bag and pickup numbers only). They never see kids' names,
          addresses, or any personal details. How a sponsor helps — bags,
          awards, prizes — is arranged outside the app between you and them.
        </p>
      </Card>

      {myGroups.length === 0 ? (
        <Empty
          icon="🏫"
          text="You need at least one group before adding a sponsor."
        />
      ) : !adding ? (
        <button
          onClick={() => {
            setAdding(true);
            setF({
              name: "",
              email: "",
              groupIds: [],
            });
          }}
          style={{ ...btnG, marginBottom: 12 }}
        >
          + Add Sponsor
        </button>
      ) : (
        <Card accent={C.g}>
          <p
            style={{
              margin: "0 0 10px",
              fontWeight: 700,
              fontSize: 14,
              color: C.g,
            }}
          >
            Add a Sponsor
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              placeholder="Company / sponsor name"
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              style={inp}
            />
            <input
              placeholder="Email (this is their login)"
              type="email"
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
              style={inp}
            />
            <div>
              <p
                style={{
                  margin: "4px 0 6px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.blue,
                }}
              >
                Which groups does this sponsor support?
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {myGroups.map((g) => (
                  <label
                    key={g.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={f.groupIds.includes(g.id)}
                      onChange={() => toggleGroup(g.id)}
                    />
                    🏫 {g.name}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={addSponsor} style={btnG}>
                Create &amp; Send Invite
              </button>
              <button
                onClick={() => setAdding(false)}
                style={{
                  background: "#eee",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 20px",
                  cursor: "pointer",
                  fontFamily: ff.b,
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Card>
      )}

      {sponsors.length === 0 && !adding && (
        <Empty icon="💚" text="No sponsors yet." />
      )}

      {sponsors.map((s) => (
        <Card key={s.id} accent={C.g}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                💚 {s.name || s.firstName}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: C.m }}>
                @{s.username}
                {s.email ? ` · ${s.email}` : ""}
              </p>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: C.blue }}>
                Supports:{" "}
                {(s.groupIds || []).length === 0 && "no groups yet"}
              </p>
              {(s.groupIds || []).length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    marginTop: 4,
                  }}
                >
                  {(s.groupIds || []).map((gid) => {
                    const canUnlink =
                      isAdmin || (user.groupIds || []).includes(gid);
                    return (
                      <span
                        key={gid}
                        style={{
                          background: C.gp,
                          color: C.g,
                          borderRadius: 8,
                          padding: "2px 8px",
                          fontSize: 11,
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {groupName(gid)}
                        {canUnlink && (
                          <button
                            onClick={() => unlinkSponsorGroup(s, gid)}
                            title="Unlink from this group"
                            style={{
                              background: "none",
                              border: "none",
                              color: C.r,
                              cursor: "pointer",
                              fontSize: 11,
                              padding: 0,
                              fontWeight: 700,
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            {isAdmin && (
            <button
              onClick={() => removeSponsor(s)}
              style={{
                background: C.rp,
                border: "none",
                borderRadius: 8,
                padding: "5px 10px",
                cursor: "pointer",
                fontSize: 11,
                color: C.r,
              }}
            >
              Remove
            </button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ━━━ SPONSOR HOME (the sponsor's own dashboard) ━━━━━━━━━━━━━━━━━━━━━━━
// TAB: My Sponsorship (sponsor) -> a sponsor's own dashboard. Privacy-safe impact
// totals only (bags, pickups, kids helped) - never names or addresses.
function SponsorHomeTab({ data, user }) {
  const myGroupIds = user.groupIds || [];
  const myGroups = (data.groups || []).filter((g) => myGroupIds.includes(g.id));

  // Totals include only Groups with a published Coordinator snapshot. Missing
  // data is unknown, not zero; treating it as zero would understate impact.
  const publishedGroups = myGroups
    .map((g) => ({ g, stats: groupStats(data, g.id, true) }))
    .filter((x) => x.stats);
  const totals = publishedGroups.reduce(
    (acc, { stats: s }) => {
      acc.bags += s.bags || 0;
      acc.activeBags += s.activeBags || 0;
      acc.pickups += s.pickups || 0;
      acc.donePickups += s.donePickups || 0;
      acc.kids += s.kids || 0;
      return acc;
    },
    { bags: 0, activeBags: 0, pickups: 0, donePickups: 0, kids: 0 },
  );
  const missingSnapshotCount = myGroups.length - publishedGroups.length;

  const Stat = ({ label, value, color }) => (
    <div
      style={{
        flex: "1 1 90px",
        background: "#fff",
        border: `1px solid ${C.brd}`,
        borderRadius: 12,
        padding: "12px 10px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 24, fontWeight: 800, color: color || C.g }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: C.m, marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div>
      <SectionTitle>Thank You for Supporting KRC 💚</SectionTitle>

      {myGroups.length === 0 ? (
        <Empty
          icon="💚"
          text="You're set up as a sponsor. Once a coordinator links you to a group, your impact will show here."
        />
      ) : (
        <>
          <Card accent={C.g}>
            <p
              style={{
                margin: "0 0 10px",
                fontWeight: 700,
                fontSize: 14,
                color: C.g,
              }}
            >
              Provisional impact counts (all groups)
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <Stat label="Bags in use" value={totals.activeBags} />
              <Stat label="Total bags" value={totals.bags} color={C.blue} />
              <Stat
                label="Pickups done"
                value={totals.donePickups}
                color={C.o}
              />
              <Stat label="Kids helped" value={totals.kids} color={C.purple} />
            </div>
            <p style={{ margin: "10px 0 0", fontSize: 11, color: C.m }}>
              These privacy-safe counts are published from family devices and
              are provisional until trusted backend aggregation is deployed.
              Sponsors never see names, addresses, or personal details.
            </p>
            {missingSnapshotCount > 0 && (
              <p style={{ margin: "8px 0 0", fontSize: 11, color: C.o }}>
                Statistics are not yet published for {missingSnapshotCount}{" "}
                supported Group{missingSnapshotCount === 1 ? "" : "s"}; those
                Groups are excluded from the totals above rather than counted as zero.
              </p>
            )}
          </Card>

          <p
            style={{
              margin: "18px 0 8px",
              fontWeight: 700,
              fontSize: 14,
              color: C.g,
            }}
          >
            Groups you support
          </p>
          {myGroups.map((g) => {
            const s = groupStats(data, g.id, true);
            return (
              <Card key={g.id} accent={C.blue}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                  🏫 {g.name}
                </p>
                <p style={{ margin: "2px 0 8px", fontSize: 12, color: C.m }}>
                  📍 {g.suburb}
                </p>
                {!s ? (
                  <p style={{ margin: 0, fontSize: 12, color: C.o }}>
                    Impact statistics will appear after this Group's Coordinator
                    publishes the first privacy-safe snapshot.
                  </p>
                ) : (
                  <>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <Stat label="Bags in use" value={s.activeBags} />
                      <Stat label="Total bags" value={s.bags} color={C.blue} />
                      <Stat
                        label="Pickups done"
                        value={s.donePickups}
                        color={C.o}
                      />
                      <Stat label="Kids" value={s.kids} color={C.purple} />
                    </div>
                    {s.updatedAt && (
                      <p style={{ margin: "7px 0 0", fontSize: 10, color: C.m }}>
                        Updated {fmt(s.updatedAt)}
                        {s.verified === true ? " · Backend verified" : " · Provisional"}
                      </p>
                    )}
                  </>
                )}
              </Card>
            );
          })}

        </>
      )}
    </div>
  );
}

// ━━━ APPROVALS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: Groups (admin) -> create/edit groups and add the coordinators who run them.
function GroupsTab({ data, user, up }) {
  const [name, setName] = useState("");
  const [suburb, setSuburb] = useState("Melbourne");
  const [editId, setEditId] = useState("");
  const [ef, setEF] = useState({ name: "", suburb: "" });
  const [coordFor, setCoordFor] = useState(""); // group id whose add-coordinator form is open
  const [cf, setCF] = useState({
    firstName: "",
    email: "",
    yearOfBirth: "",
    emoji: DEFAULT_EMOJI,
  });
  const groups = data.groups || [];

  const coordsIn = (gId) =>
    data.users.filter(
      (u) => u.role === "coordinator" && (u.groupIds || []).includes(gId),
    );

  const addCoordinator = async (gId) => {
    if (!cf.email.trim() || !cf.firstName.trim() || !cf.yearOfBirth) {
      toast("Please fill in first name, email and year of birth.");
      return;
    }
    if (!cf.email.includes("@")) {
      toast("Please enter a valid email address.");
      return;
    }
    if (!validYob(parseInt(cf.yearOfBirth)) || ageFrom(parseInt(cf.yearOfBirth)) < 18) {
      toast("A Coordinator must have a valid year of birth and be 18+.");
      return;
    }
    const email = cf.email.trim().toLowerCase();
    if (
      data.users.some(
        (u) =>
          (u.email || "").toLowerCase() === email ||
          (u.username || "").toLowerCase() === email,
      )
    ) {
      toast("An account with that email already exists.");
      return;
    }
    let result;
    try {
      result = await provisionAccountRemote({
        role: "coordinator",
        email,
        firstName: cf.firstName.trim(),
        yearOfBirth: parseInt(cf.yearOfBirth),
        groupIds: gId ? [gId] : [],
        emoji: cf.emoji,
      });
    } catch (e) {
      toast(accountProvisionErrMsg(e));
      return;
    }
    const newCoord = result.user;
    up((d) => ({ ...d, users: [...d.users, newCoord] }));
    toast(
      result.invitationSent
        ? `Coordinator account created. Firebase sent a password-setup email to ${email}.`
        : `Coordinator account created, but the password-setup email could not be sent. Use Firebase Authentication to resend it to ${email}.`,
    );
    setCF({
      firstName: "",
      email: "",
      yearOfBirth: "",
      emoji: DEFAULT_EMOJI,
    });
    setCoordFor("");
  };

  const removeCoordinator = async (id, gId) => {
    const c = data.users.find((u) => u.id === id);
    if (!c) return;
    const gids = c.groupIds || [];
    // Removing a coordinator FROM A GROUP must not delete their account if
    // they run other groups — it just unlinks this one group.
    if (gId && gids.length > 1) {
      if (
        !window.confirm(
          `Remove ${c.firstName} from this group? They stay coordinator of their other group(s).`,
        )
      )
        return;
      try {
        const result = await unlinkCoordinatorGroupRemote(id, gId);
        up((d) => ({
          ...d,
          users: d.users.map((u) =>
            u.id === id ? { ...u, groupIds: result.groupIds } : u,
          ),
        }));
        toast("Coordinator removed from this Group. Their other Group access is unchanged.");
      } catch (e) {
        toast(accountLifecycleErrMsg(e, "update"));
      }
      return;
    }
    if (
      !window.confirm(
        `Deactivate coordinator ${c.firstName} (@${c.username})? This is their only Group, so their KRC access and Firebase login will be disabled.`,
      )
    )
      return;
    try {
      const result = await deactivateAccountRemote(id);
      up((d) => ({
        ...d,
        users: d.users.map((u) => (u.id === id ? { ...u, active: false } : u)),
      }));
      toast(
        result?.authDisabled === false
          ? "KRC access is inactive, but Firebase Auth still needs an operator retry."
          : "Coordinator account and Firebase login deactivated.",
      );
    } catch (e) {
      toast(accountLifecycleErrMsg(e, "deactivate"));
    }
  };

  const addGroup = async () => {
    if (!name.trim()) return;
    const group = {
      id: uid(),
      createdBy: user.id,
      name: name.trim(),
      suburb: suburb.trim() || "Melbourne",
      isDefault: false,
      createdAt: now(),
    };
    try {
      const result = await createGroupAtomic(group, user);
      if (!result) return;
      up((d) => ({ ...d, groups: [...(d.groups || []), result.group] }));
      setName("");
      setSuburb("Melbourne");
    } catch (error) {
      console.error("Platform Group creation failed", error);
      toast("Couldn't create this group just now. Please try again.");
    }
  };

  const saveEdit = () => {
    if (!editId) return;
    up((d) => ({
      ...d,
      groups: (d.groups || []).map((g) =>
        g.id === editId
          ? {
              ...g,
              name: ef.name.trim() || g.name,
              suburb: ef.suburb.trim() || g.suburb,
            }
          : g,
      ),
    }));
    setEditId("");
  };

  const removeGroup = (g) => {
    if (g.isDefault) return;
    toast(
      `"${g.name}" was not deleted. Group deletion is disabled until a trusted backend can verify that no hidden family, House, Bag, Pickup or request records still reference it.`,
    );
  };

  const countFor = (gId, role) => {
    const g = (data.groups || []).find((x) => x.id === gId);
    if (role === "parent" && Number.isInteger(g?.stats?.parents))
      return g.stats.parents;
    if (role === "kid" && Number.isInteger(g?.stats?.kids)) return g.stats.kids;
    return data.users.filter(
      (u) =>
        (u.groupId === gId || (u.groupIds || []).includes(gId)) &&
        u.role === role,
    ).length;
  };

  return (
    <div>
      <SectionTitle>Groups</SectionTitle>
      <Card>
        <p
          style={{
            margin: "0 0 10px",
            fontWeight: 700,
            fontSize: 14,
            color: C.g,
          }}
        >
          Add a Group
        </p>
        <p style={{ margin: "0 0 10px", fontSize: 12, color: C.m }}>
          A Group is a school, sporting club, or community centre. Each Group
          has its own Coordinator(s) who look after the parents in that group.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            placeholder="Group name (e.g. St Mary's Primary)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inp}
          />
          <input
            placeholder="Suburb (optional)"
            value={suburb}
            onChange={(e) => setSuburb(e.target.value)}
            style={inp}
          />
          <button onClick={addGroup} style={btnG}>
            Add Group
          </button>
        </div>
      </Card>

      <Card>
        <p
          style={{
            margin: "0 0 10px",
            fontWeight: 700,
            fontSize: 14,
            color: C.g,
          }}
        >
          Add a Coordinator
        </p>
        <p style={{ margin: "0 0 10px", fontSize: 12, color: C.m }}>
          A Coordinator runs one or more groups. Create the coordinator here —
          they create and manage their own groups/schools after they log in. You
          don't need to set up a group for them first.
        </p>
        {coordFor === "__new__" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              placeholder="First name"
              value={cf.firstName}
              onChange={(e) => setCF({ ...cf, firstName: e.target.value })}
              style={inp}
            />
            <input
              placeholder="Email"
              value={cf.email}
              onChange={(e) => setCF({ ...cf, email: e.target.value })}
              style={inp}
            />
            <input
              placeholder="Year of birth (Coordinator must be 18+)"
              type="number"
              value={cf.yearOfBirth}
              onChange={(e) => setCF({ ...cf, yearOfBirth: e.target.value })}
              style={inp}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => addCoordinator("")} style={btnG}>
                Create &amp; Send Invite
              </button>
              <button
                onClick={() => setCoordFor("")}
                style={{ ...btnG, background: "#9e9e9e", boxShadow: "none" }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => {
              setCF({
                firstName: "",
                email: "",
                yearOfBirth: "",
                emoji: DEFAULT_EMOJI,
              });
              setCoordFor("__new__");
            }}
            style={btnG}
          >
            ➕ Add a coordinator
          </button>
        )}
      </Card>

      {groups.map((g) => (
        <Card key={g.id} accent={g.isDefault ? C.gold : C.blue}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                🏫 {g.name}{" "}
                {g.isDefault && (
                  <Tag color={C.gold} bg="#fff8e1">
                    DEFAULT
                  </Tag>
                )}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: C.m }}>
                📍 {g.suburb}
              </p>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: C.g }}>
                {countFor(g.id, "coordinator")} coordinator(s) ·{" "}
                {countFor(g.id, "parent")} parent(s) · {countFor(g.id, "kid")}{" "}
                kid(s)
              </p>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                onClick={() => {
                  setEditId(g.id);
                  setEF({ name: g.name, suburb: g.suburb });
                }}
                title="Edit"
                style={{
                  background: C.bp,
                  border: "none",
                  borderRadius: 8,
                  padding: "5px 8px",
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                ✏️
              </button>
              {!g.isDefault && (
                <button
                  onClick={() => removeGroup(g)}
                  title="Deletion disabled pending backend reference check"
                  style={{
                    background: C.rp,
                    border: "none",
                    borderRadius: 8,
                    padding: "5px 8px",
                    cursor: "pointer",
                    fontSize: 12,
                    color: C.r,
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          {editId === g.id && (
            <div
              style={{
                marginTop: 10,
                padding: 12,
                background: "#fafafa",
                borderRadius: 10,
              }}
            >
              <p
                style={{
                  margin: "0 0 8px",
                  fontWeight: 700,
                  fontSize: 13,
                  color: C.blue,
                }}
              >
                Edit Group
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <input
                  placeholder="Group name"
                  value={ef.name}
                  onChange={(e) => setEF({ ...ef, name: e.target.value })}
                  style={{ ...inp, fontSize: 12, padding: "8px 10px" }}
                />
                <input
                  placeholder="Suburb (optional)"
                  value={ef.suburb}
                  onChange={(e) => setEF({ ...ef, suburb: e.target.value })}
                  style={{ ...inp, fontSize: 12, padding: "8px 10px" }}
                />
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={saveEdit}
                    style={{ ...btnG, padding: "6px 14px", fontSize: 12 }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditId("")}
                    style={{
                      background: "#eee",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 14px",
                      cursor: "pointer",
                      fontFamily: ff.b,
                      fontSize: 12,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          <div
            style={{
              marginTop: 10,
              borderTop: `1px solid ${C.brd}`,
              paddingTop: 10,
            }}
          >
            <p
              style={{
                margin: "0 0 6px",
                fontWeight: 700,
                fontSize: 12,
                color: C.blue,
              }}
            >
              Coordinators
            </p>
            {coordsIn(g.id).length === 0 && (
              <p style={{ margin: "0 0 6px", fontSize: 12, color: C.m }}>
                No coordinator yet.
              </p>
            )}
            {coordsIn(g.id).map((c) => (
              <div
                key={c.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 0",
                }}
              >
                <span style={{ fontSize: 13 }}>
                  {getEmoji(c)} {c.firstName}{" "}
                  <span style={{ color: C.m, fontSize: 11 }}>
                    @{c.username}
                  </span>
                </span>
                <button
                  onClick={() => removeCoordinator(c.id, g.id)}
                  style={{
                    background: C.rp,
                    border: "none",
                    borderRadius: 8,
                    padding: "3px 8px",
                    cursor: "pointer",
                    fontSize: 11,
                    color: C.r,
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            {coordFor === g.id ? (
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  background: "#f5f8ff",
                  borderRadius: 10,
                }}
              >
                <p
                  style={{
                    margin: "0 0 8px",
                    fontWeight: 700,
                    fontSize: 13,
                    color: C.blue,
                  }}
                >
                  Add Coordinator to {g.name}
                </p>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  <input
                    placeholder="First name"
                    value={cf.firstName}
                    onChange={(e) =>
                      setCF({ ...cf, firstName: e.target.value })
                    }
                    style={{ ...inp, fontSize: 12, padding: "8px 10px" }}
                  />
                  <input
                    placeholder="Email (this is their login)"
                    type="email"
                    value={cf.email}
                    onChange={(e) => setCF({ ...cf, email: e.target.value })}
                    style={{ ...inp, fontSize: 12, padding: "8px 10px" }}
                  />
                  <input
                    placeholder="Year of birth (Coordinator must be 18+)"
                    type="number"
                    value={cf.yearOfBirth}
                    onChange={(e) => setCF({ ...cf, yearOfBirth: e.target.value })}
                    style={{ ...inp, fontSize: 12, padding: "8px 10px" }}
                  />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => addCoordinator(g.id)}
                      style={{ ...btnB, padding: "6px 14px", fontSize: 12 }}
                    >
                      Create &amp; Send Invite
                    </button>
                    <button
                      onClick={() => setCoordFor("")}
                      style={{
                        background: "#eee",
                        border: "none",
                        borderRadius: 8,
                        padding: "6px 14px",
                        cursor: "pointer",
                        fontFamily: ff.b,
                        fontSize: 12,
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setCoordFor(g.id);
                  setCF({
                    firstName: "",
                    email: "",
                    yearOfBirth: "",
                    emoji: DEFAULT_EMOJI,
                  });
                }}
                style={{
                  background: C.bp,
                  border: `1px solid ${C.blue}`,
                  borderRadius: 8,
                  padding: "5px 12px",
                  cursor: "pointer",
                  fontSize: 12,
                  fontFamily: ff.b,
                  fontWeight: 600,
                  color: C.blue,
                  marginTop: 4,
                }}
              >
                + Add Coordinator
              </button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// TAB: Approvals -> review and approve or decline pending sign-ups.
// Admin/Main see everyone pending (including pending coordinators). A
// Coordinator sees only pending parents/sponsors in the groups they run, and
// (per the grant chain) can't approve another coordinator.
function ApprovalsTab({ data, user, up }) {
  // Show exactly the pending users this person may grant: admins/main see all;
  // a coordinator sees pending parents/sponsors in groups they run. Using the
  // shared helper means it handles both single groupId and groupIds[].
  const pending = [
    ...(data.users || []),
    ...(data.coordinatorParents || []),
  ].filter((u) => u.status === "pending" && canGrantAccessTo(user, u));
  const approve = async (id) => {
    const u =
      data.users.find((x) => x.id === id) ||
      (data.coordinatorParents || []).find((x) => x.id === id);
    if (!u) return;
    // Grant access (writes the approved user doc + access entry atomically)
    // BEFORE updating local state, so a failed grant doesn't leave someone
    // shown as approved but unable to log in.
    const ok =
      user.role === "coordinator" && u.role === "parent"
        ? await approveCoordinatorParentAtomic(u)
        : await grantAccessAtomic({ ...u, status: "approved" }, u.role);
    if (!ok) {
      toast(
        "Approved — but login access couldn't be granted just now. It will retry automatically; if they still can't log in, try again or contact an admin.",
      );
      return;
    }
    up((d) => ({
      ...d,
      users: d.users.map((x) =>
        x.id === id ? { ...x, status: "approved" } : x,
      ),
      coordinatorParents: (d.coordinatorParents || []).map((x) =>
        x.id === id ? { ...x, status: "approved", active: true, updatedAt: now() } : x,
      ),
    }));
  };
  const deny = async (id) => {
    const u =
      data.users.find((x) => x.id === id) ||
      (data.coordinatorParents || []).find((x) => x.id === id);
    if (!u) return;
    const ok =
      user.role === "coordinator" && u.role === "parent"
        ? await denyPendingCoordinatorParentAtomic(u)
        : true;
    if (!ok) {
      toast("Couldn't deny this account just now. Please try again.");
      return;
    }
    up((d) => ({
      ...d,
      users: d.users.filter((x) => x.id !== id),
      coordinatorParents: (d.coordinatorParents || []).filter((x) => x.id !== id),
    }));
  };
  return (
    <div>
      <SectionTitle>Account Approvals</SectionTitle>
      {pending.length === 0 && <Empty icon="✅" text="No pending approvals." />}
      {pending.map((u) => (
        <Card key={u.id} accent={C.o}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                {getEmoji(u)} {u.firstName} {u.lastName}
              </p>
              <p style={{ margin: "2px 0", color: C.m, fontSize: 12 }}>
                @{u.username}
                {u.role !== "parent" && u.yearOfBirth
                  ? ` · Age ${ageFrom(u.yearOfBirth)}`
                  : ""}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => approve(u.id)}
                style={{ ...btnG, padding: "8px 14px", fontSize: 13 }}
              >
                ✅ Approve
              </button>
              <button onClick={() => deny(u.id)} style={btnD}>
                ✕ Deny
              </button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ━━━ HOUSES ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: Houses -> manage households: add houses, edit details, set map locations,
// and link kids/parents to them.
function HousesTab({ data, user, up, onDemo }) {
  const [f, sF] = useState({ name: "", nickname: "", address: "", email: "" });
  const [locHouse, setLocHouse] = useState(null);
  const [editHouse, setEditHouse] = useState(null);
  const [ef, sEF] = useState({ name: "", nickname: "", address: "" });
  const isOwner = user.role === "admin";
  const isParent = user.role === "parent";
  const isKid = user.role === "kid";
  const myKids = data.users.filter(
    (u) => u.role === "kid" && u.parentId === user.id,
  );
  const myKidIds = myKids.map((k) => k.id);

  const addH = () => {
    if (!f.name.trim()) return;
    const hId = uid();
    up((d) => ({
      ...d,
      houses: [
        ...d.houses,
        {
          id: hId,
          name: f.name.trim(),
          // Leave the nickname EMPTY if not given — the public bag-scan page
          // then shows the generic "Recycler". The old fallback (use the full
          // name) silently published the household's real name on the public
          // QR page.
          nickname: f.nickname.trim(),
          address: f.address.trim(),
          email: f.email.trim(),
          mapLat: null,
          mapLng: null,
          mapLabel: "",
          mapUpdatedAt: null,
          cancelLevel: null,
          ageRestriction: "Standard",
          // Stage 4 scope: who owns this house (drives per-role read rules)
          parentId: user.role === "parent" ? user.id : null,
          groupId: user.groupId || null,
          addedAt: now(),
        },
      ],
      // The creating parent's user doc lists their family's houses — the
      // security rules check this list for house/pickup access.
      users:
        user.role === "parent"
          ? d.users.map((u) =>
              u.id === user.id
                ? { ...u, houses: [...new Set([...(u.houses || []), hId])] }
                : u,
            )
          : d.users,
    }));
    sF({ name: "", nickname: "", address: "", email: "" });
  };
  const toggleAssign = (hId, kId) =>
    up((d) => {
      const kid = d.users.find((u) => u.id === kId);
      const adding = kid && !(kid.houses || []).includes(hId);
      return {
        ...d,
        users: d.users.map((u) => {
          if (u.id === kId) {
            const h = u.houses || [];
            return h.includes(hId)
              ? { ...u, houses: h.filter((x) => x !== hId) }
              : { ...u, houses: [...h, hId] };
          }
          // Mirror onto the kid's parent so the parent's per-role access
          // includes this house (rules read the parent doc's houses list).
          if (adding && kid && u.id === kid.parentId)
            return { ...u, houses: [...new Set([...(u.houses || []), hId])] };
          return u;
        }),
      };
    });
  const updateHouse = () => {
    if (!editHouse) return;
    up((d) => ({
      ...d,
      houses: d.houses.map((h) =>
        h.id === editHouse.id
          ? {
              ...h,
              name: ef.name.trim() || h.name,
              nickname: ef.nickname.trim() || h.nickname,
              address: ef.address.trim() || h.address,
            }
          : h,
      ),
    }));
    setEditHouse(null);
  };
  const setAgeBracket = (hId, bracket) =>
    up((d) => ({
      ...d,
      houses: d.houses.map((h) =>
        h.id === hId ? { ...h, ageRestriction: bracket } : h,
      ),
    }));
  const saveMapLocation = (hId, loc) =>
    up((d) => ({
      ...d,
      houses: d.houses.map((h) =>
        h.id === hId
          ? {
              ...h,
              mapLat: loc.lat,
              mapLng: loc.lng,
              mapLabel: loc.label || "Selected by parent on map",
              mapUpdatedAt: now(),
            }
          : h,
      ),
    }));
  const clearMapLocation = (hId) =>
    up((d) => ({
      ...d,
      houses: d.houses.map((h) =>
        h.id === hId
          ? {
              ...h,
              mapLat: null,
              mapLng: null,
              mapLabel: "",
              mapUpdatedAt: null,
            }
          : h,
      ),
    }));

  // Parent cancel actions
  const cancelLevel1 = (hId, kId) => {
    up((d) => {
      const parent = d.users.find((u) => u.id === user.id);
      const dc = (parent?.declineCount || 0) + 1;
      let nd = {
        ...d,
        users: d.users.map((u) => {
          if (u.id === kId)
            return { ...u, houses: (u.houses || []).filter((h) => h !== hId) };
          if (u.id === user.id) return { ...u, declineCount: dc };
          return u;
        }),
      };
      return nd;
    });
  };
  const cancelLevel2 = (hId, kId, bracket) => {
    up((d) => {
      const parent = d.users.find((u) => u.id === user.id);
      const dc = (parent?.declineCount || 0) + 1;
      let nd = {
        ...d,
        houses: d.houses.map((h) =>
          h.id === hId ? { ...h, ageRestriction: bracket } : h,
        ),
        users: d.users.map((u) => {
          if (u.id === kId)
            return { ...u, houses: (u.houses || []).filter((h) => h !== hId) };
          if (u.id === user.id) return { ...u, declineCount: dc };
          return u;
        }),
      };
      return nd;
    });
  };
  const cancelLevel3 = (hId) => {
    up((d) => {
      const parent = d.users.find((u) => u.id === user.id);
      const dc = (parent?.declineCount || 0) + 1;
      let nd = {
        ...d,
        houses: d.houses.map((h) =>
          h.id === hId ? { ...h, cancelLevel: "permanent" } : h,
        ),
        users: d.users.map((u) => {
          if (u.houses)
            return {
              ...u,
              houses: u.houses.filter((h) => h !== hId),
              declineCount: u.id === user.id ? dc : u.declineCount,
            };
          return u;
        }),
      };
      return nd;
    });
  };

  // Kid decline request
  const requestDecline = async (houseId) => {
    const requestedAt = now();
    const decline = {
      id: uid(),
      kidId: user.id,
      parentId: user.parentId || null,
      groupId: user.groupId || null,
      houseId,
      ts: requestedAt,
      status: "pending",
    };
    const parent = data.users.find((u) => u.id === user.parentId);
    const notification = parent
      ? makeNotification({
          userId: parent.id,
          eventType: "decline_requested",
          refType: "decline",
          refId: decline.id,
          subjectUserId: user.id,
          groupId: decline.groupId,
          actorId: user.id,
          ts: requestedAt,
        })
      : null;
    try {
      const result = await requestDeclineAtomic(decline, notification);
      if (!result) return;
      up((d) => ({
        ...d,
        declineRequests: [...d.declineRequests, decline],
        notifications: notification
          ? [...d.notifications, notification]
          : d.notifications,
      }));
    } catch (error) {
      console.error("Decline request failed", error);
      toast("Couldn't send this request just now. Please try again.");
    }
  };

  // Parent review decline
  const reviewDecline = async (drId, action, ageBracket) => {
    const decline = data.declineRequests.find((x) => x.id === drId);
    if (!decline) return;
    try {
      const nextData = await reviewDeclineAtomic({
        data,
        decline,
        action,
        ageBracket,
        parentId: user.id,
      });
      if (nextData) up(() => nextData);
    } catch (error) {
      console.error("Decline review failed", error);
      toast("Couldn't save this review just now. Please try again.");
    }
  };

  const pendingDeclines = data.declineRequests.filter(
    (dr) =>
      dr.status === "pending" &&
      (isParent ? myKidIds.includes(dr.kidId) : isOwner),
  );

  const visH = isOwner
    ? data.houses
    : isKid
      ? data.houses.filter(
          (h) => user.houses?.includes(h.id) && h.cancelLevel !== "permanent",
        )
      : data.houses.filter((h) => h.cancelLevel !== "permanent");
  const lastPickup = (hId) => {
    const ps = data.pickups
      .filter((p) => p.houseId === hId && p.status === "done")
      .sort((a, b) => new Date(b.doneAt) - new Date(a.doneAt));
    return ps[0]?.doneAt;
  };

  return (
    <div>
      <SectionTitle>Houses</SectionTitle>
      {locHouse && (
        <HouseLocationPicker
          house={locHouse}
          onClose={() => setLocHouse(null)}
          onSave={(loc) => {
            saveMapLocation(locHouse.id, loc);
            setLocHouse(null);
          }}
          onClear={() => {
            clearMapLocation(locHouse.id);
            setLocHouse(null);
          }}
        />
      )}

      {/* Pending decline requests for parent */}
      {pendingDeclines.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h3
            style={{
              fontFamily: ff.h,
              fontSize: 16,
              color: C.purple,
              margin: "0 0 8px",
            }}
          >
            🔔 Decline Requests
          </h3>
          {pendingDeclines.map((dr) => {
            const kid = data.users.find((u) => u.id === dr.kidId);
            const house = data.houses.find((h) => h.id === dr.houseId);
            return (
              <Card key={dr.id} accent={C.purple}>
                <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 14 }}>
                  {getEmoji(kid)} {kid?.firstName} wants to stop collecting from
                  🏠 {house?.nickname || house?.name}
                </p>
                <p style={{ margin: "0 0 10px", color: C.m, fontSize: 12 }}>
                  Talk to {kid?.firstName} privately about why, then choose an
                  action:
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <button
                    onClick={() => reviewDecline(dr.id, "unassign")}
                    style={{
                      background: "#f5f5f5",
                      border: `1px solid ${C.brd}`,
                      borderRadius: 8,
                      padding: "6px 10px",
                      fontSize: 11,
                      cursor: "pointer",
                      fontFamily: ff.b,
                      fontWeight: 600,
                      color: C.m,
                    }}
                  >
                    Unassign only
                  </button>
                  <button
                    onClick={() => reviewDecline(dr.id, "age_restrict", "12+")}
                    style={{
                      background: C.op,
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 10px",
                      fontSize: 11,
                      cursor: "pointer",
                      fontFamily: ff.b,
                      fontWeight: 600,
                      color: C.o,
                    }}
                  >
                    Flag 12+ only
                  </button>
                  <button
                    onClick={() => reviewDecline(dr.id, "age_restrict", "14+")}
                    style={{
                      background: C.op,
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 10px",
                      fontSize: 11,
                      cursor: "pointer",
                      fontFamily: ff.b,
                      fontWeight: 600,
                      color: C.o,
                    }}
                  >
                    Flag 14+ only
                  </button>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          "Permanently remove this house? No one can use it again.",
                        )
                      )
                        reviewDecline(dr.id, "permanent");
                    }}
                    style={{
                      background: C.rp,
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 10px",
                      fontSize: 11,
                      cursor: "pointer",
                      fontFamily: ff.b,
                      fontWeight: 600,
                      color: C.r,
                    }}
                  >
                    ⛔ Permanent cancel
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {(isOwner || isParent) && (
        <Card>
          <p
            style={{
              margin: "0 0 10px",
              fontWeight: 700,
              fontSize: 14,
              color: C.g,
            }}
          >
            Add a House
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              placeholder="Full name (e.g. Johnson Family)"
              value={f.name}
              onChange={(e) => sF({ ...f, name: e.target.value })}
              style={inp}
            />
            <input
              placeholder="Nickname neighbours will see (optional — shows &quot;Recycler&quot; if blank)"
              value={f.nickname}
              onChange={(e) => sF({ ...f, nickname: e.target.value })}
              style={inp}
            />
            <input
              placeholder="Address (optional)"
              value={f.address}
              onChange={(e) => sF({ ...f, address: e.target.value })}
              style={inp}
            />
            <input
              placeholder="Email for notifications (optional)"
              value={f.email}
              onChange={(e) => sF({ ...f, email: e.target.value })}
              style={inp}
            />
            <button onClick={addH} style={btnG}>
              + Add House
            </button>
          </div>
        </Card>
      )}
      {visH.length === 0 && (
        <Empty
          icon="🏘️"
          text={isKid ? "No houses assigned to you yet." : "No houses yet."}
        />
      )}
      {visH.map((h) => {
        const assigned = data.users.filter(
          (u) => u.role === "kid" && u.houses?.includes(h.id),
        );
        const lp = lastPickup(h.id);
        const inactive = lp && daysBetween(lp, now()) > 150;
        const bags = data.bags.filter(
          (b) => b.houseId === h.id && b.status === "at_house",
        );
        const hasPendingDecline = data.declineRequests.some(
          (dr) =>
            dr.kidId === user.id &&
            dr.houseId === h.id &&
            dr.status === "pending",
        );
        return (
          <Card
            key={h.id}
            accent={h.cancelLevel === "permanent" ? C.r : undefined}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                  🏠 {h.nickname || h.name}{" "}
                  {h.cancelLevel === "permanent" && (
                    <Tag color={C.r} bg={C.rp}>
                      CANCELLED
                    </Tag>
                  )}{" "}
                  {h.ageRestriction !== "Standard" && (
                    <Tag color={C.o} bg={C.op}>
                      {h.ageRestriction}
                    </Tag>
                  )}
                </p>
                <p style={{ margin: "2px 0", color: C.m, fontSize: 12 }}>
                  {h.address}
                </p>
                {hasHouseLocation(h) && (
                  <p style={{ margin: "2px 0", fontSize: 12, color: C.blue }}>
                    📍 Map location set
                  </p>
                )}
                {(isOwner || isParent) && h.cancelLevel !== "permanent" && (
                  <div
                    style={{
                      marginTop: 6,
                      display: "flex",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      onClick={() => setLocHouse(h)}
                      style={{
                        background: C.bp,
                        border: `1px solid ${C.blue}`,
                        borderRadius: 8,
                        padding: "4px 10px",
                        fontSize: 11,
                        cursor: "pointer",
                        fontFamily: ff.b,
                        fontWeight: 700,
                        color: C.blue,
                      }}
                    >
                      {hasHouseLocation(h)
                        ? "📍 Change map location"
                        : "📍 Find on map"}
                    </button>
                    {hasHouseLocation(h) && (
                      <button
                        onClick={() => clearMapLocation(h.id)}
                        style={{
                          background: "#f5f5f5",
                          border: `1px solid ${C.brd}`,
                          borderRadius: 8,
                          padding: "4px 10px",
                          fontSize: 11,
                          cursor: "pointer",
                          fontFamily: ff.b,
                          fontWeight: 700,
                          color: C.m,
                        }}
                      >
                        Clear map pin
                      </button>
                    )}
                  </div>
                )}
                {inactive && (
                  <p style={{ margin: "2px 0", fontSize: 12, color: C.o }}>
                    🕐 No activity in 5+ months
                  </p>
                )}
                {assigned.length > 0 && !isKid && (
                  <p style={{ margin: "2px 0", fontSize: 12, color: C.g }}>
                    Assigned: {assigned.map((k) => k.firstName).join(", ")}
                  </p>
                )}
                {bags.length > 0 && (
                  <p style={{ margin: "2px 0", fontSize: 12, color: C.m }}>
                    🎒 {bags.length} bag{bags.length > 1 ? "s" : ""}
                  </p>
                )}

                {/* Kid: decline request button */}
                {isKid && h.cancelLevel !== "permanent" && (
                  <div style={{ marginTop: 6 }}>
                    {hasPendingDecline ? (
                      <Tag color={C.purple} bg={C.pp}>
                        Decline requested — waiting for parent
                      </Tag>
                    ) : (
                      <button
                        onClick={() => requestDecline(h.id)}
                        style={{
                          background: C.pp,
                          border: `1px solid ${C.purple}`,
                          borderRadius: 8,
                          padding: "4px 10px",
                          fontSize: 11,
                          cursor: "pointer",
                          fontFamily: ff.b,
                          fontWeight: 600,
                          color: C.purple,
                        }}
                      >
                        Request to stop collecting here
                      </button>
                    )}
                  </div>
                )}

                {/* Parent: assign kids + cancel options */}
                {isParent &&
                  h.cancelLevel !== "permanent" &&
                  myKids.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 4,
                        marginTop: 6,
                      }}
                    >
                      {myKids.map((k) => {
                        const on = k.houses?.includes(h.id);
                        return (
                          <button
                            key={k.id}
                            onClick={() => toggleAssign(h.id, k.id)}
                            style={{
                              background: on ? C.gp : "#f5f5f5",
                              border: `1.5px solid ${on ? C.g : C.brd}`,
                              borderRadius: 8,
                              padding: "3px 8px",
                              fontSize: 11,
                              cursor: "pointer",
                              fontFamily: ff.b,
                              fontWeight: 600,
                              color: on ? C.gd : C.m,
                            }}
                          >
                            {on ? "✅" : "+"} {k.firstName}
                          </button>
                        );
                      })}
                    </div>
                  )}
                {isParent &&
                  h.cancelLevel !== "permanent" &&
                  myKids.filter((k) => k.houses?.includes(h.id)).length > 0 && (
                    <div
                      style={{
                        marginTop: 6,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 4,
                      }}
                    >
                      {myKids
                        .filter((k) => k.houses?.includes(h.id))
                        .map((k) => (
                          <button
                            key={k.id}
                            onClick={() => cancelLevel1(h.id, k.id)}
                            style={{
                              background: "#f5f5f5",
                              border: `1px solid ${C.brd}`,
                              borderRadius: 6,
                              padding: "3px 6px",
                              fontSize: 10,
                              cursor: "pointer",
                              fontFamily: ff.b,
                              color: C.m,
                            }}
                          >
                            Unassign {k.firstName}
                          </button>
                        ))}
                      <button
                        onClick={() =>
                          cancelLevel2(
                            h.id,
                            myKids.find((k) => k.houses?.includes(h.id))?.id,
                            "12+",
                          )
                        }
                        style={{
                          background: C.op,
                          border: "none",
                          borderRadius: 6,
                          padding: "3px 6px",
                          fontSize: 10,
                          cursor: "pointer",
                          fontFamily: ff.b,
                          color: C.o,
                        }}
                      >
                        12+ only
                      </button>
                      <button
                        onClick={() =>
                          cancelLevel2(
                            h.id,
                            myKids.find((k) => k.houses?.includes(h.id))?.id,
                            "14+",
                          )
                        }
                        style={{
                          background: C.op,
                          border: "none",
                          borderRadius: 6,
                          padding: "3px 6px",
                          fontSize: 10,
                          cursor: "pointer",
                          fontFamily: ff.b,
                          color: C.o,
                        }}
                      >
                        14+ only
                      </button>
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              "Permanently cancel? " +
                                REMOVAL_MSG.slice(0, 60) +
                                "...",
                            )
                          )
                            cancelLevel3(h.id);
                        }}
                        style={{
                          background: C.rp,
                          border: "none",
                          borderRadius: 6,
                          padding: "3px 6px",
                          fontSize: 10,
                          cursor: "pointer",
                          fontFamily: ff.b,
                          color: C.r,
                        }}
                      >
                        ⛔ Cancel
                      </button>
                    </div>
                  )}

                {/* Owner: age bracket */}
                {isOwner && h.cancelLevel !== "permanent" && (
                  <div
                    style={{
                      marginTop: 6,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: 11, color: C.m }}>Age:</span>
                    {AGE_BRACKETS.map((b) => (
                      <button
                        key={b}
                        onClick={() => setAgeBracket(h.id, b)}
                        style={{
                          background: h.ageRestriction === b ? C.gp : "#f5f5f5",
                          border: `1px solid ${h.ageRestriction === b ? C.g : C.brd}`,
                          borderRadius: 6,
                          padding: "2px 8px",
                          fontSize: 10,
                          cursor: "pointer",
                          fontFamily: ff.b,
                          fontWeight: 600,
                          color: h.ageRestriction === b ? C.gd : C.m,
                        }}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {isOwner && (
                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    onClick={() => onDemo(h)}
                    title="Demo"
                    style={{
                      background: C.gp,
                      border: "none",
                      borderRadius: 8,
                      padding: "5px 8px",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    👁️
                  </button>
                  <button
                    onClick={() => {
                      setEditHouse(h);
                      sEF({
                        name: h.name,
                        nickname: h.nickname,
                        address: h.address,
                      });
                    }}
                    title="Edit"
                    style={{
                      background: C.bp,
                      border: "none",
                      borderRadius: 8,
                      padding: "5px 8px",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() =>
                      toast(
                        "House hard deletion is disabled until Bags, QR links and retained history can be retired safely. Use Permanent cancel for now.",
                      )
                    }
                    title="House deletion requires a trusted archive workflow"
                    style={{
                      background: "#f3f4f6",
                      border: "none",
                      borderRadius: 8,
                      padding: "5px 8px",
                      cursor: "pointer",
                      fontSize: 12,
                      color: C.m,
                    }}
                  >
                    🔒
                  </button>
                </div>
              )}
              {isParent && !isOwner && (
                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    onClick={() => {
                      setEditHouse(h);
                      sEF({
                        name: h.name,
                        nickname: h.nickname,
                        address: h.address,
                      });
                    }}
                    title="Edit"
                    style={{
                      background: C.bp,
                      border: "none",
                      borderRadius: 8,
                      padding: "5px 8px",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    ✏️
                  </button>
                </div>
              )}
            </div>
            {editHouse && editHouse.id === h.id && (
              <div
                style={{
                  marginTop: 10,
                  padding: 12,
                  background: "#fafafa",
                  borderRadius: 10,
                }}
              >
                <p
                  style={{
                    margin: "0 0 8px",
                    fontWeight: 700,
                    fontSize: 13,
                    color: C.blue,
                  }}
                >
                  Edit House
                </p>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  <input
                    placeholder="Name"
                    value={ef.name}
                    onChange={(e) => sEF({ ...ef, name: e.target.value })}
                    style={{ ...inp, fontSize: 12, padding: "8px 10px" }}
                  />
                  <input
                    placeholder="Nickname (optional)"
                    value={ef.nickname}
                    onChange={(e) => sEF({ ...ef, nickname: e.target.value })}
                    style={{ ...inp, fontSize: 12, padding: "8px 10px" }}
                  />
                  <input
                    placeholder="Address (optional)"
                    value={ef.address}
                    onChange={(e) => sEF({ ...ef, address: e.target.value })}
                    style={{ ...inp, fontSize: 12, padding: "8px 10px" }}
                  />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={updateHouse}
                      style={{ ...btnG, padding: "6px 14px", fontSize: 12 }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditHouse(null)}
                      style={{
                        background: "#eee",
                        border: "none",
                        borderRadius: 8,
                        padding: "6px 14px",
                        cursor: "pointer",
                        fontFamily: ff.b,
                        fontSize: 12,
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
function applyDeclineReview(d, dr, action, ageBracket, parentId) {
  let next = {
    ...d,
    declineRequests: d.declineRequests.map((x) =>
      x.id === dr.id ? { ...x, status: "reviewed" } : x,
    ),
  };
  if (action === "unassign")
    return cancelLevel1_fn(next, dr.houseId, dr.kidId, parentId);
  if (action === "age_restrict") {
    const parent = next.users.find((u) => u.id === parentId);
    const declineCount = (parent?.declineCount || 0) + 1;
    return {
      ...next,
      houses: next.houses.map((h) =>
        h.id === dr.houseId
          ? { ...h, ageRestriction: ageBracket || "12+" }
          : h,
      ),
      users: next.users.map((u) => {
        if (u.id === dr.kidId)
          return {
            ...u,
            houses: (u.houses || []).filter((h) => h !== dr.houseId),
          };
        if (u.id === parentId) return { ...u, declineCount };
        return u;
      }),
    };
  }
  if (action === "permanent") {
    const parent = next.users.find((u) => u.id === parentId);
    const declineCount = (parent?.declineCount || 0) + 1;
    return {
      ...next,
      houses: next.houses.map((h) =>
        h.id === dr.houseId ? { ...h, cancelLevel: "permanent" } : h,
      ),
      users: next.users.map((u) =>
        u.houses
          ? {
              ...u,
              houses: u.houses.filter((h) => h !== dr.houseId),
              declineCount: u.id === parentId ? declineCount : u.declineCount,
            }
          : u,
      ),
    };
  }
  return next;
}

function cancelLevel1_fn(d, hId, kId, parentId) {
  const par = d.users.find((u) => u.id === parentId);
  const dc = (par?.declineCount || 0) + 1;
  return {
    ...d,
    users: d.users.map((u) => {
      if (u.id === kId)
        return { ...u, houses: (u.houses || []).filter((h) => h !== hId) };
      if (u.id === parentId) return { ...u, declineCount: dc };
      return u;
    }),
  };
}

// ━━━ BAGS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: Bags -> manage the collection bags: create them, assign to houses, track
// status, and view/print their QR codes.
function BagsTab({ data, user, up }) {
  const isOwner = user.role === "admin";
  const [homeHouse, setHomeHouse] = useState("");
  const [adding, sA] = useState(false);
  const [qty, sQ] = useState(1);
  const [size, sS] = useState("Medium");
  const [type, sT] = useState("Standard");
  const addBags = () => {
    const nb = [];
    const ex = data.bags.length;
    for (let i = 0; i < qty; i++)
      nb.push({
        id: secureToken(), // public QR token -> crypto-strength, not uid()
        bagNumber: `BAG-${String(ex + i + 1).padStart(3, "0")}`,
        size,
        type,
        status: "in_stock",
        parentId: null,
        kidId: null,
        houseId: null,
        pickupCount: 0,
        addedAt: now(),
        lastScanDate: null,
      });
    up((d) => ({ ...d, bags: [...d.bags, ...nb] }));
    sA(false);
    sQ(1);
  };
  const allocateParent = (bId, pId) =>
    up((d) => ({
      ...d,
      bags: d.bags.map((b) =>
        b.id === bId ? { ...b, status: "allocated_parent", parentId: pId } : b,
      ),
    }));
  const allocateKid = (bId, kId) =>
    up((d) => ({
      ...d,
      bags: d.bags.map((b) =>
        b.id === bId ? { ...b, status: "allocated_kid", kidId: kId } : b,
      ),
    }));
  // Bag transitions that touch the PUBLIC QR doc are done atomically via
  // setBagAtomic (parent sessions have no mirror sync; revocation must not
  // be best-effort). The local state update follows the committed write and
  // echoes to a zero diff.
  const applyBagPatch = (bId, patch) =>
    up((d) => ({
      ...d,
      bags: d.bags.map((b) => (b.id === bId ? { ...b, ...patch } : b)),
    }));
  const allocateHouse = async (bId, hId) => {
    const b = data.bags.find((x) => x.id === bId);
    if (!b) return;
    const patch = { status: "at_house", houseId: hId };
    try {
      const house = data.houses.find((h) => h.id === hId);
      const publicState = publicStateForBag(data, b.id, hId);
      await setBagAtomic(b, patch, {
        bagNumber: b.bagNumber || "",
        groupId: b.groupId || house?.groupId || "",
        status: publicState.status,
        activePickupId: publicState.activePickupId,
        updatedAt: now(),
      });
    } catch (e) {
      console.error("allocateHouse failed", e);
      toast("Couldn't allocate the bag just now — please try again.");
      return;
    }
    applyBagPatch(bId, patch);
  };
  const markLost = async (bId) => {
    const b = data.bags.find((x) => x.id === bId);
    if (!b) return;
    try {
      await setBagAtomic(b, { status: "lost" }, null); // revokes the QR doc
    } catch (e) {
      console.error("markLost failed", e);
      toast("Couldn't mark the bag lost just now — please try again.");
      return;
    }
    applyBagPatch(bId, { status: "lost" });
  };
  const retire = async (bId) => {
    const b = data.bags.find((x) => x.id === bId);
    if (!b) return;
    try {
      await setBagAtomic(b, { status: "retired" }, null); // revokes the QR doc
    } catch (e) {
      console.error("retire failed", e);
      toast("Couldn't retire the bag just now — please try again.");
      return;
    }
    applyBagPatch(bId, { status: "retired" });
  };
  const backToStock = async (bId) => {
    const b = data.bags.find((x) => x.id === bId);
    if (!b) return;
    const patch = {
      status: "in_stock",
      parentId: null,
      kidId: null,
      houseId: null,
    };
    try {
      await setBagAtomic(b, patch, null); // any public doc is removed
    } catch (e) {
      console.error("backToStock failed", e);
      toast("Couldn't return the bag to stock just now — please try again.");
      return;
    }
    applyBagPatch(bId, patch);
  };
  const parents = data.users.filter(
    (u) => u.role === "parent" && u.active && u.status !== "pending",
  );
  const kids = data.users.filter((u) => u.role === "kid" && u.active);
  const myKids =
    user.role === "parent" ? kids.filter((k) => k.parentId === user.id) : kids;
  const visBags = isOwner
    ? data.bags
    : data.bags.filter(
        (b) =>
          b.parentId === user.id ||
          myKids.some((k) => k.id === b.kidId) ||
          myKids.flatMap((k) => k.houses || []).includes(b.houseId),
      );
  const stats = {
    in_stock: visBags.filter((b) => b.status === "in_stock").length,
    at_house: visBags.filter((b) => b.status === "at_house").length,
    lost: visBags.filter((b) => b.status === "lost").length,
    total: visBags.length,
  };

  return (
    <div>
      <SectionTitle>🎒 Bag Inventory</SectionTitle>
      <Card>
        <p
          style={{
            margin: "0 0 6px",
            fontWeight: 700,
            fontSize: 14,
            color: C.g,
          }}
        >
          🖨️ Home-printed QR
        </p>
        <p style={{ margin: "0 0 10px", fontSize: 12, color: C.m }}>
          Branded bags are the standard — but if a household is waiting on one,
          you can create a QR now, print it at home (or email them the link),
          and they can start collecting today.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select
            value={homeHouse}
            onChange={(e) => setHomeHouse(e.target.value)}
            style={inp}
          >
            <option value="">Choose a house…</option>
            {(user.role === "parent"
              ? data.houses.filter((h) => h.parentId === user.id)
              : data.houses
            ).map((h) => (
              <option key={h.id} value={h.id}>
                {h.nickname || h.name}
              </option>
            ))}
          </select>
          <button
            onClick={async () => {
              const house = data.houses.find((h) => h.id === homeHouse);
              if (!house) {
                toast("Choose a house first");
                return;
              }
              try {
                const bag = await createHomeQr(house, user, data.bags);
                up((d) => ({ ...d, bags: [...d.bags, bag] }));
                setHomeHouse("");
                toast(
                  `QR ${bag.bagNumber} created for ${house.nickname || house.name} — find it in the QR tab to print or share ✅`,
                );
              } catch (e) {
                console.error("home QR failed", e);
                toast("Couldn't create the QR — please try again.");
              }
            }}
            style={btnG}
          >
            Create QR
          </button>
        </div>
      </Card>
      <div
        style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}
      >
        <MiniStat label="Total" val={stats.total} color={C.g} />
        <MiniStat label="In Stock" val={stats.in_stock} color={C.blue} />
        <MiniStat label="At Houses" val={stats.at_house} color={C.g} />
        <MiniStat label="Lost" val={stats.lost} color={C.r} />
      </div>
      {isOwner && !adding && (
        <button onClick={() => sA(true)} style={{ ...btnG, marginBottom: 14 }}>
          + Add New Bags
        </button>
      )}
      {adding && (
        <Card accent={C.g}>
          <p
            style={{
              margin: "0 0 10px",
              fontWeight: 700,
              fontSize: 14,
              color: C.g,
            }}
          >
            Add Bags
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: C.m }}>Qty</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={qty}
                  onChange={(e) => sQ(parseInt(e.target.value) || 1)}
                  style={inp}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: C.m }}>Size</label>
                <select
                  value={size}
                  onChange={(e) => sS(e.target.value)}
                  style={inp}
                >
                  {BAG_SIZES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, color: C.m }}>Type</label>
              <select
                value={type}
                onChange={(e) => sT(e.target.value)}
                style={inp}
              >
                {BAG_TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={addBags} style={btnG}>
                Add {qty} Bag{qty > 1 ? "s" : ""}
              </button>
              <button
                onClick={() => sA(false)}
                style={{
                  background: "#eee",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 20px",
                  cursor: "pointer",
                  fontFamily: ff.b,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Card>
      )}
      {visBags.length === 0 && <Empty icon="🎒" text="No bags yet." />}
      {visBags.map((b) => {
        const house = data.houses.find((h) => h.id === b.houseId);
        const parent = data.users.find((u) => u.id === b.parentId);
        const kid = data.users.find((u) => u.id === b.kidId);
        const daysUsed = b.addedAt ? daysBetween(b.addedAt, now()) : 0;
        return (
          <Card key={b.id}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>
                  {b.bagNumber}{" "}
                  <Tag color={C.g} bg={C.gp}>
                    {BAG_STATUS_LABEL[b.status]}
                  </Tag>
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: C.m }}>
                  {b.size} · {b.type} · {daysUsed}d old · {b.pickupCount}{" "}
                  pickups
                </p>
                {parent && (
                  <p style={{ margin: "2px 0", fontSize: 12, color: C.blue }}>
                    {getEmoji(parent)} {parent.firstName}
                  </p>
                )}
                {kid && (
                  <p style={{ margin: "2px 0", fontSize: 12, color: C.g }}>
                    {getEmoji(kid)} {kid.firstName}
                  </p>
                )}
                {house && (
                  <p style={{ margin: "2px 0", fontSize: 12, color: C.tx }}>
                    🏠 {house.nickname || house.name}
                  </p>
                )}
                {b.lastScanDate && (
                  <p style={{ margin: "2px 0", fontSize: 11, color: C.m }}>
                    Last scan: {fmt(b.lastScanDate)}
                  </p>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {isOwner && b.status === "in_stock" && parents.length > 0 && (
                  <select
                    onChange={(e) => {
                      if (e.target.value) allocateParent(b.id, e.target.value);
                    }}
                    style={{
                      ...inp,
                      padding: "4px 6px",
                      fontSize: 11,
                      width: "auto",
                    }}
                    defaultValue=""
                  >
                    <option value="">→ Parent</option>
                    {parents.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.firstName}
                      </option>
                    ))}
                  </select>
                )}
                {user.role === "parent" &&
                  b.status === "allocated_parent" &&
                  b.parentId === user.id && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                      }}
                    >
                      {myKids.length > 0 && (
                        <select
                          onChange={(e) => {
                            if (e.target.value)
                              allocateKid(b.id, e.target.value);
                          }}
                          style={{
                            ...inp,
                            padding: "4px 6px",
                            fontSize: 11,
                            width: "auto",
                          }}
                          defaultValue=""
                        >
                          <option value="">→ Kid</option>
                          {myKids.map((k) => (
                            <option key={k.id} value={k.id}>
                              {k.firstName}
                            </option>
                          ))}
                        </select>
                      )}
                      <select
                        onChange={(e) => {
                          if (e.target.value)
                            allocateHouse(b.id, e.target.value);
                        }}
                        style={{
                          ...inp,
                          padding: "4px 6px",
                          fontSize: 11,
                          width: "auto",
                        }}
                        defaultValue=""
                      >
                        <option value="">→ House</option>
                        {data.houses
                          .filter((h) => h.cancelLevel !== "permanent")
                          .map((h) => (
                            <option key={h.id} value={h.id}>
                              {h.nickname || h.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                {(isOwner ||
                  (user.role === "parent" && b.parentId === user.id)) &&
                  b.status !== "retired" &&
                  b.status !== "lost" && (
                    <div style={{ display: "flex", gap: 3 }}>
                      <button
                        onClick={() => markLost(b.id)}
                        style={{
                          background: C.rp,
                          border: "none",
                          borderRadius: 6,
                          padding: "3px 6px",
                          fontSize: 10,
                          cursor: "pointer",
                          color: C.r,
                          fontFamily: ff.b,
                        }}
                      >
                        Lost
                      </button>
                      <button
                        onClick={() => retire(b.id)}
                        style={{
                          background: "#f5f5f5",
                          border: "none",
                          borderRadius: 6,
                          padding: "3px 6px",
                          fontSize: 10,
                          cursor: "pointer",
                          color: C.m,
                          fontFamily: ff.b,
                        }}
                      >
                        Retire
                      </button>
                    </div>
                  )}
                {isOwner && (b.status === "lost" || b.status === "retired") && (
                  <button
                    onClick={() => backToStock(b.id)}
                    style={{
                      background: C.gp,
                      border: "none",
                      borderRadius: 6,
                      padding: "3px 6px",
                      fontSize: 10,
                      cursor: "pointer",
                      color: C.g,
                      fontFamily: ff.b,
                    }}
                  >
                    → Stock
                  </button>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ━━━ TEAM ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: Team -> manage people: add parents and kids. Each new person gets a secure
// email login (created without logging out the adder). Admin/parent view.
function TeamTab({ data, user, up }) {
  const [adding, sA] = useState(null);
  const [f, sF] = useState({
    email: "",
    firstName: "",
    lastName: "",
    yearOfBirth: "",
    parentId: "",
    emoji: DEFAULT_EMOJI,
  });
  const [transport, sT] = useState(false);
  const isOwner = user.role === "admin";
  const isMain = user.isMainOwner === true;
  const parents = data.users.filter(
    (u) => u.role === "parent" && u.status !== "pending",
  );
  const kids = data.users.filter((u) => u.role === "kid");
  const myKids =
    user.role === "parent" ? kids.filter((k) => k.parentId === user.id) : kids;

  const addUser = async () => {
    if (!f.email || !f.firstName || !f.yearOfBirth) return;
    if (!f.email.includes("@")) {
      toast("Please enter a valid email address");
      return;
    }
    const email = f.email.trim().toLowerCase();
    if (
      data.users.some(
        (u) =>
          (u.email || "").toLowerCase() === email ||
          (u.username || "").toLowerCase() === email,
      )
    ) {
      toast("An account with that email already exists");
      return;
    }
    if (f.yearOfBirth && !validYob(parseInt(f.yearOfBirth))) {
      toast("Please enter a real year of birth (e.g. 2014)");
      return;
    }
    if ((adding === "parent" || adding === "admin") && ageFrom(parseInt(f.yearOfBirth)) < 18) {
      toast(adding === "admin" ? "Admin must be 18+" : "Parent must be 18+");
      return;
    }
    if (adding === "kid") {
      sT(true);
      return;
    }
    const role = adding === "admin" ? "admin" : "parent";
    let result;
    try {
      result = await provisionAccountRemote({
        role,
        email,
        firstName: f.firstName,
        lastName: f.lastName,
        yearOfBirth: parseInt(f.yearOfBirth),
        groupId: role === "parent" ? "grp-krc-crew" : "",
        emoji: f.emoji,
      });
    } catch (e) {
      toast(accountProvisionErrMsg(e));
      return;
    }
    const newUser = result.user;
    up((d) => ({ ...d, users: [...d.users, newUser] }));
    toast(
      result.invitationSent
        ? `${role === "admin" ? "Admin" : "Parent"} account created. Firebase sent a password-setup email to ${email}.`
        : `Account created, but the password-setup email could not be sent. Use Firebase Authentication to resend it to ${email}.`,
    );
    sF({
      email: "",
      firstName: "",
      lastName: "",
      yearOfBirth: "",
      parentId: "",
      emoji: DEFAULT_EMOJI,
    });
    sA(null);
  };
  const confirmKid = async () => {
    const email = f.email.trim().toLowerCase();
    const parentId = f.parentId || (user.role === "parent" ? user.id : "");
    let result;
    try {
      result = await provisionAccountRemote({
        role: "kid",
        email,
        firstName: f.firstName,
        yearOfBirth: parseInt(f.yearOfBirth),
        parentId,
        emoji: f.emoji,
      });
    } catch (e) {
      toast(accountProvisionErrMsg(e));
      return;
    }
    const newKid = result.user;
    up((d) => ({ ...d, users: [...d.users, newKid] }));
    toast(
      result.invitationSent
        ? `Kid account created. Firebase sent a password-setup email to ${email}.`
        : `Kid account created, but the password-setup email could not be sent. Use Firebase Authentication to resend it to ${email}.`,
    );
    sF({
      email: "",
      firstName: "",
      lastName: "",
      yearOfBirth: "",
      parentId: "",
      emoji: DEFAULT_EMOJI,
    });
    sA(null);
    sT(false);
  };
  const deactivate = async (id) => {
    const u = data.users.find((x) => x.id === id);
    if (!u) return;
    try {
      const result = await deactivateAccountRemote(id);
      up((d) => ({
        ...d,
        users: d.users.map((x) => (x.id === id ? { ...x, active: false } : x)),
      }));
      toast(
        result?.authDisabled === false
          ? "KRC access is inactive, but Firebase Auth could not be disabled automatically. An operator must retry the backend deactivation."
          : "Account and Firebase login deactivated.",
      );
    } catch (e) {
      toast(accountLifecycleErrMsg(e, "deactivate"));
    }
  };
  const reactivate = async (id) => {
    const u = data.users.find((x) => x.id === id);
    if (!u) return;
    try {
      await reactivateAccountRemote(id);
      up((d) => ({
        ...d,
        users: d.users.map((x) => (x.id === id ? { ...x, active: true } : x)),
      }));
      toast("Account and Firebase login reactivated.");
    } catch (e) {
      toast(accountLifecycleErrMsg(e, "reactivate"));
    }
  };
  // Promotion/demotion of the admin role has been removed by design: a role is
  // fixed when an account is created. An Admin is a distinct account with its
  // own email (created via "+ Add Admin"), never a promoted Parent, and is
  // removed by deactivating or deleting the account -- not by changing its role.
  const deleteAccount = async (id) => {
    const u = data.users.find((x) => x.id === id);
    if (!u) return;
    toast(
      `Hard deletion of ${u.firstName || u.username} is disabled. Deactivate the account instead; trusted deletion and de-identification will be added as a separate audited backend workflow.`,
    );
  };

  if (transport)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: `radial-gradient(ellipse at 50% 30%,${C.gp} 0%,${C.bg} 70%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <Fonts />
        <div
          style={{
            background: C.w,
            borderRadius: 24,
            padding: 36,
            maxWidth: 400,
            width: "100%",
            boxShadow: "0 12px 48px rgba(46,125,50,0.12)",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 56, marginBottom: 12 }}>🚲🛴👟</div>
          <h2
            style={{
              fontFamily: ff.h,
              color: C.gd,
              fontSize: 22,
              margin: "0 0 8px",
            }}
          >
            At Kids Recycle Cycle...
          </h2>
          <p style={{ fontSize: 15, color: C.tx, lineHeight: 1.6 }}>
            We travel by <strong>bike</strong>, <strong>scooter</strong> or{" "}
            <strong>walking</strong> only.
          </p>
          <p
            style={{
              fontSize: 18,
              color: C.gold,
              fontWeight: 700,
              fontFamily: ff.h,
              margin: "16px 0 24px",
            }}
          >
            Powered by Pedals not Petrol! ♻️
          </p>
          <button
            onClick={confirmKid}
            style={{ ...btnG, fontSize: 16, padding: "14px 32px" }}
          >
            Sounds good! 👍
          </button>
        </div>
      </div>
    );

  return (
    <div>
      <Fonts />
      <SectionTitle>Team</SectionTitle>
      {!adding && (
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          {user.role === "parent" && (
            <button onClick={() => sA("kid")} style={btnG}>
              + Add Kid
            </button>
          )}
          {isMain && (
            <button
              onClick={() => sA("admin")}
              style={{
                background: C.gold,
                color: C.w,
                border: "none",
                borderRadius: 10,
                padding: "10px 16px",
                cursor: "pointer",
                fontFamily: ff.b,
                fontWeight: 600,
              }}
            >
              + Add Admin
            </button>
          )}
        </div>
      )}
      {adding && !transport && (
        <Card accent={adding === "kid" ? C.g : adding === "admin" ? C.gold : C.blue}>
          <p
            style={{
              margin: "0 0 10px",
              fontWeight: 700,
              fontSize: 14,
              color: adding === "kid" ? C.g : adding === "admin" ? C.gold : C.blue,
            }}
          >
            New {adding === "kid" ? "Kid" : adding === "admin" ? "Admin" : "Parent"}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              placeholder="Email (this is their login)"
              type="email"
              value={f.email}
              onChange={(e) => sF({ ...f, email: e.target.value })}
              style={inp}
            />
            {adding === "kid" && (
              <p
                style={{
                  margin: "-2px 2px 0",
                  fontSize: 11.5,
                  lineHeight: 1.4,
                  color: C.m,
                }}
              >
                💡 Tip: kids need their own email to log in. With Gmail you can
                use <b>you+sam@gmail.com</b> — it's a unique address for Sam
                that still arrives in your inbox, so you stay in control.
              </p>
            )}
            <p style={{ margin: "0 2px", fontSize: 11.5, lineHeight: 1.4, color: C.m }}>
              Firebase will email them a secure link to choose their own password.
              You never create or see it.
            </p>
            <input
              placeholder="First name"
              value={f.firstName}
              onChange={(e) => sF({ ...f, firstName: e.target.value })}
              style={inp}
            />
            {(adding === "parent" || adding === "admin") && (
              <input
                placeholder="Last name (optional)"
                value={f.lastName}
                onChange={(e) => sF({ ...f, lastName: e.target.value })}
                style={inp}
              />
            )}
            <input
              placeholder="Year of birth"
              type="number"
              value={f.yearOfBirth}
              onChange={(e) => sF({ ...f, yearOfBirth: e.target.value })}
              style={inp}
            />
            {adding === "kid" && isOwner && parents.length > 0 && (
              <select
                value={f.parentId}
                onChange={(e) => sF({ ...f, parentId: e.target.value })}
                style={inp}
              >
                <option value="">— Select parent —</option>
                {parents.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.firstName} {p.lastName}
                  </option>
                ))}
              </select>
            )}
            <div>
              <label
                style={{
                  fontSize: 12,
                  color: C.m,
                  fontWeight: 600,
                  marginBottom: 4,
                  display: "block",
                }}
              >
                Pick your emoji
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {EMOJI_OPTIONS.map((e) => (
                  <button
                    key={e}
                    onClick={() => sF({ ...f, emoji: e })}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      border:
                        f.emoji === e ? `2px solid ${C.g}` : "2px solid #eee",
                      background: f.emoji === e ? C.gp : C.w,
                      cursor: "pointer",
                      fontSize: 18,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={addUser} style={btnG}>
                Continue &amp; Send Invite
              </button>
              <button
                onClick={() => sA(null)}
                style={{
                  background: "#eee",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 20px",
                  cursor: "pointer",
                  fontFamily: ff.b,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Card>
      )}
      {isOwner &&
        data.users
          .filter((u) => u.role === "admin")
          .map((o) => (
            <Card key={o.id} accent={C.gold}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                    👑 {o.firstName}{" "}
                    <Tag color={C.gold} bg="#fff8e1">
                      ADMIN
                    </Tag>{" "}
                    {o.isMainOwner === true && (
                      <Tag color={C.gd} bg={C.gp}>
                        MAIN
                      </Tag>
                    )}{" "}
                    {!o.active && (
                      <Tag color={C.r} bg={C.rp}>
                        INACTIVE
                      </Tag>
                    )}
                  </p>
                  <p style={{ margin: "2px 0", color: C.m, fontSize: 12 }}>
                    @{o.username} · Age {ageFrom(o.yearOfBirth)}
                  </p>
                </div>
                {isMain &&
                  o.id !== user.id &&
                  !(o.isMainOwner === true) &&
                  (o.active ? (
                    <button
                      onClick={() => deactivate(o.id)}
                      title="Deactivate admin"
                      style={{
                        background: C.rp,
                        border: "none",
                        borderRadius: 8,
                        padding: "4px 8px",
                        cursor: "pointer",
                        fontSize: 10,
                        color: C.r,
                        fontFamily: ff.b,
                      }}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => reactivate(o.id)}
                      title="Reactivate admin"
                      style={{
                        background: C.gp,
                        border: "none",
                        borderRadius: 8,
                        padding: "4px 8px",
                        cursor: "pointer",
                        fontSize: 10,
                        color: C.g,
                        fontFamily: ff.b,
                      }}
                    >
                      Reactivate
                    </button>
                  ))}
              </div>
            </Card>
          ))}
      {isOwner && parents.length > 0 && (
        <h3
          style={{
            fontFamily: ff.h,
            fontSize: 16,
            color: C.blue,
            margin: "16px 0 8px",
          }}
        >
          Parents
        </h3>
      )}
      {isOwner &&
        parents.map((p) => {
          const pKids = kids.filter((k) => k.parentId === p.id);
          return (
            <Card key={p.id} accent={C.blue}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 6,
                }}
              >
                <div>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                    {getEmoji(p)} {p.firstName} {p.lastName}{" "}
                    {!p.active && (
                      <Tag color={C.r} bg={C.rp}>
                        INACTIVE
                      </Tag>
                    )}
                  </p>
                  <p style={{ margin: "2px 0", color: C.m, fontSize: 12 }}>
                    @{p.username} · Age {ageFrom(p.yearOfBirth)}
                  </p>
                  {pKids.length > 0 && (
                    <p style={{ margin: "2px 0", fontSize: 12, color: C.g }}>
                      Kids: {pKids.map((k) => k.firstName).join(", ")}
                    </p>
                  )}
                </div>
                <div style={{ display: "flex", gap: 3 }}>
                  {p.active ? (
                    <button
                      onClick={() => deactivate(p.id)}
                      style={{
                        background: C.rp,
                        border: "none",
                        borderRadius: 8,
                        padding: "4px 8px",
                        cursor: "pointer",
                        fontSize: 10,
                        color: C.r,
                        fontFamily: ff.b,
                      }}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => reactivate(p.id)}
                      style={{
                        background: C.gp,
                        border: "none",
                        borderRadius: 8,
                        padding: "4px 8px",
                        cursor: "pointer",
                        fontSize: 10,
                        color: C.g,
                        fontFamily: ff.b,
                      }}
                    >
                      Reactivate
                    </button>
                  )}
                  {isMain && (
                    <button
                      onClick={() => deleteAccount(p.id)}
                      style={{
                        background: C.r,
                        color: C.w,
                        border: "none",
                        borderRadius: 8,
                        padding: "4px 8px",
                        cursor: "pointer",
                        fontSize: 10,
                        fontFamily: ff.b,
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      <h3
        style={{
          fontFamily: ff.h,
          fontSize: 16,
          color: C.g,
          margin: "16px 0 8px",
        }}
      >
        {user.role === "parent" ? "My Kids" : "Kids"}
      </h3>
      {myKids.length === 0 && <Empty icon="🙂" text="No kids yet." />}
      {myKids.map((k) => {
        const par = data.users.find((u) => u.id === k.parentId);
        return (
          <Card key={k.id}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
                  {getEmoji(k)} {k.firstName}{" "}
                  {!k.active && (
                    <Tag color={C.r} bg={C.rp}>
                      INACTIVE
                    </Tag>
                  )}
                </p>
                <p style={{ margin: "2px 0", color: C.m, fontSize: 12 }}>
                  @{k.username} · Age {ageFrom(k.yearOfBirth)}
                  {par && isOwner && ` · Parent: ${par.firstName}`}
                </p>
                <p style={{ margin: "2px 0", fontSize: 12, color: C.m }}>
                  Houses:{" "}
                  {(k.houses || [])
                    .map(
                      (hId) =>
                        data.houses.find((h) => h.id === hId)?.nickname || "?",
                    )
                    .join(", ") || "None"}
                </p>
                <p
                  style={{
                    margin: "6px 0 2px",
                    fontSize: 12,
                    color: C.m,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span>
                    "I'm Here" arrival button{" "}
                    <span style={{ color: "#9e9e9e" }}>
                      (pings you when they arrive)
                    </span>
                    :
                  </span>
                  <button
                    onClick={() =>
                      up((d) => ({
                        ...d,
                        users: d.users.map((u) =>
                          u.id === k.id
                            ? { ...u, imHereEnabled: !u.imHereEnabled }
                            : u,
                        ),
                      }))
                    }
                    style={{
                      background: k.imHereEnabled ? C.gp : C.rp,
                      color: k.imHereEnabled ? C.g : C.r,
                      border: "none",
                      borderRadius: 8,
                      padding: "2px 12px",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: ff.b,
                    }}
                  >
                    {k.imHereEnabled ? "On" : "Off"}
                  </button>
                </p>
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: 12,
                    color: C.m,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <span>
                    Show on group scoreboard{" "}
                    <span style={{ color: "#9e9e9e" }}>
                      (other families can see their name and count)
                    </span>
                    :
                  </span>
                  <button
                    onClick={() =>
                      up((d) => ({
                        ...d,
                        users: d.users.map((u) =>
                          u.id === k.id
                            ? { ...u, scoreOptIn: !u.scoreOptIn }
                            : u,
                        ),
                      }))
                    }
                    style={{
                      background: k.scoreOptIn ? C.gp : C.rp,
                      color: k.scoreOptIn ? C.g : C.r,
                      border: "none",
                      borderRadius: 8,
                      padding: "2px 12px",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: ff.b,
                    }}
                  >
                    {k.scoreOptIn ? "On" : "Off"}
                  </button>
                </p>
              </div>
              <div style={{ display: "flex", gap: 3 }}>
                {k.active ? (
                  <button
                    onClick={() => deactivate(k.id)}
                    style={{
                      background: C.rp,
                      border: "none",
                      borderRadius: 8,
                      padding: "4px 8px",
                      cursor: "pointer",
                      fontSize: 10,
                      color: C.r,
                      fontFamily: ff.b,
                    }}
                  >
                    Deactivate
                  </button>
                ) : (
                  <button
                    onClick={() => reactivate(k.id)}
                    style={{
                      background: C.gp,
                      border: "none",
                      borderRadius: 8,
                      padding: "4px 8px",
                      cursor: "pointer",
                      fontSize: 10,
                      color: C.g,
                      fontFamily: ff.b,
                    }}
                  >
                    Reactivate
                  </button>
                )}
                {isMain && (
                  <button
                    onClick={() => deleteAccount(k.id)}
                    style={{
                      background: C.r,
                      color: C.w,
                      border: "none",
                      borderRadius: 8,
                      padding: "4px 8px",
                      cursor: "pointer",
                      fontSize: 10,
                      fontFamily: ff.b,
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ━━━ SCOREBOARD ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: Family Score -> rolling family-scoped pickup counts and streaks.
function ScoreTab({ data, user }) {
  const [months, setMonths] = useState(1);
  const cutoff = Date.now() - (months === 1 ? 31 : 183) * 24 * 60 * 60 * 1000;
  const recentDone = (data.pickups || []).filter((p) => {
    if (p.status !== "done" || !p.doneAt) return false;
    const ms = new Date(p.doneAt).getTime();
    return Number.isFinite(ms) && ms >= cutoff;
  });
  // Current role-scoped loads contain only this family's people and Pickups.
  // Calling this a Group leaderboard would therefore be inaccurate. A future
  // Group-wide opt-in board needs a dedicated privacy-safe projection.
  const collectors = (data.users || []).filter(
    (u) =>
      u.role === "kid" &&
      (u.id === user.id || u.parentId === user.id || u.parentId === user.parentId),
  );
  const scores = collectors
    .map((c) => {
      const count = recentDone.filter((p) => p.doneBy === c.id).length;
      const streak = calcStreak(recentDone, c.id);
      return { ...c, count, streak };
    })
    .sort((a, b) => b.count - a.count || a.firstName.localeCompare(b.firstName));
  const max = Math.max(...scores.map((s) => s.count), 1);
  const totalDone = recentDone.length;

  return (
    <div>
      <SectionTitle>🏆 Family Score</SectionTitle>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {[1, 6].map((m) => (
          <button
            key={m}
            onClick={() => setMonths(m)}
            style={{
              ...(months === m ? btnG : btnB),
              padding: "7px 12px",
              fontSize: 12,
            }}
          >
            {m === 1 ? "Last month" : "Last 6 months"}
          </button>
        ))}
      </div>
      <Card style={{ textAlign: "center" }}>
        <p style={{ margin: 0, fontFamily: ff.h, fontSize: 16, color: C.g }}>
          🌏 Your family: {totalDone} pickup{totalDone === 1 ? "" : "s"} done
        </p>
        <p style={{ margin: "4px 0 0", fontSize: 11, color: C.m }}>
          This rolling family view uses only the information your login is
          permitted to load. A privacy-safe Group-wide opt-in view is still a
          separate pre-pilot feature.
        </p>
      </Card>
      {scores.length === 0 && <Empty icon="🏆" text="No family collectors yet." />}
      {scores.map((s, i) => (
        <Card key={s.id}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: i === 0 ? "#ffd700" : "#eee",
                fontFamily: ff.h,
                fontWeight: 700,
                fontSize: 16,
                color: i === 0 ? C.w : C.m,
                flexShrink: 0,
              }}
            >
              {i + 1}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>
                {s.firstName} {s.id === user.id && "(you)"}
                {s.streak.current > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 12, color: C.o }}>
                    🔥 {s.streak.current}w
                  </span>
                )}
              </p>
              <div
                style={{
                  marginTop: 4,
                  height: 8,
                  background: "#eee",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${(s.count / max) * 100}%`,
                    background: `linear-gradient(90deg,${C.gl},${C.g})`,
                    borderRadius: 4,
                  }}
                />
              </div>
            </div>
            <div
              style={{
                fontFamily: ff.h,
                fontSize: 22,
                fontWeight: 700,
                color: C.gd,
                minWidth: 36,
                textAlign: "right",
              }}
            >
              {s.count}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ━━━ MAP ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: Map -> a map showing the households.
function MapTab({ data, user }) {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const [loaded, setL] = useState(false);
  const pendingIds = data.pickups
    .filter((p) =>
      ["waiting", "unallocated", "accepted", "parent_approval"].includes(
        p.status,
      ),
    )
    .map((p) => p.houseId);
  const myKids = data.users.filter((u) => u.parentId === user.id);
  const visH =
    user.role === "admin"
      ? data.houses.filter((h) => h.cancelLevel !== "permanent")
      : data.houses.filter((h) => {
          if (h.cancelLevel === "permanent") return false;
          if (user.role === "kid") return user.houses?.includes(h.id);
          if (user.role === "parent")
            return myKids.some((k) => k.houses?.includes(h.id));
          return false;
        });
  useEffect(() => {
    loadLeafletOnce(setL);
  }, []);
  useEffect(() => {
    if (!loaded || !mapRef.current || !window.L) return;
    if (mapInst.current) {
      mapInst.current.remove();
      mapInst.current = null;
    }
    const points = visH.map((h, i) => ({
      house: h,
      point: getHouseLocation(h) || fallbackHouseLocation(i),
      saved: hasHouseLocation(h),
    }));
    const first = points[0]?.point || DEFAULT_MAP_CENTER;
    const map = window.L.map(mapRef.current).setView(
      [first.lat, first.lng],
      points.length ? 15 : 14,
    );
    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OSM",
    }).addTo(map);
    mapInst.current = map;
    points.forEach(({ house: h, point, saved }) => {
      const active = pendingIds.includes(h.id);
      const icon = window.L.divIcon({
        className: "",
        html: `<div style="width:${active ? 42 : 30}px;height:${active ? 42 : 30}px;border-radius:50%;background:${active ? C.g : saved ? C.blue : "#bbb"};display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:${active ? 9 : 8}px;font-family:${ff.b};box-shadow:0 2px 8px rgba(0,0,0,0.3);border:3px solid white;text-align:center;line-height:1.1">${active ? "PICK<br>UP" : saved ? "PIN" : "—"}</div>`,
        iconSize: [active ? 42 : 30, active ? 42 : 30],
        iconAnchor: [active ? 21 : 15, active ? 21 : 15],
      });
      const popup = document.createElement("div");
      const title = document.createElement("strong");
      title.textContent = `🏠 ${h.nickname || h.name || "House"}`;
      const address = document.createElement("div");
      address.textContent = h.address || "";
      const note = document.createElement("small");
      note.textContent = saved
        ? "Parent-selected map pin"
        : "Approximate demo marker";
      popup.append(title, address, note);
      window.L.marker([point.lat, point.lng], { icon })
        .addTo(map)
        .bindPopup(popup);
    });
    if (points.length > 1) {
      const bounds = window.L.latLngBounds(
        points.map((x) => [x.point.lat, x.point.lng]),
      );
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
    return () => {
      if (mapInst.current) {
        mapInst.current.remove();
        mapInst.current = null;
      }
    };
  }, [
    loaded,
    JSON.stringify(
      visH.map((h) => `${h.id}:${h.mapLat || ""}:${h.mapLng || ""}`),
    ),
    pendingIds.join(","),
  ]);
  return (
    <div>
      <SectionTitle>🗺️ Pickup Map</SectionTitle>
      <p style={{ color: C.m, fontSize: 13, margin: "0 0 12px" }}>
        Green = pickup waiting. Blue = parent-selected house location. Grey =
        approximate demo marker.
      </p>
      {visH.length === 0 ? (
        <Empty icon="🗺️" text="No houses to show." />
      ) : (
        <div
          ref={mapRef}
          style={{
            height: 400,
            borderRadius: 16,
            overflow: "hidden",
            border: `2px solid ${C.brd}`,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        />
      )}
    </div>
  );
}

// ━━━ QR ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: QR -> generate and print the QR codes that go on bags.
function QRTab({ data, user }) {
  // Parents see only their own bags/houses; staff see everything.
  const myHouseIds =
    user.role === "parent"
      ? new Set(
          data.houses.filter((h) => h.parentId === user.id).map((h) => h.id),
        )
      : null;
  const visBags = myHouseIds
    ? data.bags.filter(
        (b) => b.parentId === user.id || myHouseIds.has(b.houseId),
      )
    : data.bags;
  return (
    <div>
      <SectionTitle>📱 QR Codes</SectionTitle>
      <p style={{ color: C.m, fontSize: 13, margin: "0 0 14px" }}>
        Print and stick on branded bags — or print a home QR / share its link.
      </p>
      {visBags.length === 0 && (
        <Empty
          icon="📱"
          text={
            user.role === "parent"
              ? "No QR codes yet — create one from the Bags tab."
              : "Add bags first."
          }
        />
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
          gap: 14,
        }}
      >
        {visBags
          .filter((b) => b.status !== "retired")
          .map((b) => {
            const url = `${BASE}/bag/${b.id}`;
            return (
              <div
                key={b.id}
                id={`qr-${b.id}`}
                style={{
                  background: C.w,
                  borderRadius: 16,
                  padding: 16,
                  textAlign: "center",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  border: `2px solid ${C.brd}`,
                }}
              >
                <p
                  style={{
                    margin: "0 0 2px",
                    fontFamily: ff.h,
                    fontWeight: 700,
                    fontSize: 13,
                    color: C.gd,
                  }}
                >
                  Kids Recycle Cycle
                </p>
                <p
                  style={{
                    margin: "0 0 8px",
                    fontSize: 9,
                    color: C.gold,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Pedals not Petrol
                </p>
                <div
                  style={{
                    width: 140,
                    height: 140,
                    margin: "0 auto",
                    background: "#fff",
                    padding: 4,
                    borderRadius: 8,
                  }}
                  className="krc-qr"
                >
                  <QRCodeSVG value={url} size={132} />
                </div>
                <p
                  style={{ margin: "8px 0 2px", fontWeight: 700, fontSize: 13 }}
                >
                  {b.bagNumber}
                </p>
                <p style={{ margin: 0, color: C.m, fontSize: 11 }}>
                  {b.size} · {b.type}
                </p>
                <Tag color={C.g} bg={C.gp}>
                  {BAG_STATUS_LABEL[b.status]}
                </Tag>
                <div style={{ marginTop: 8 }}>
                  <button
                    onClick={() => {
                      const el = document.getElementById(`qr-${b.id}`);
                      if (!el) return;
                      // Print the locally rendered SVG — no external service.
                      const svg = el.querySelector(".krc-qr svg");
                      const w = window.open("", "", "width=400,height=500");
                      if (!w) {
                        toast("Please allow pop-ups to print this QR label.");
                        return;
                      }
                      const docOut = w.document;
                      docOut.title = `Print QR - ${b.bagNumber}`;
                      const body = docOut.body;
                      body.style.textAlign = "center";
                      body.style.fontFamily = "Arial";
                      body.style.padding = "20px";
                      const heading = docOut.createElement("h2");
                      heading.textContent = "Kids Recycle Cycle";
                      const slogan = docOut.createElement("p");
                      slogan.textContent = "PEDALS NOT PETROL";
                      slogan.style.color = "#c8a415";
                      slogan.style.fontWeight = "bold";
                      slogan.style.letterSpacing = "2px";
                      const number = docOut.createElement("h3");
                      number.textContent = b.bagNumber || "KRC Bag";
                      body.append(heading, slogan);
                      if (svg) {
                        const clone = svg.cloneNode(true);
                        clone.setAttribute("width", "250");
                        clone.setAttribute("height", "250");
                        body.appendChild(docOut.importNode(clone, true));
                      }
                      body.appendChild(number);
                      w.focus();
                      w.print();
                    }}
                    style={{
                      background: C.gp,
                      border: `1px solid ${C.g}`,
                      borderRadius: 8,
                      padding: "4px 12px",
                      fontSize: 11,
                      cursor: "pointer",
                      fontFamily: ff.b,
                      fontWeight: 600,
                      color: C.gd,
                    }}
                  >
                    🖨️ Print
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard
                        .writeText(url)
                        .then(() => toast("Link copied ✅"))
                        .catch(() => toast(url));
                    }}
                    style={{
                      background: C.gp,
                      border: `1px solid ${C.g}`,
                      borderRadius: 8,
                      padding: "6px 10px",
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: ff.b,
                      fontWeight: 600,
                      color: C.gd,
                      marginLeft: 6,
                    }}
                  >
                    📋 Copy link
                  </button>
                  <button
                    onClick={() => {
                      const house = data.houses.find(
                        (h) => h.id === b.houseId,
                      );
                      const to = (house && house.email) || "";
                      const subject = encodeURIComponent(
                        "Your Kids Recycle Cycle link 🚲♻️",
                      );
                      const body = encodeURIComponent(
                        `Hi!

Here's your Kids Recycle Cycle link. When your cans and bottles are ready for pickup, open it and press the big green button:

${url}

Tip: bookmark it, or print the page's QR code and stick it on your container.

Powered by Pedals not Petrol 🚲🛴👟`,
                      );
                      window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
                    }}
                    style={{
                      background: C.gp,
                      border: `1px solid ${C.g}`,
                      borderRadius: 8,
                      padding: "6px 10px",
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: ff.b,
                      fontWeight: 600,
                      color: C.gd,
                      marginLeft: 6,
                    }}
                  >
                    ✉️ Email link
                  </button>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ━━━ SETTINGS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB: Settings -> account settings, incl. changing your password (via Firebase).
function SettingsTab({ data, user, up }) {
  const me = data.users.find((u) => u.id === user.id);
  const [un, sUN] = useState(me?.username || "");
  const [fn, sFN] = useState(me?.firstName || "");
  const [yob, sYOB] = useState(me?.yearOfBirth || "");
  const [oldPw, sOP] = useState("");
  const [newPw, sNP] = useState("");
  const [confPw, sCP] = useState("");
  const [pwMsg, sPM] = useState("");
  const [emNew, sEmNew] = useState("");
  const [emPw, sEmPw] = useState("");
  const [emMsg, sEmMsg] = useState("");
  const [noteHouse, sNH] = useState("");
  const [note, sNote] = useState("");
  const isMain = user.isMainOwner === true;
  const [editUserId, sEditUserId] = useState("");
  const [eu, sEU] = useState({ username: "", firstName: "", yearOfBirth: "" });
  const saveProfile = () =>
    up((d) => ({
      ...d,
      users: d.users.map((u) =>
        u.id === user.id
          ? {
              ...u,
              username: un,
              firstName: fn,
              yearOfBirth: parseInt(yob) || u.yearOfBirth,
            }
          : u,
      ),
    }));
  const changePw = async () => {
    if (!oldPw) {
      sPM("Enter your current password");
      return;
    }
    if (!newPw) {
      sPM("Enter a new password");
      return;
    }
    if (newPw.length < 6) {
      sPM("New password must be at least 6 characters");
      return;
    }
    if (newPw !== confPw) {
      sPM("Passwords don't match");
      return;
    }
    const email = (me.email || me.username || "").toLowerCase();
    const cu = auth.currentUser;
    if (!cu || !email.includes("@")) {
      sPM('Please log out and use "Forgot password?" on the login screen.');
      return;
    }
    try {
      const cred = EmailAuthProvider.credential(email, oldPw);
      await reauthenticateWithCredential(cu, cred);
      await updatePassword(cu, newPw);
    } catch (e) {
      const c = (e && e.code) || "";
      if (c === "auth/wrong-password" || c === "auth/invalid-credential")
        sPM("Current password is incorrect");
      else if (c === "auth/weak-password")
        sPM("New password must be at least 6 characters");
      else if (c === "auth/requires-recent-login")
        sPM("For safety, please log out and back in, then try again.");
      else sPM("Couldn't change password. Please try again.");
      return;
    }
    sOP("");
    sNP("");
    sCP("");
    sPM("Password changed! ✅");
  };
  // Self-service email change. Firebase requires a recent login, so we re-auth
  // with the CURRENT email + password first, then send a verification link to
  // the NEW address. The login email only switches once they click that link
  // (verifyBeforeUpdateEmail) — so a typo can't lock anyone out. The UID never
  // changes, so all their records stay keyed correctly; we update the stored
  // email field too so the displayed value matches once verified.
  const changeEmail = async () => {
    // Firebase Auth's email is the credential of record — using the app
    // record here would break a second change attempt in the same session
    // (the record may already hold a pending, unverified address).
    const current = (
      (auth.currentUser && auth.currentUser.email) ||
      me.email ||
      me.username ||
      ""
    ).toLowerCase();
    const next = emNew.trim().toLowerCase();
    if (!next || !next.includes("@")) {
      sEmMsg("Enter a valid new email address");
      return;
    }
    if (next === current) {
      sEmMsg("That's already your email");
      return;
    }
    if (!emPw) {
      sEmMsg("Enter your current password to confirm");
      return;
    }
    const cu = auth.currentUser;
    if (!cu || !current.includes("@")) {
      sEmMsg('Please log out and back in, then try again.');
      return;
    }
    try {
      const cred = EmailAuthProvider.credential(current, emPw);
      await reauthenticateWithCredential(cu, cred);
      await verifyBeforeUpdateEmail(cu, next);
    } catch (e) {
      const c = (e && e.code) || "";
      if (c === "auth/wrong-password" || c === "auth/invalid-credential")
        sEmMsg("Current password is incorrect");
      else if (c === "auth/email-already-in-use")
        sEmMsg("That email is already used by another account");
      else if (c === "auth/invalid-email") sEmMsg("That email looks invalid");
      else if (c === "auth/requires-recent-login")
        sEmMsg("For safety, please log out and back in, then try again.");
      else sEmMsg("Couldn't change email. Please try again.");
      return;
    }
    // Record only the PENDING address. The active email/username switch
    // when the verification link is clicked: the next login self-heals the
    // record from Firebase Auth (the credential of record) and clears this.
    up((d) => ({
      ...d,
      users: d.users.map((u) =>
        u.id === user.id ? { ...u, pendingEmail: next } : u,
      ),
    }));
    sEmNew("");
    sEmPw("");
    sEmMsg(
      "Check your new inbox for a verification link — your login email switches once you click it. ✅",
    );
  };
  const saveNote = () => {
    if (!noteHouse || !note.trim()) return;
    up((d) => ({
      ...d,
      users: d.users.map((u) => {
        if (u.id !== user.id) return u;
        const notes = { ...(u.notes || {}) };
        notes[noteHouse] = note.trim();
        return { ...u, notes };
      }),
    }));
    sNote("");
    sNH("");
  };
  const myHouses = (me?.houses || [])
    .map((hId) => data.houses.find((h) => h.id === hId))
    .filter(Boolean);
  const visibleNotes =
    user.role === "parent"
      ? data.users.filter(
          (u) =>
            u.parentId === user.id &&
            u.notes &&
            Object.keys(u.notes).length > 0,
        )
      : user.role === "kid"
        ? [me].filter((u) => u?.notes && Object.keys(u.notes).length > 0)
        : [];

  return (
    <div>
      <SectionTitle>Settings</SectionTitle>
      <Card>
        <p
          style={{
            margin: "0 0 10px",
            fontWeight: 700,
            fontSize: 14,
            color: C.g,
          }}
        >
          My Profile
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <label style={{ fontSize: 12, color: C.m, fontWeight: 600 }}>
              Username
            </label>
            <input
              value={un}
              onChange={(e) => sUN(e.target.value)}
              style={inp}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: C.m, fontWeight: 600 }}>
              First Name
            </label>
            <input
              value={fn}
              onChange={(e) => sFN(e.target.value)}
              style={inp}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: C.m, fontWeight: 600 }}>
              Year of Birth
            </label>
            <input
              type="number"
              value={yob}
              onChange={(e) => sYOB(e.target.value)}
              style={inp}
            />
          </div>
          <p style={{ margin: 0, fontSize: 12, color: C.m }}>
            Role:{" "}
            {me?.role === "admin"
              ? "👑 Admin"
              : me?.role === "parent"
                ? `${getEmoji(me)} Parent`
                : `${getEmoji(me)} Kid`}{" "}
            · Age: {ageFrom(me?.yearOfBirth)}
          </p>
          <div>
            <label style={{ fontSize: 12, color: C.m, fontWeight: 600 }}>
              My Emoji
            </label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                marginTop: 4,
              }}
            >
              {EMOJI_OPTIONS.map((e) => (
                <button
                  key={e}
                  onClick={() =>
                    up((d) => ({
                      ...d,
                      users: d.users.map((u) =>
                        u.id === user.id ? { ...u, emoji: e } : u,
                      ),
                    }))
                  }
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    border:
                      (me?.emoji || DEFAULT_EMOJI) === e
                        ? `2px solid ${C.g}`
                        : "2px solid #eee",
                    background: (me?.emoji || DEFAULT_EMOJI) === e ? C.gp : C.w,
                    cursor: "pointer",
                    fontSize: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
          <button onClick={saveProfile} style={btnG}>
            Save Profile
          </button>
        </div>
      </Card>
      {(isMain || user.role === "admin") && (
        <Card>
          <p
            style={{
              margin: "0 0 6px",
              fontWeight: 700,
              fontSize: 14,
              color: C.g,
            }}
          >
            💾 Platform snapshot
          </p>
          <p style={{ margin: "0 0 10px", fontSize: 12, color: C.m }}>
            Downloads a snapshot of the platform data your account loads — groups
            and staff accounts only. It is NOT a complete backup: it does not
            contain family, household, bag or pickup data. A full system backup
            now needs a backend export.
          </p>
          <button
            onClick={() => {
              const stamp = new Date()
                .toISOString()
                .slice(0, 16)
                .replace(/[:T]/g, "-");
              const blob = new Blob([JSON.stringify(data, null, 2)], {
                type: "application/json",
              });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = `krc-platform-snapshot-${stamp}.json`;
              a.click();
              URL.revokeObjectURL(a.href);
            }}
            style={btnG}
          >
            Download platform snapshot
          </button>
        </Card>
      )}
      {(isMain || user.role === "admin" || user.role === "parent") && (
        <Card>
          <p
            style={{
              margin: "0 0 10px",
              fontWeight: 700,
              fontSize: 14,
              color: C.blue,
            }}
          >
            Edit Another User
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <select
              value={editUserId}
              onChange={(e) => {
                sEditUserId(e.target.value);
                const u = data.users.find((x) => x.id === e.target.value);
                if (u)
                  sEU({
                    username: u.username,
                    firstName: u.firstName,
                    yearOfBirth: u.yearOfBirth || "",
                  });
              }}
              style={inp}
            >
              <option value="">— Select user —</option>
              {data.users
                .filter((u) => {
                  if (u.id === user.id) return false;
                  if (isMain) return true;
                  if (user.role === "admin")
                    return u.role === "parent" || u.role === "kid";
                  if (user.role === "parent")
                    return u.role === "kid" && u.parentId === user.id;
                  return false;
                })
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} (@{u.username}) - {u.role}
                  </option>
                ))}
            </select>
            {editUserId && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div>
                  <label style={{ fontSize: 12, color: C.m, fontWeight: 600 }}>
                    Username
                  </label>
                  <input
                    value={eu.username}
                    onChange={(e) => sEU({ ...eu, username: e.target.value })}
                    style={inp}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: C.m, fontWeight: 600 }}>
                    First Name
                  </label>
                  <input
                    value={eu.firstName}
                    onChange={(e) => sEU({ ...eu, firstName: e.target.value })}
                    style={inp}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: C.m, fontWeight: 600 }}>
                    Year of Birth
                  </label>
                  <input
                    type="number"
                    value={eu.yearOfBirth}
                    onChange={(e) =>
                      sEU({ ...eu, yearOfBirth: e.target.value })
                    }
                    style={inp}
                  />
                </div>
                <button
                  onClick={() => {
                    up((d) => ({
                      ...d,
                      users: d.users.map((u) =>
                        u.id === editUserId
                          ? {
                              ...u,
                              username: eu.username,
                              firstName: eu.firstName,
                              yearOfBirth:
                                parseInt(eu.yearOfBirth) || u.yearOfBirth,
                            }
                          : u,
                      ),
                    }));
                    sEditUserId("");
                  }}
                  style={btnB}
                >
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </Card>
      )}
      <Card>
        <p
          style={{
            margin: "0 0 4px",
            fontWeight: 700,
            fontSize: 14,
            color: C.g,
          }}
        >
          Change Email
        </p>
        <p style={{ margin: "0 0 10px", fontSize: 12, color: C.m }}>
          Your current login email is <strong>{me.email || me.username}</strong>
          . We'll send a verification link to the new address — your login
          switches once you click it.
        </p>
        {me.pendingEmail && (
          <p style={{ margin: "0 0 10px", fontSize: 12, color: C.o }}>
            ⏳ Waiting on verification: <strong>{me.pendingEmail}</strong> —
            check that inbox for the link. Your current email stays active
            until then.
          </p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            placeholder="New email address"
            type="email"
            value={emNew}
            onChange={(e) => {
              sEmNew(e.target.value);
              sEmMsg("");
            }}
            style={inp}
          />
          <input
            placeholder="Current password (to confirm it's you)"
            type="password"
            value={emPw}
            onChange={(e) => {
              sEmPw(e.target.value);
              sEmMsg("");
            }}
            style={inp}
          />
          {emMsg && (
            <p
              style={{
                color: emMsg.includes("✅") ? C.g : C.r,
                fontSize: 13,
                margin: 0,
              }}
            >
              {emMsg}
            </p>
          )}
          <button onClick={changeEmail} style={btnG}>
            Change Email
          </button>
        </div>
      </Card>
      <Card>
        <p
          style={{
            margin: "0 0 10px",
            fontWeight: 700,
            fontSize: 14,
            color: C.g,
          }}
        >
          Change Password
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            placeholder="Current password"
            type="password"
            value={oldPw}
            onChange={(e) => {
              sOP(e.target.value);
              sPM("");
            }}
            style={inp}
          />
          <input
            placeholder="New password"
            type="password"
            value={newPw}
            onChange={(e) => {
              sNP(e.target.value);
              sPM("");
            }}
            style={inp}
          />
          <input
            placeholder="Confirm new password"
            type="password"
            value={confPw}
            onChange={(e) => {
              sCP(e.target.value);
              sPM("");
            }}
            style={inp}
          />
          {pwMsg && (
            <p
              style={{
                color: pwMsg.includes("✅") ? C.g : C.r,
                fontSize: 13,
                margin: 0,
              }}
            >
              {pwMsg}
            </p>
          )}
          <button onClick={changePw} style={btnG}>
            Change Password
          </button>
        </div>
      </Card>
      {(user.role === "kid" ||
        user.role === "admin" ||
        user.role === "parent") &&
        myHouses.length > 0 && (
          <Card>
            <p
              style={{
                margin: "0 0 4px",
                fontWeight: 700,
                fontSize: 14,
                color: C.g,
              }}
            >
              My Notes (private)
            </p>
            <p style={{ color: C.m, fontSize: 12, margin: "0 0 8px" }}>
              {user.role === "kid"
                ? "Only you and your parent can see these."
                : "Only you can see these."}
            </p>
            <select
              value={noteHouse}
              onChange={(e) => {
                sNH(e.target.value);
                sNote(me?.notes?.[e.target.value] || "");
              }}
              style={inp}
            >
              <option value="">— Select house —</option>
              {myHouses.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.nickname || h.name}
                </option>
              ))}
            </select>
            {noteHouse && (
              <div>
                <textarea
                  value={note}
                  onChange={(e) => sNote(e.target.value)}
                  placeholder="e.g. Say hi to Mary, dog is called Buster"
                  style={{
                    ...inp,
                    marginTop: 8,
                    minHeight: 60,
                    resize: "vertical",
                  }}
                />
                <button onClick={saveNote} style={{ ...btnG, marginTop: 8 }}>
                  Save Note
                </button>
              </div>
            )}
            {me?.notes && Object.keys(me.notes).length > 0 && (
              <div style={{ marginTop: 12 }}>
                <p
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: C.m,
                    margin: "0 0 4px",
                  }}
                >
                  Saved notes:
                </p>
                {Object.entries(me.notes).map(([hId, n]) => {
                  const h = data.houses.find((x) => x.id === hId);
                  return (
                    <div
                      key={hId}
                      style={{
                        background: "#fafafa",
                        borderRadius: 8,
                        padding: "4px 10px",
                        marginBottom: 3,
                      }}
                    >
                      <p style={{ margin: 0, fontSize: 12 }}>
                        🏠 <strong>{h?.nickname || h?.name || "?"}</strong>: {n}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}
      {user.role === "parent" && visibleNotes.length > 0 && (
        <Card>
          <p
            style={{
              margin: "0 0 8px",
              fontWeight: 700,
              fontSize: 14,
              color: C.g,
            }}
          >
            My Kids' Notes
          </p>
          {visibleNotes.map((u) => (
            <div key={u.id} style={{ marginBottom: 8 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>
                {getEmoji(u)} {u.firstName}:
              </p>
              {Object.entries(u.notes).map(([hId, n]) => {
                const h = data.houses.find((x) => x.id === hId);
                return (
                  <div
                    key={hId}
                    style={{
                      background: "#fafafa",
                      borderRadius: 8,
                      padding: "3px 10px",
                      marginTop: 2,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 12 }}>
                      🏠 {h?.nickname || h?.name || "?"}: {n}
                    </p>
                  </div>
                );
              })}
            </div>
          ))}
        </Card>
      )}
      <Card>
        <p
          style={{
            margin: "0 0 4px",
            fontWeight: 700,
            fontSize: 14,
            color: C.g,
          }}
        >
          How It Works
        </p>
        <ol
          style={{
            margin: "8px 0 0",
            paddingLeft: 20,
            color: C.tx,
            fontSize: 12,
            lineHeight: 1.9,
          }}
        >
          <li>Coordinator approves parents; Admin approves Coordinators and manages platform accounts and Groups</li>
          <li>Parents sign up → coordinator approves → parent adds kids</li>
          <li>Parents assign + approve houses for their kids</li>
          <li>Kids accept or pass pickups</li>
          <li>📍 "I'm Here" → parent notified → collect → done</li>
          <li>Kids can request to stop collecting (parent reviews)</li>
          <li>Parent can set age brackets (Standard, 12+, 14+)</li>
          <li>🏆 Scoreboard with 🔥 weekly streaks</li>
          <li>🚲🛴👟 Pedals not Petrol — always!</li>
        </ol>
      </Card>
    </div>
  );
}

// ━━━ SHARED ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// UI: Card -> a rounded white container used throughout the app.
function Card({ children, accent, muted: m, style: s }) {
  return (
    <div
      style={{
        background: m ? "#fafafa" : C.w,
        borderRadius: 14,
        padding: 14,
        marginBottom: 10,
        boxShadow: m ? "none" : "0 2px 10px rgba(0,0,0,0.04)",
        borderLeft: accent ? `4px solid ${accent}` : "none",
        border: m ? `1px solid ${C.brd}` : undefined,
        opacity: m ? 0.65 : 1,
        ...s,
      }}
    >
      {children}
    </div>
  );
}
// UI: SectionTitle -> a small green heading above a section.
function SectionTitle({ children }) {
  return (
    <h2
      style={{
        fontFamily: ff.h,
        fontSize: 20,
        color: C.gd,
        margin: "0 0 12px",
      }}
    >
      {children}
    </h2>
  );
}
// UI: Tag -> a small coloured pill/badge (e.g. a role label).
function Tag({ children, color, bg, style: s }) {
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: 10,
        fontWeight: 700,
        color,
        background: bg,
        borderRadius: 6,
        padding: "2px 7px",
        ...s,
      }}
    >
      {children}
    </span>
  );
}
// UI: Empty -> a friendly "nothing here yet" placeholder with an icon.
function Empty({ icon, text }) {
  return (
    <div style={{ textAlign: "center", padding: "32px 20px", color: C.m }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>{icon}</div>
      <p
        style={{
          fontSize: 13,
          lineHeight: 1.6,
          maxWidth: 260,
          margin: "0 auto",
        }}
      >
        {text}
      </p>
    </div>
  );
}
// UI: MiniStat -> a small labelled number, used in stats rows.
function MiniStat({ label, val, color }) {
  return (
    <div
      style={{
        background: C.w,
        borderRadius: 10,
        padding: "8px 12px",
        textAlign: "center",
        flex: "1 1 0",
        minWidth: 60,
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
      }}
    >
      <div style={{ fontFamily: ff.h, fontSize: 20, fontWeight: 700, color }}>
        {val}
      </div>
      <div style={{ fontSize: 10, color: C.m, fontWeight: 600 }}>{label}</div>
    </div>
  );
}
